import { urlSourceLabel, urlSourceFromProbe } from '../utils/urlSource';
import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import ReviewView from '../components/ReviewView';
import EvidenceStrip from '../components/EvidenceStrip';
import { DEFAULTS, getIdleReloadMs, syncAlertPollFromServer, saveAlertPollToServer } from '../utils/alertPollConfig';
import { getOfflineSyncConfig, setOfflineSyncConfig } from '../utils/offlineSyncConfig';
import * as offline from '../utils/offlineAlertStore';
import ConfirmDialog from '../components/ConfirmDialog';
import useIsMobile from '../utils/useIsMobile';
import '../css/admin-config.css';
import '../css/dashboard.css';
import CameraStreamSettings from '../components/CameraStreamSettings';
import DetectionSettingsPage from './DetectionSettingsPage';
import { productHas, useEntitlements } from '../utils/entitlements';
import { requestUpdate, useUpdateStatus } from '../utils/updateStatus';
import CameraDetectorsTab from '../components/CameraDetectorsTab';

// A product without recording (CLIENT_TYPE, see product_profiles.py on the box)
// drops the 'recordings' menu — recording controls are hidden along with it.
const recOn = () => productHas('recordings');

const fmtDT = dt => {
  if (!dt || dt.length < 15) return dt || '—';
  return `${dt.slice(6,8)}/${dt.slice(4,6)}/${dt.slice(0,4)}  ${dt.slice(9,11)}:${dt.slice(11,13)}`;
};

// A device window is stored as {start, end}; "always" and "follow_global" are the
// only strings the engine understands (external_devices._in_schedule). Anything
// else it cannot parse fails OPEN — the device fires at any hour — so these two
// exist to stop a text box ever producing one.
const fmtRelaySchedule = v =>
  (v && typeof v === 'object' && v.start && v.end) ? `${v.start}-${v.end}`
    : (typeof v === 'string' ? v : 'always');

const parseRelaySchedule = raw => {
  const s = String(raw || '').trim();
  const m = s.match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
  if (m) return { start: m[1], end: m[2] };
  // Only the words the engine actually recognises get through as strings; anything
  // else becomes "always", which is at least a state it can evaluate rather than
  // one it silently ignores.
  return (s === 'follow_global') ? 'follow_global' : 'always';
};

const fmtISO = s => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return s; }
};

const UPDATE_STATE_TEXT = {
  up_to_date:   'Up to date',
  updated:      'Up to date (just updated)',
  downloading:  'Downloading update…',
  ready:        'Update ready to install',
  scheduled:    'Update scheduled',
  installing:   'Installing update…',
  health_check: 'Checking new version…',
  rolling_back: 'Rolling back…',
  rolled_back:  'Last update was rolled back',
  blocked:      'Update blocked after repeated failures',
  too_old:      'Too old for automatic update — contact support',
};

function BuildInfoBar() {
  const [info, setInfo] = useState(null);
  const [upd]           = useUpdateStatus();
  const [checking, setChecking] = useState(false);
  const [msg, setMsg]   = useState('');

  useEffect(() => {
    apiFetch('/build-info')
      .then(r => r.json())
      .then(d => { if (d.build_id || d.build_number) setInfo(d); })
      .catch(() => {});
  }, []);

  if (!info) return null;

  const applied = info.applied_at || info.installed_at;
  const parts = [];
  // Auto-update releases carry a version + build number; older patches only a build_id.
  if (info.version && info.build_number) parts.push(`Version ${info.version} (build ${info.build_number})`);
  else parts.push(info.build_id);
  if (info.channel)   parts.push(info.channel);
  if (info.git_commit && info.git_commit !== 'unknown') parts.push(`commit ${info.git_commit.slice(0, 7)}`);
  if (info.built_at)  parts.push(`built ${fmtISO(info.built_at)}`);
  if (applied)        parts.push(`applied ${fmtISO(applied)}`);

  const hasUpdater = upd && upd.state && upd.state !== 'not_installed';
  const check = async () => {
    setChecking(true); setMsg('');
    try { await requestUpdate('check'); setMsg('Checking — the result shows here within a minute.'); }
    catch (e) { setMsg(e.message); }
    finally { setChecking(false); }
  };

  return (
    <div className="adm-build-bar">
      {parts.join('  ·  ')}
      {hasUpdater && (
        <div style={{ marginTop: 4 }}>
          Updates: {UPDATE_STATE_TEXT[upd.state] || upd.state}
          {upd.available && ` — ${upd.available.version || `build ${upd.available.build_number}`} available`}
          {upd.last_check && `  ·  last checked ${fmtISO(upd.last_check)}`}
          {'  '}
          <button className="adm-btn" style={{ padding: '1px 8px', fontSize: 11 }} disabled={checking} onClick={check}>
            {checking ? 'Checking…' : 'Check for updates'}
          </button>
          {msg && <span style={{ marginLeft: 8 }}>{msg}</span>}
          {upd.last_error && <div style={{ color: '#b91c1c' }}>Last error: {upd.last_error}</div>}
        </div>
      )}
    </div>
  );
}

const STATUS_COLOR = { ok: '#22c55e', degraded: '#f59e0b', unhealthy: '#ef4444' };

function fmtUptime(s) {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function MemoryChart({ history }) {
  const [hover, setHover] = useState(null);  // { ts, mb, x, y }
  if (!history || history.length < 2) return null;

  const W = 600, H = 84, PL = 46, PR = 8, PT = 6, PB = 28;
  const iW = W - PL - PR, iH = H - PT - PB;
  const ts0 = history[0].ts, ts1 = history[history.length - 1].ts;
  const mbs = history.map(p => p.mb);
  const mMin = Math.min(...mbs), mMax = Math.max(...mbs);
  const pad  = Math.max((mMax - mMin) * 0.1, 50);
  const yLo  = mMin - pad, yHi = mMax + pad;
  const px   = ts => PL + ((ts - ts0) / Math.max(ts1 - ts0, 1)) * iW;
  const py   = mb => PT + (1 - (mb - yLo) / (yHi - yLo)) * iH;
  const fmtGb = mb => (mb / 1024).toFixed(2);
  const fmtTime = ts => new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const pts  = history.map(p => `${px(p.ts).toFixed(1)},${py(p.mb).toFixed(1)}`).join(' ');
  const last = history[history.length - 1];
  const lx   = px(last.ts), ly = py(last.mb);
  const labelRight = lx > W * 0.85;

  // X-axis tick labels: start, middle, end
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    ts: ts0 + f * (ts1 - ts0),
    x:  PL + f * iW,
  }));

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX  = ((e.clientX - rect.left) / rect.width) * W;
    const frac  = Math.max(0, Math.min(1, (svgX - PL) / iW));
    const tsAt  = ts0 + frac * (ts1 - ts0);
    // find closest point
    let closest = history[0];
    for (const p of history) if (Math.abs(p.ts - tsAt) < Math.abs(closest.ts - tsAt)) closest = p;
    setHover({ ts: closest.ts, mb: closest.mb, x: px(closest.ts), y: py(closest.mb) });
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 92, display: 'block', marginTop: 4 }}
      onMouseMove={onMouseMove} onMouseLeave={() => setHover(null)}>
      {/* Y labels */}
      <text x={PL - 3} y={PT + 5}      textAnchor="end" fontSize={9} fill="var(--text-muted)">{fmtGb(yHi)}GB</text>
      <text x={PL - 3} y={PT + iH + 1} textAnchor="end" fontSize={9} fill="var(--text-muted)">{fmtGb(yLo)}GB</text>
      {/* Grid lines */}
      <line x1={PL} y1={PT}      x2={W - PR} y2={PT}      stroke="var(--border)" strokeWidth={0.5} />
      <line x1={PL} y1={PT + iH} x2={W - PR} y2={PT + iH} stroke="var(--border)" strokeWidth={0.5} />
      {/* X-axis tick marks + labels */}
      {xTicks.map((t, i) => (
        <g key={i}>
          <line x1={t.x} y1={PT + iH} x2={t.x} y2={PT + iH + 3} stroke="var(--border)" strokeWidth={0.5} />
          <text x={t.x} y={PT + iH + 10} textAnchor="middle" fontSize={8} fill="var(--text-muted)">
            {fmtTime(t.ts)}
          </text>
        </g>
      ))}
      {/* Line */}
      <polyline points={pts} fill="none" stroke="var(--primary)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {/* Latest dot + label */}
      <circle cx={lx} cy={ly} r={3} fill="var(--primary)" />
      <text x={labelRight ? lx - 5 : lx + 5} y={ly + 3}
            textAnchor={labelRight ? 'end' : 'start'} fontSize={9} fill="var(--primary)" fontWeight="600">
        {fmtGb(last.mb)}GB
      </text>
      {/* Hover crosshair + tooltip */}
      {hover && (
        <g>
          <line x1={hover.x} y1={PT} x2={hover.x} y2={PT + iH} stroke="var(--text-muted)" strokeWidth={0.7} strokeDasharray="3,2" />
          <circle cx={hover.x} cy={hover.y} r={3.5} fill="var(--primary)" stroke="var(--surface)" strokeWidth={1.5} />
          {(() => {
            const tip = `${fmtTime(hover.ts)}  ${fmtGb(hover.mb)}GB`;
            const tipW = tip.length * 5.5 + 10;
            const tipX = hover.x + tipW + 4 > W - PR ? hover.x - tipW - 4 : hover.x + 4;
            const tipY = hover.y - 14 < PT ? hover.y + 4 : hover.y - 14;
            return (
              <g>
                <rect x={tipX - 3} y={tipY - 8} width={tipW} height={13} rx={3}
                  fill="var(--surface)" stroke="var(--border)" strokeWidth={0.7} />
                <text x={tipX + tipW / 2 - 3} y={tipY + 1} textAnchor="middle" fontSize={8.5}
                  fill="var(--text)" fontWeight="600">{tip}</text>
              </g>
            );
          })()}
        </g>
      )}
      {/* Invisible hit area */}
      <rect x={PL} y={PT} width={iW} height={iH} fill="transparent" />
    </svg>
  );
}

function SystemHealthSection() {
  const [health,     setHealth]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [hlsDebug,   setHlsDebug]   = useState([]);
  const [hlsRunning, setHlsRunning] = useState(false);
  const [hlsProgress, setHlsProgress] = useState(null);   // cam idx being checked
  const [memHistory, setMemHistory] = useState([]);
  const [tm,           setTm]           = useState({ running: false, top: [] });
  const [tmBusy,       setTmBusy]       = useState(false);
  const [tmShowLast,   setTmShowLast]   = useState(false);
  const [snapshot,     setSnapshot]     = useState(null);   // {top, ts} current-state breakdown
  const [snapBusy,     setSnapBusy]     = useState(false);
  const [tmLastSaved,  setTmLastSaved]  = useState(null);   // {top, captured_at} persisted client-side
  const [storageStat,  setStorageStat]  = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    let healthData = null;
    try {
      const res = await apiFetch('/health');
      healthData = await res.json();
      setHealth(healthData);
      setError(null);
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);   // show health cards immediately — secondary fetches run below
    }

    // Secondary fetches — run in background, don't block the initial render
    setStorageLoading(true);
    apiFetch('/health/storage')
      .then(r => r.json()).then(d => setStorageStat(d)).catch(() => setStorageStat({ error: 'failed' }))
      .finally(() => setStorageLoading(false));

    apiFetch('/health/memory-history')
      .then(r => r.json()).then(d => setMemHistory(d.history ?? [])).catch(() => {});

    apiFetch('/health/tracemalloc')
      .then(r => r.json())
      .then(d => {
        setTm(d);
        if (!d.running && d.top?.length > 0) {
          setTmLastSaved({ top: d.top, captured_at: d.captured_at });
          setTmShowLast(true);
        }
      })
      .catch(() => {});

    // HLS diagnostics are NOT fetched here — see runHlsDebug(). This used to fan
    // out one /hls/{idx}/debug per camera on every page load and refresh, and
    // that endpoint walked the whole recordings directory. On a box holding
    // millions of segments, opening this page blocked the backend on disk I/O
    // and took the site down with it. Diagnostics are on-demand now.
  }, []);

  // One camera at a time, only when asked. Sequential on purpose: firing these
  // in parallel is what made a diagnostic panel capable of stalling the server.
  const fetchHlsForCam = useCallback(async (idx) => {
    const debug = await apiFetch(`/hls/${idx}/debug`)
      .then(r => r.json())
      .catch(e => ({ error: e.message }));
    const m3u8Info = await apiFetch(`/hls/${idx}/live.m3u8`)
      .then(r => r.text())
      .then(text => {
        const lines    = text.split('\n').map(l => l.trim()).filter(Boolean);
        const segLines = lines.filter(l => !l.startsWith('#'));
        const firstSeg = segLines[0] ?? null;
        return { firstSeg, isRelative: firstSeg && !firstSeg.startsWith('/') && !firstSeg.startsWith('http'), segCount: segLines.length };
      })
      .catch(e => ({ error: e.message }));
    return { idx, ...debug, m3u8: m3u8Info };
  }, []);

  const runHlsDebug = useCallback(async () => {
    const camIdxs = (health?.cameras ?? []).map(c => c.idx);
    if (camIdxs.length === 0 || hlsRunning) return;
    setHlsRunning(true);
    setHlsDebug([]);
    try {
      for (const idx of camIdxs) {
        setHlsProgress(idx);
        const r = await fetchHlsForCam(idx);
        setHlsDebug(prev => [...prev, r]);
      }
    } finally {
      setHlsRunning(false);
      setHlsProgress(null);
    }
  }, [health, hlsRunning, fetchHlsForCam]);

  const toggleTm = async () => {
    setTmBusy(true);
    try {
      const action = tm.running ? 'stop' : 'start';
      await apiFetch(`/health/tracemalloc/${action}`, { method: 'POST' });
      const d = await apiFetch('/health/tracemalloc').then(r => r.json());
      setTm(d);
      if (action === 'stop' && d.top?.length > 0) {
        setTmLastSaved({ top: d.top, captured_at: d.captured_at });
        setTmShowLast(true);
      }
      if (action === 'start') {
        setTmShowLast(false);
      }
    } catch {} finally { setTmBusy(false); }
  };

  const takeSnapshot = async () => {
    setSnapBusy(true);
    try {
      const d = await apiFetch('/health/memory-snapshot').then(r => r.json());
      setSnapshot({ top: d.top, ts: Date.now() });
    } catch {} finally { setSnapBusy(false); }
  };

  const downloadTmCsv = (rows, capturedAt) => {
    const ts  = capturedAt ? new Date(capturedAt * 1000).toISOString().slice(0,19).replace(/:/g,'-') : 'snapshot';
    const hdr = ['Type','Variable','Info','Total_MB','Size_Delta_KB','Count_Delta'];
    const csv = [hdr, ...rows.map(r => [r.type, r.var, r.hint, r.total_mb, r.size_kb, r.count_delta])]
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
    const a   = Object.assign(document.createElement('a'), {
      href:     URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `mem_profile_${ts}.csv`,
    });
    a.click(); URL.revokeObjectURL(a.href);
  };

  useEffect(() => {
    fetch_();
    const id = setInterval(fetch_, 30_000);
    return () => clearInterval(id);
  }, [fetch_]);

  const isMobile = useIsMobile();
  const dot   = health ? STATUS_COLOR[health.status] ?? '#94a3b8' : '#94a3b8';
  const cams  = health?.cameras ?? [];
  const r     = health?.resources ?? {};
  const warnC = '#ef4444', okC = 'inherit';

  return (
    <div className="adm-health-section">
      <div className="adm-health-header">
        <h2 className="adm-section-title">System Health</h2>
        <button className="adm-refresh-btn" onClick={fetch_}>↻ Refresh</button>
      </div>

      {loading && (
        <div className="adm-state" style={{ minHeight: 80 }}>
          <div className="adm-spinner" />
        </div>
      )}

      {!loading && error && (
        <div className="adm-state adm-error-state" style={{ minHeight: 80 }}>
          <div>⚠ {error}</div>
          <button className="adm-btn secondary" onClick={fetch_}>Retry</button>
        </div>
      )}

      {!loading && !error && health && (
        <>
          <div className="adm-health-cards">
            <div className="adm-health-card">
              <div className="adm-health-card-label">Status</div>
              <div className="adm-health-card-value">
                <span className="adm-health-dot" style={{ background: dot }} />
                {health.status}
              </div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">Uptime</div>
              <div className="adm-health-card-value">{fmtUptime(health.uptime_s)}</div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">Threads</div>
              <div className="adm-health-card-value">{health.thread_count ?? '—'}</div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">App RAM (incl ffmpeg)</div>
              <div className="adm-health-card-value">
                {(r.proc_rss_mb ?? health.memory_mb) != null
                  ? `${((r.proc_rss_mb ?? health.memory_mb) / 1024).toFixed(2)} GB` : '—'}
              </div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">App CPU (incl ffmpeg)</div>
              <div className="adm-health-card-value">
                {r.proc_cpu_pct != null ? `${r.proc_cpu_pct.toFixed(0)}%` : '—'}
              </div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">Box CPU{r.cpu_count ? ` (${r.cpu_count} cores)` : ''}</div>
              <div className="adm-health-card-value" style={{ color: r.overloaded ? warnC : okC }}>
                {r.load_avg ? `load ${r.load_avg[0].toFixed(1)}`
                  : (r.box_cpu_pct != null ? `${r.box_cpu_pct.toFixed(0)}%` : '—')}
                {r.load_per_core != null && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                    {(r.load_per_core * 100).toFixed(0)}%/core
                  </span>
                )}
              </div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">Box RAM</div>
              <div className="adm-health-card-value" style={{ color: r.swapping ? warnC : okC }}>
                {r.box_mem_total_mb != null ? `${(r.box_mem_total_mb / 1024).toFixed(1)} GB` : '—'}
                {r.box_mem_used_pct != null && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                    {r.box_mem_used_pct.toFixed(0)}% used
                  </span>
                )}
                {r.box_swap_used_mb != null && r.box_swap_used_mb > 0 && (
                  <span style={{ fontSize: 11, color: r.swapping ? warnC : 'var(--text-muted)', marginLeft: 6 }}>
                    · swap {(r.box_swap_used_mb / 1024).toFixed(1)} GB
                  </span>
                )}
              </div>
            </div>
            {(r.disks ?? []).map(d => (
              <div className="adm-health-card" key={d.mount}>
                <div className="adm-health-card-label">
                  {d.external ? '💾 HDD' : '🖥️ Disk'} <span style={{ color: 'var(--text-muted)' }}>{d.mount}</span>
                </div>
                <div className="adm-health-card-value"
                     style={{ color: d.used_pct >= 90 ? warnC : d.used_pct >= 80 ? '#f59e0b' : okC }}>
                  {d.total_gb != null ? `${d.total_gb} GB` : '—'}
                  {d.free_gb != null && (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                      {d.free_gb} GB free · {d.used_pct}% used
                    </span>
                  )}
                </div>
              </div>
            ))}
            <div className="adm-health-card">
              <div className="adm-health-card-label">ffmpeg</div>
              <div className="adm-health-card-value">
                {r.ffmpeg_count != null ? r.ffmpeg_count : '—'}
                {r.ffmpeg_count > 0 && r.ffmpeg_oldest_s != null && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>
                    oldest {Math.round(r.ffmpeg_oldest_s / 60)}m
                  </span>
                )}
              </div>
            </div>
            <div className="adm-health-card">
              <div className="adm-health-card-label">Watchdog</div>
              <div className="adm-health-card-value">
                <span className={`adm-check-indicator ${health.watchdog ? 'on' : 'off'}`}>
                  {health.watchdog ? '✓' : ''}
                </span>
              </div>
            </div>
          </div>

          {memHistory.length >= 2 && (
            <div className="adm-health-card" style={{ marginTop: 8, padding: '8px 12px' }}>
              <div className="adm-health-card-label" style={{ marginBottom: 2 }}>Memory trend (last {Math.round((memHistory[memHistory.length-1].ts - memHistory[0].ts) / 60)} min)</div>
              <MemoryChart history={memHistory} />
            </div>
          )}

          {/* ── Memory Snapshot ── */}
          <div className="adm-health-card" style={{ marginTop: 8, padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="adm-health-card-label" style={{ margin: 0 }}>Memory Snapshot</div>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>current state — no movement needed</span>
              <button className="adm-btn secondary" style={{ marginLeft: 'auto', padding: '3px 12px', fontSize: 12 }}
                onClick={takeSnapshot} disabled={snapBusy}>
                {snapBusy ? '…' : '📸 Take Snapshot'}
              </button>
            </div>
            {snapshot && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Taken: {new Date(snapshot.ts).toLocaleTimeString()}
                  </span>
                  <button className="adm-btn secondary" style={{ padding: '3px 12px', fontSize: 12 }}
                    onClick={() => {
                      const hdr = ['Type','Info','Total_MB','Count'];
                      const csv = [hdr, ...snapshot.top.map(r => [r.type, r.hint, r.total_mb, r.count])]
                        .map(row => row.map(v => `"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\n');
                      const a = Object.assign(document.createElement('a'), {
                        href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
                        download: `mem_snapshot_${new Date(snapshot.ts).toISOString().slice(0,19).replace(/:/g,'-')}.csv`,
                      });
                      a.click(); URL.revokeObjectURL(a.href);
                    }}>
                    ⬇ Download CSV
                  </button>
                </div>
                <div className="adm-table-wrap" style={{ marginTop: 6 }}>
                  <table className="adm-table" style={{ fontSize: 12 }}>
                    <thead><tr><th>Type</th><th>Info</th><th>Total MB</th><th>Count</th></tr></thead>
                    <tbody>
                      {snapshot.top.map((r, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: 'monospace' }}>{r.type}</td>
                          <td style={{ color: 'var(--text-muted)', fontSize: 11 }}>{r.hint || '—'}</td>
                          <td style={{ fontWeight: 600, color: r.total_mb > 100 ? '#ef4444' : r.total_mb > 10 ? '#f59e0b' : 'var(--text)' }}>
                            {r.total_mb} MB
                          </td>
                          <td style={{ color: 'var(--text-muted)' }}>{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* ── Tracemalloc (growth diff) ── */}
          <div className="adm-health-card" style={{ marginTop: 8, padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="adm-health-card-label" style={{ margin: 0 }}>Tracemalloc</div>
              <span style={{ fontSize: 11, color: tm.running ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>
                {tm.running
                  ? `● Running ${tm.mins_running != null ? `(${tm.mins_running}m / auto-stop at ${tm.auto_stop_mins}m)` : ''}`
                  : '○ Stopped'}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {!tm.running && tmLastSaved?.top?.length > 0 && (
                  <button className="adm-btn secondary" style={{ padding: '3px 12px', fontSize: 12 }}
                    onClick={() => setTmShowLast(v => !v)}>
                    {tmShowLast ? 'Hide' : 'Last Results'}
                  </button>
                )}
                <button className="adm-btn secondary" style={{ padding: '3px 12px', fontSize: 12 }}
                  onClick={toggleTm} disabled={tmBusy}>
                  {tmBusy ? '…' : tm.running ? 'Stop' : 'Start'}
                </button>
              </div>
            </div>

            {tm.running && tm.top.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                Collecting… refresh in 30s to see allocations.
              </div>
            )}

            {(() => {
              const rows = tm.running ? tm.top : (tmShowLast ? tmLastSaved?.top : null);
              const capturedAt = tm.running ? tm.captured_at : tmLastSaved?.captured_at;
              if (!rows?.length) return null;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                    {!tm.running && capturedAt && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        Captured: {new Date(capturedAt * 1000).toLocaleTimeString()}
                      </span>
                    )}
                    <button className="adm-btn secondary" style={{ marginLeft: 'auto', padding: '3px 12px', fontSize: 12 }}
                      onClick={() => downloadTmCsv(rows, capturedAt)}>
                      ⬇ Download CSV
                    </button>
                  </div>
                  <div className="adm-table-wrap" style={{ marginTop: 6 }}>
                    <table className="adm-table" style={{ fontSize: 12 }}>
                      <thead><tr><th>Type</th><th>Variable</th><th>Info</th><th>Total</th><th>Size Δ</th><th>Count Δ</th></tr></thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i}>
                            <td style={{ fontFamily: 'monospace' }}>{r.type}</td>
                            <td style={{ fontFamily: 'monospace', color: r.var ? 'var(--primary)' : 'var(--text-muted)' }}>
                              {r.var || '—'}
                            </td>
                            <td style={{ color: 'var(--text-muted)', fontSize: 11 }}>{r.hint || '—'}</td>
                            <td>{r.total_mb} MB</td>
                            <td style={{ color: r.size_kb > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                              {r.size_kb > 0 ? '+' : ''}{r.size_kb} KB
                            </td>
                            <td style={{ color: r.count_delta > 0 ? '#f59e0b' : 'var(--text-muted)' }}>
                              {r.count_delta > 0 ? '+' : ''}{r.count_delta}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>

          {cams.length > 0 && (
            <div className="adm-section" style={{ marginTop: 12 }}>
              {isMobile ? (
                <div className="adm-card-list">
                  {cams.map(c => (
                    <div key={c.idx} className="adm-user-card">
                      <div className="adm-user-card-top">
                        <span className="adm-user-card-name">📷 {c.name}</span>
                        {c.frame_age_s != null && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Age: {c.frame_age_s}s</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span className={`adm-health-pill ${c.connected ? 'on' : (!c.reader_alive ? 'off' : 'warn')}`}>
                          {c.connected ? 'Connected ✓' : (c.reader_alive ? 'Reconnecting ~' : 'Offline ✗')}
                        </span>
                        <span className={`adm-health-pill ${c.reader_alive ? 'on' : 'off'}`}>Reader {c.reader_alive ? '✓' : '✗'}</span>
                        <span className={`adm-health-pill ${c.infer_alive ? 'on' : 'off'}`}>AI {c.infer_alive ? '✓' : '✗'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="adm-table-wrap"><table className="adm-table">
                  <thead>
                    <tr>
                      <th>Camera</th>
                      <th className="adm-th-center">Connected</th>
                      <th className="adm-th-center">Reader</th>
                      <th className="adm-th-center">Inference</th>
                      <th>Frame Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cams.map(c => (
                      <tr key={c.idx}>
                        <td className="adm-cell-name">{c.name}</td>
                        <td className="adm-cell-center">
                          <span className={`adm-check-indicator ${c.connected ? 'on' : 'off'}`}>
                            {c.connected ? '✓' : ''}
                          </span>
                        </td>
                        <td className="adm-cell-center">
                          <span className={`adm-check-indicator ${c.reader_alive ? 'on' : 'off'}`}>
                            {c.reader_alive ? '✓' : ''}
                          </span>
                        </td>
                        <td className="adm-cell-center">
                          <span className={`adm-check-indicator ${c.infer_alive ? 'on' : 'off'}`}>
                            {c.infer_alive ? '✓' : ''}
                          </span>
                        </td>
                        <td className="adm-cell-dt">
                          {c.frame_age_s != null ? `${c.frame_age_s}s` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          )}
          {(health?.cameras?.length ?? 0) > 0 && (
            <div className="adm-section" style={{ marginTop: 16 }}>
              <div className="adm-section-hdr" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <span>HLS Live Stream Diagnostics</span>
                <button className="adm-btn-sm" onClick={runHlsDebug} disabled={hlsRunning}>
                  {hlsRunning
                    ? `Checking cam ${hlsProgress}…`
                    : hlsDebug.length > 0 ? 'Re-check' : 'Run check'}
                </button>
              </div>
              {hlsDebug.length === 0 && !hlsRunning && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Not run. These checks read from disk on the server — run them when you need them.
                </div>
              )}
              {hlsDebug.length > 0 && (isMobile ? (
                <div className="adm-card-list">
                  {hlsDebug.map((d, i) => {
                    const m = d.m3u8 ?? {};
                    const urlOk = m.isRelative === true;
                    const urlBad = m.isRelative === false;
                    return (
                      <div key={i} className="adm-user-card">
                        <div className="adm-user-card-top">
                          <span className="adm-user-card-name">cam{d.idx ?? i}</span>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <span className={`adm-health-pill ${d.error ? 'off' : d.valid_mpegts ? 'on' : 'off'}`}>
                              File {d.error ? 'ERR' : d.valid_mpegts ? '✓' : '✗'}
                            </span>
                            <span className={`adm-health-pill ${urlOk ? 'on' : urlBad ? 'off' : ''}`}>
                              URL {urlOk ? '✓' : urlBad ? '✗' : '—'}
                            </span>
                          </div>
                        </div>
                        <div className="adm-user-card-meta">
                          {d.size_bytes != null && <span>Size: {Math.round(d.size_bytes / 1024)} KB</span>}
                          {d.first_8_bytes && <span style={{ fontFamily: 'monospace' }}>{d.first_8_bytes.split(' ').slice(0,4).join(' ')}</span>}
                        </div>
                        {(m.firstSeg || m.error) && (
                          <div style={{ fontSize: 10, fontFamily: 'monospace', color: urlOk ? 'var(--text-muted)' : urlBad ? 'var(--danger)' : 'var(--text-muted)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.firstSeg ?? m.error}
                          </div>
                        )}
                        {d.error && <div style={{ fontSize: 11, color: 'var(--danger)' }}>{d.error}</div>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="adm-table-wrap"><table className="adm-table">
                  <thead>
                    <tr>
                      <th>Cam</th>
                      <th className="adm-th-center">File OK</th>
                      <th>First 4 bytes</th>
                      <th>Size</th>
                      <th className="adm-th-center">URL OK</th>
                      <th>Segment URL in m3u8</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hlsDebug.map((d, i) => {
                      const m = d.m3u8 ?? {};
                      const urlOk = m.isRelative === true;
                      const urlBad = m.isRelative === false;
                      return (
                        <tr key={i}>
                          <td className="adm-cell-name">cam{d.idx ?? i}</td>
                          <td className="adm-cell-center">
                            {d.error ? (
                              <span style={{ color: 'var(--danger)', fontSize: 11 }}>ERR</span>
                            ) : (
                              <span className={`adm-check-indicator ${d.valid_mpegts ? 'on' : 'off'}`}
                                title={d.valid_mpegts ? '0x47 ✓ valid MPEG-TS' : 'NOT valid MPEG-TS'}>
                                {d.valid_mpegts ? '✓' : '✗'}
                              </span>
                            )}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 11,
                            color: d.valid_mpegts ? 'var(--text)' : 'var(--danger)' }}>
                            {d.first_8_bytes ? d.first_8_bytes.split(' ').slice(0, 4).join(' ') : d.error ?? '—'}
                          </td>
                          <td className="adm-cell-dt">
                            {d.size_bytes != null ? `${Math.round(d.size_bytes / 1024)} KB` : '—'}
                          </td>
                          <td className="adm-cell-center">
                            {m.error ? (
                              <span style={{ color: 'var(--danger)', fontSize: 11 }}>ERR</span>
                            ) : (
                              <span className={`adm-check-indicator ${urlOk ? 'on' : urlBad ? 'off' : ''}`}
                                title={urlOk ? 'Relative URL ✓ proxy will work' : urlBad ? 'Root-relative URL ✗ bypasses proxy → HTML served instead of .ts' : '—'}>
                                {urlOk ? '✓' : urlBad ? '✗' : '—'}
                              </span>
                            )}
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: 10,
                            color: urlOk ? 'var(--text)' : urlBad ? 'var(--danger)' : 'var(--text-muted)',
                            maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={m.firstSeg ?? ''}>
                            {m.firstSeg ?? m.error ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></div>
              ))}
              {hlsDebug.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, padding: '0 10px' }}>
                  URL OK ✓ = relative (e.g. <code>nvr_cam0_....ts?token=...</code>) — proxied correctly<br/>
                  URL OK ✗ = root-relative (e.g. <code>/hls/0/....ts?token=...</code>) — bypasses proxy, returns HTML → restart Parvekshak
                </div>
              )}
            </div>
          )}

          {/* ── NVR Storage ── */}
          {recOn() && health.nvr && !health.nvr.error && (
            <div className="adm-section" style={{ marginTop: 16 }}>
              <div className="adm-section-hdr" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                NVR Storage
              </div>
              <div className="adm-health-cards">
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Recording</div>
                  <div className="adm-health-card-value">
                    <span className={`adm-check-indicator ${health.nvr.enabled ? 'on' : 'off'}`}>
                      {health.nvr.enabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                </div>
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Retention</div>
                  <div className="adm-health-card-value">{health.nvr.retention_days}d</div>
                </div>
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Disk Used</div>
                  <div className="adm-health-card-value"
                    style={{ color: health.nvr.disk_used_pct >= 85 ? '#ef4444' : health.nvr.disk_used_pct >= 70 ? '#f59e0b' : 'inherit' }}>
                    {health.nvr.disk_used_pct}%
                  </div>
                </div>
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Disk Free</div>
                  <div className="adm-health-card-value">{health.nvr.disk_free_gb} GB</div>
                </div>
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Recordings</div>
                  <div className="adm-health-card-value">{health.nvr.recordings_gb} GB</div>
                </div>
                <div className="adm-health-card">
                  <div className="adm-health-card-label">Segments</div>
                  <div className="adm-health-card-value">{health.nvr.segment_count}</div>
                </div>
                {health.nvr.avg_kbps_per_cam != null && (
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Avg Bitrate/Cam</div>
                    <div className="adm-health-card-value">{health.nvr.avg_kbps_per_cam} Kbps</div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 4, padding: '0 10px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {health.nvr.recording_dir}</span>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {health.nvr.oldest_rec && <span>⏮ Oldest: {health.nvr.oldest_rec}</span>}
                  {health.nvr.newest_rec && <span>⏭ Newest: {health.nvr.newest_rec}</span>}
                  {health.nvr.recorders_running?.length > 0 &&
                    <span>🔴 Recording: cam {health.nvr.recorders_running.join(', ')}</span>}
                </div>
              </div>
              {health.nvr.disk_used_pct >= 80 && (
                <div style={{ marginTop: 8, padding: '6px 12px', background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, color: '#ef4444' }}>
                  ⚠️ Disk above 80% — pressure cleanup is purging oldest recordings. Consider pointing NVR to external storage or reducing retention days.
                </div>
              )}
            </div>
          )}

          {/* ── Alert Storage ── */}
          <div className="adm-section" style={{ marginTop: 16 }}>
            <div className="adm-section-hdr" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Alert Storage
            </div>
            {storageLoading && !storageStat && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>Loading storage stats…</div>
            )}
            {storageStat && !storageStat.error && (
              <>
                <div className="adm-health-cards">
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Images</div>
                    <div className="adm-health-card-value">{storageStat.images.count} files</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Images Size</div>
                    <div className="adm-health-card-value">{storageStat.images.size_gb} GB</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Img Retention</div>
                    <div className="adm-health-card-value">{storageStat.images.retention_days}d</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Videos</div>
                    <div className="adm-health-card-value">{storageStat.videos.count} files</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Videos Size</div>
                    <div className="adm-health-card-value"
                      style={{ color: storageStat.videos.quota_gb && storageStat.videos.size_gb >= storageStat.videos.quota_gb * 0.85 ? '#f59e0b' : 'inherit' }}>
                      {storageStat.videos.size_gb} GB
                    </div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Vid Quota</div>
                    <div className="adm-health-card-value">{storageStat.videos.quota_gb} GB ({storageStat.videos.max_disk_pct}%)</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Vid Retention</div>
                    <div className="adm-health-card-value">{storageStat.videos.retention_days}d</div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Disk Free</div>
                    <div className="adm-health-card-value"
                      style={{ color: storageStat.disk.free_vs_reserved_gb != null && storageStat.disk.free_vs_reserved_gb < 5 ? '#ef4444' : storageStat.disk.free_vs_reserved_gb < 20 ? '#f59e0b' : 'inherit' }}>
                      {storageStat.disk.free_gb} GB
                    </div>
                  </div>
                  <div className="adm-health-card">
                    <div className="adm-health-card-label">Reserved</div>
                    <div className="adm-health-card-value">{storageStat.disk.reserved_gb} GB</div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 24, flexWrap: 'wrap', padding: '0 20px 14px' }}>
                  {storageStat.images.oldest && <span>Oldest image: {storageStat.images.oldest}</span>}
                  {storageStat.videos.oldest && <span>Oldest video: {storageStat.videos.oldest}</span>}
                  {storageStat.faces.count > 0 && <span>Face sidecars: {storageStat.faces.count} files ({storageStat.faces.size_mb} MB)</span>}
                </div>
                {storageStat.disk.free_vs_reserved_gb != null && storageStat.disk.free_vs_reserved_gb < 5 && (
                  <div style={{ marginTop: 8, padding: '6px 12px', background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, fontSize: 12, color: '#ef4444' }}>
                    Free disk near reserved floor ({storageStat.disk.reserved_gb} GB) — emergency cleanup will start purging alert files.
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
      <ServerLogsSection />
      <BuildInfoBar />
    </div>
  );
}

export function ServerLogsSection() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - 60);
  const defaultSince = now.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM"

  const [since,   setSince]   = useState(defaultSince);
  const [lines,   setLines]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [fetched, setFetched] = useState(false);
  const boxRef = useRef(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await apiFetch(`/admin/logs?since=${encodeURIComponent(since)}&lines=1000`);
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to fetch logs'); setLines([]); return; }
      setLines(data.lines || []);
      setFetched(true);
      setTimeout(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, 50);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLogs = () => {
    navigator.clipboard?.writeText(lines.join('\n')).catch(() => {});
  };

  const downloadLogs = () => {
    const ts = since.replace('T', '_').replace(/:/g, '-');
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: `parvekshak_logs_${ts}.txt`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="adm-section" style={{ marginTop: 24 }}>
      <div className="adm-section-hdr" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
        📋 Server Logs
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, padding: '0 20px' }}>
        <label style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>Since</label>
        <input
          type="datetime-local"
          value={since}
          onChange={e => setSince(e.target.value)}
          style={{
            flex: 1, minWidth: 180, padding: '7px 10px', borderRadius: 8, fontSize: 13,
            border: '1.5px solid var(--border)', background: 'var(--surface-alt)',
            color: 'var(--text)', outline: 'none',
          }}
        />
        <button
          onClick={fetchLogs}
          disabled={loading}
          style={{
            padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, flexShrink: 0,
            background: 'var(--primary)', color: 'var(--btn-primary-text, #000)',
            border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
          }}>
          {loading ? '…' : '↓ Fetch'}
        </button>
        {fetched && lines.length > 0 && (
          <>
            <button onClick={copyLogs} style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, border: '1.5px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', cursor: 'pointer', flexShrink: 0 }}>
              Copy
            </button>
            <button onClick={downloadLogs} style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, border: '1.5px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)', cursor: 'pointer', flexShrink: 0 }}>
              ↓ Download
            </button>
          </>
        )}
      </div>
      {error && <p style={{ fontSize: 12, color: '#e53935', marginBottom: 8 }}>⚠ {error}</p>}
      {fetched && (
        <div
          ref={boxRef}
          style={{
            background: 'var(--surface-alt)', border: '1.5px solid var(--border)', borderRadius: 10,
            padding: '10px 12px', fontFamily: 'monospace', fontSize: 11.5, lineHeight: 1.6,
            maxHeight: 400, overflowY: 'auto', overflowX: 'auto', whiteSpace: 'pre',
            color: 'var(--text)',
          }}>
          {lines.length === 0
            ? <span style={{ color: 'var(--text-muted)' }}>(no log entries in this time range)</span>
            : lines.map((l, i) => {
                const isErr  = /error|exception|traceback|critical/i.test(l);
                const isWarn = /warn|warning/i.test(l);
                return (
                  <div key={i} style={{ color: isErr ? '#e53935' : isWarn ? '#f59e0b' : 'inherit' }}>
                    {l}
                  </div>
                );
              })
          }
        </div>
      )}
      {fetched && lines.length > 0 && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          {lines.length} lines · journalctl -u parvekshak
        </p>
      )}
    </div>
  );
}

function AddUserModal({ onClose, onAdded }) {
  const [id,      setId]      = useState('');
  const [name,    setName]    = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    if (!id.trim())                     { setError('User ID is required'); return; }
    if (!/^\d{5,12}$/.test(id.trim()))  { setError('User ID must be 5–12 digits (e.g. 7965790454)'); return; }
    if (!name.trim())                   { setError('Name is required'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/admin/users', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: id.trim(), name: name.trim(), is_admin: isAdmin }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to add user'); return; }
      onAdded();
      onClose();
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Add User</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="adm-modal-form" onSubmit={handleSubmit}>
          <div className="adm-field">
            <label>User ID</label>
            <input className="adm-input" type="text" placeholder="e.g. 7965790454"
              value={id}
              onChange={e => setId(e.target.value.replace(/\D/g, '').slice(0, 12))}
              inputMode="numeric" maxLength={12} autoFocus />
          </div>
          <div className="adm-field">
            <label>Name</label>
            <input className="adm-input" type="text" placeholder="Display name"
              value={name} onChange={e => setName(e.target.value)} />
          </div>
          <label className="adm-checkbox-row">
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            <span>Grant admin access</span>
          </label>
          {error && <div className="adm-msg error">{error}</div>}
          <div className="adm-modal-actions">
            <button type="button" className="adm-btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="adm-btn primary" disabled={loading}>
              {loading ? 'Adding…' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [name,    setName]    = useState(user.name || '');
  const [isAdmin, setIsAdmin] = useState(!!user.isAdmin);
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/admin/users/${user.id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: name.trim(), is_admin: isAdmin }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to update user'); return; }
      onSaved();
      onClose();
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Edit User</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="adm-modal-form" onSubmit={handleSubmit}>
          <div className="adm-field">
            <label>User ID</label>
            <input className="adm-input" type="text" value={user.id} disabled />
          </div>
          <div className="adm-field">
            <label>Name</label>
            <input className="adm-input" type="text" placeholder="Display name"
              value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <label className="adm-checkbox-row">
            <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
            <span>Admin access</span>
          </label>
          {error && <div className="adm-msg error">{error}</div>}
          <div className="adm-modal-actions">
            <button type="button" className="adm-btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="adm-btn primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmModal({ user, onCancel, onConfirm, loading }) {
  return (
    <div className="adm-overlay" onClick={onCancel}>
      <div className="adm-modal adm-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Remove User</h3>
          <button className="adm-modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="adm-confirm-body">
          <div className="adm-confirm-icon">⚠️</div>
          <p>Remove <strong>{user.name || user.id}</strong>{user.name ? ` (${user.id})` : ''}?</p>
        </div>
        <div className="adm-modal-actions">
          <button className="adm-btn secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="adm-btn danger"    onClick={onConfirm} disabled={loading}>
            {loading ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

// One line describing a WiZ Wi-Fi device: kind, current IP, live reachability
// (from /ui/devices/status → wiz_status; null until the first health check).
function WizInfo({ ch, st }) {
  const reach = st?.reachable;
  return (
    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
      📶 WiZ {ch.wiz_kind === 'bulb' ? 'bulb' : 'plug'} · {ch.wiz_ip || 'IP unknown'}
      {reach != null && (
        <span style={{ marginLeft: 6, color: reach && !st?.locked ? '#27ae60' : '#e74c3c', fontWeight: 600 }}
          title={st?.locked ? 'WiZ app → Settings → Security → Allow all controls' : undefined}>
          ● {!reach ? 'Not reachable' : st?.locked ? 'Locked — allow all controls in WiZ app' : 'Reachable'}
        </span>
      )}
    </span>
  );
}

// WiZ device picker for Add Device: "Find devices" lists the WiZ plugs/bulbs on
// this network, Test switches one ON for 2 s so the user can tell which is which.
// The user never types an IP or MAC — the picked device is saved by MAC.
function WizPicker({ picked, setPicked, setError }) {
  const [devices, setDevices] = useState(null);   // null = not searched yet
  const [busy,    setBusy]    = useState(false);
  const [testing, setTesting] = useState(null);

  const find = async () => {
    setBusy(true); setError(null);
    try {
      const r = await apiFetch('/ui/devices/wiz/discover');
      const d = await r.json();
      const list = (d.devices || []).filter(x => x.kind !== 'other');
      setDevices(list);
      const free = list.filter(x => !x.channel);
      if (free.length === 1) setPicked(free[0]);   // only one candidate — preselect it
    } catch { setError('Cannot reach server'); }
    finally { setBusy(false); }
  };

  const test = async dev => {
    setTesting(dev.mac); setError(null);
    try {
      const r = await apiFetch('/ui/devices/wiz/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac: dev.mac, ip: dev.ip }),
      });
      if (!r.ok) setError((await r.json()).detail || 'Test failed');
    } catch { setError('Cannot reach server'); }
    finally { setTesting(null); }
  };

  return (
    <div className="adm-field">
      <label>WiZ device</label>
      <button type="button" className="adm-btn secondary" onClick={find} disabled={busy}>
        {busy ? 'Searching…' : devices ? '↻ Search again' : '🔍 Find devices'}
      </button>
      {devices && devices.length === 0 && (
        <p className="adm-dev-empty" style={{ marginTop: 8 }}>
          No WiZ device found. It must be on the same Wi-Fi as this box and set up in the
          WiZ app. In the WiZ app → Settings → Security, keep "Allow local" on and choose
          "Allow all controls".
        </p>
      )}
      {devices && devices.map(d => (
        <label key={d.mac} className="adm-checkbox-row"
          style={{ marginTop: 8, opacity: d.channel ? 0.5 : 1, alignItems: 'center' }}>
          <input type="radio" name="wiz-dev" disabled={!!d.channel}
            checked={picked?.mac === d.mac} onChange={() => setPicked(d)} />
          <span style={{ flex: 1 }}>
            {d.kind === 'bulb' ? '💡 WiZ bulb' : '🔌 WiZ plug'} — {d.ip}
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              {d.channel ? ' · already added' : d.on != null ? ` · now ${d.on ? 'ON' : 'OFF'}` : ''}
            </span>
          </span>
          {!d.channel && (
            <button type="button" className="adm-btn secondary" style={{ fontSize: 12, padding: '3px 10px' }}
              disabled={testing === d.mac} onClick={() => test(d)}>
              {testing === d.mac ? 'Testing…' : 'Test'}
            </button>
          )}
        </label>
      ))}
    </div>
  );
}

function AddChannelModal({ onClose, onAdded, existingChannels = [], ports = [] }) {
  // Multi-board support: each board (device) exposes channels 1–8. When 2+ boards
  // are detected we show a board picker and add channels *within* a chosen board —
  // mirroring the Telegram device→channel flow. With 0 boards known we fall back to
  // the legacy single global-channel input so old backends keep working.
  const hasBoards  = ports.length > 0;
  const multiBoard = ports.length > 1;
  const firstPort  = ports[0]?.port || null;
  const boardOf    = c => c.port || firstPort;   // legacy null-port channels ≈ first board

  const freeBoardChannels = p => {
    const used = new Set(
      existingChannels.filter(c => boardOf(c) === p).map(c => c.board_channel ?? c.channel)
    );
    return Array.from({ length: 8 }, (_, i) => i + 1).filter(n => !used.has(n));
  };
  // Next unused GLOBAL channel id (the backend's stable per-channel key across boards)
  const nextGlobalChannel = () => {
    const used = new Set(existingChannels.map(c => c.channel));
    let g = 1; while (used.has(g)) g++; return g;
  };
  const firstFree = p => { const f = freeBoardChannels(p); return f.length ? String(f[0]) : ''; };

  const [port,          setPort]          = useState(firstPort);
  const [channel,       setChannel]       = useState(() =>
    hasBoards ? firstFree(firstPort)
              : (() => { const u = new Set(existingChannels.map(c => c.channel));
                         for (let i = 1; i <= 8; i++) if (!u.has(i)) return String(i); return ''; })());
  const [name,          setName]          = useState('');
  const [deviceType,    setDeviceType]    = useState('onoff');
  const [onAlert,       setOnAlert]       = useState(true);
  const [schedule,      setSchedule]      = useState('always');
  const [autoOff,       setAutoOff]       = useState('15');
  const [cooldown,      setCooldown]      = useState('30');
  const [fixedEnabled,  setFixedEnabled]  = useState(false);
  const [fixedStart,    setFixedStart]    = useState('07:00');
  const [fixedEnd,      setFixedEnd]      = useState('19:00');
  const [alertMode,     setAlertMode]     = useState('steady');
  const [blinkMs,       setBlinkMs]       = useState('500');
  const [error,         setError]         = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [source,        setSource]        = useState('board');   // 'board' | 'wiz'
  const [wizDev,        setWizDev]        = useState(null);      // picked discovery entry
  const isWiz     = source === 'wiz';
  const canBlink  = !(isWiz && wizDev?.kind === 'plug');         // a plug's relay can't blink

  const submitWiz = async () => {
    if (!wizDev)       { setError('Find and select a WiZ device'); return; }
    if (!name.trim())  { setError('Name is required'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/ui/devices/wiz/channels', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), mac: wizDev.mac, ip: wizDev.ip, kind: wizDev.kind,
          device_type: deviceType, on_alert: onAlert, alert_schedule: parseRelaySchedule(schedule),
          auto_off_seconds: parseInt(autoOff) || 15,
          cooldown_seconds: parseInt(cooldown) || 30,
          fixed_schedule: fixedEnabled ? { start: fixedStart, end: fixedEnd } : null,
          ...(deviceType === 'light'
            ? { alert_mode: canBlink ? alertMode : 'steady', blink_interval_ms: parseInt(blinkMs) || 500 } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed'); return; }
      onAdded(data);
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  };

  // When the selected board changes, jump to its first free channel
  const onPortChange = p => { setPort(p); setChannel(firstFree(p)); };

  const handleSubmit = async e => {
    e.preventDefault();
    setError(null);
    if (isWiz) { submitWiz(); return; }
    const boardCh = parseInt(channel);
    if (!boardCh || boardCh < 1 || boardCh > 8) { setError('Channel must be 1–8'); return; }
    if (!name.trim())                            { setError('Name is required'); return; }
    if (hasBoards && freeBoardChannels(port).indexOf(boardCh) === -1) {
      setError(`Channel ${boardCh} is already used on this board`); return;
    }
    // Board mode → global id is auto-assigned and the physical target is (port, boardCh).
    // Legacy mode → the picked number IS the global channel (no board addressing).
    const globalCh = hasBoards ? nextGlobalChannel() : boardCh;
    setLoading(true);
    try {
      const res = await apiFetch('/ui/devices/relay/channels', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: globalCh, name: name.trim(), device_type: deviceType,
          ...(hasBoards ? { port, board_channel: boardCh } : {}),
          on_alert: onAlert, alert_schedule: parseRelaySchedule(schedule),
          auto_off_seconds: parseInt(autoOff) || 15,
          cooldown_seconds: parseInt(cooldown) || 30,
          fixed_schedule: fixedEnabled ? { start: fixedStart, end: fixedEnd } : null,
          ...(deviceType === 'light'
            ? { alert_mode: alertMode, blink_interval_ms: parseInt(blinkMs) || 500 } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed'); return; }
      onAdded(data);
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Add Device</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="adm-modal-form" onSubmit={handleSubmit}>
          <div className="adm-field">
            <label>Connected via</label>
            <select className="adm-input" value={source}
              onChange={e => { setSource(e.target.value); setError(null); }}>
              <option value="board">Relay board (USB)</option>
              <option value="wiz">WiZ smart plug / bulb (Wi-Fi)</option>
            </select>
          </div>
          {isWiz && <WizPicker picked={wizDev} setPicked={setWizDev} setError={setError} />}
          {!isWiz && multiBoard && (
            <div className="adm-field">
              <label>Relay Board (Device)</label>
              <select className="adm-input" value={port || ''} onChange={e => onPortChange(e.target.value)}>
                {ports.map(b => (
                  <option key={b.port} value={b.port}>
                    {b.label}{b.connected === false ? ' (offline)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="adm-dev-form-row">
            {!isWiz && (
            <div className="adm-field" style={{ flex: '0 0 90px' }}>
              <label>Channel (1–8)</label>
              <input className="adm-input" type="number" min="1" max="8"
                value={channel} onChange={e => setChannel(e.target.value)} autoFocus />
            </div>
            )}
            <div className="adm-field" style={{ flex: 1 }}>
              <label>Name</label>
              <input className="adm-input" type="text" placeholder="e.g. Siren, Light, Motor"
                value={name} onChange={e => setName(e.target.value)} />
            </div>
          </div>
          <div className="adm-field">
            <label>Type</label>
            <select className="adm-input" value={deviceType} onChange={e => {
              const t = e.target.value;
              setDeviceType(t);
              if (t === 'motor') { setOnAlert(false); setFixedEnabled(true); }
            }}>
              <option value="onoff">On / Off</option>
              <option value="siren">Siren</option>
              <option value="light">Light</option>
              <option value="strobe">Strobe</option>
              <option value="speaker">Speaker</option>
              <option value="motor">Motor</option>
            </select>
          </div>
          <div className="adm-dev-form-row">
            <div className="adm-field" style={{ flex: 1 }}>
              <label>Auto-off (s)</label>
              <input className="adm-input" type="number" min="1"
                value={autoOff} onChange={e => setAutoOff(e.target.value)} />
            </div>
            <div className="adm-field" style={{ flex: 1 }}>
              <label>Cooldown (s)</label>
              <input className="adm-input" type="number" min="0"
                value={cooldown} onChange={e => setCooldown(e.target.value)} />
            </div>
          </div>
          <label className="adm-checkbox-row">
            <input type="checkbox" checked={onAlert} onChange={e => setOnAlert(e.target.checked)} />
            <span>Fire on alert</span>
          </label>
          {onAlert && (
            <div className="adm-field">
              <label>Alert Schedule</label>
              <input className="adm-input" type="text" placeholder="always | HH:MM-HH:MM"
                value={schedule} onChange={e => setSchedule(e.target.value)} />
            </div>
          )}
          {onAlert && deviceType === 'light' && canBlink && (
            <LightAlertModeFields mode={alertMode} setMode={setAlertMode}
              blinkMs={blinkMs} setBlinkMs={setBlinkMs} />
          )}
          {onAlert && deviceType === 'light' && !canBlink && (
            <p className="adm-dev-empty">A WiZ plug stays ON on alert — blinking would wear out its relay.</p>
          )}
          <div className="adm-field">
            <label>Fixed Schedule</label>
            <div className="adm-sched-wrap">
              <select className="adm-sched-mode" value={fixedEnabled ? 'on' : 'off'}
                onChange={e => setFixedEnabled(e.target.value === 'on')}>
                <option value="off">Off</option>
                <option value="on">Scheduled</option>
              </select>
              {fixedEnabled && (
                <div className="adm-sched-times">
                  <input type="time" className="adm-sched-time" value={fixedStart}
                    onChange={e => setFixedStart(e.target.value)} />
                  <span className="adm-sched-sep">→</span>
                  <input type="time" className="adm-sched-time" value={fixedEnd}
                    onChange={e => setFixedEnd(e.target.value)} />
                </div>
              )}
            </div>
          </div>
          {error && <div className="adm-msg error">{error}</div>}
          <div className="adm-modal-actions">
            <button type="button" className="adm-btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="adm-btn primary" disabled={loading}>
              {loading ? 'Adding…' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Light-only: how the light behaves on an alert or panic. Blink runs for the
// channel's Auto-off seconds, then the light returns to its fixed schedule.
function LightAlertModeFields({ mode, setMode, blinkMs, setBlinkMs }) {
  return (
    <div className="adm-dev-form-row">
      <div className="adm-field" style={{ flex: 1 }}>
        <label>On alert / panic</label>
        <select className="adm-input" value={mode} onChange={e => setMode(e.target.value)}>
          <option value="steady">Stay ON</option>
          <option value="blink">Blink</option>
        </select>
      </div>
      {mode === 'blink' && (
        <div className="adm-field" style={{ flex: 1 }}>
          <label>Blink interval (ms)</label>
          <input className="adm-input" type="number" min="100" step="100"
            value={blinkMs} onChange={e => setBlinkMs(e.target.value)} />
        </div>
      )}
    </div>
  );
}

function EditChannelModal({ channel, onClose, onSaved }) {
  const [name,         setName]         = useState(channel.name || '');
  const [deviceType,   setDeviceType]   = useState(channel.type || 'onoff');
  const [onAlert,      setOnAlert]      = useState(channel.on_alert ?? true);
  const [schedule,     setSchedule]     = useState(
    fmtRelaySchedule(channel.alert_schedule)
  );
  const [autoOff,      setAutoOff]      = useState(String(channel.auto_off_seconds ?? 15));
  const [cooldown,     setCooldown]     = useState(String(channel.cooldown_seconds ?? 30));
  const fs = channel.fixed_schedule;
  const [fixedEnabled, setFixedEnabled] = useState(!!(fs && typeof fs === 'object'));
  const [fixedStart,   setFixedStart]   = useState(fs?.start || '07:00');
  const [fixedEnd,     setFixedEnd]     = useState(fs?.end   || '19:00');
  const [alertMode,    setAlertMode]    = useState(channel.alert_mode || 'steady');
  const [blinkMs,      setBlinkMs]      = useState(String(channel.blink_interval_ms ?? 500));
  const wizPlug = channel.backend === 'wiz' && channel.wiz_kind === 'plug';   // no blink on a plug
  const [error,        setError]        = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [saved,        setSaved]        = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/ui/devices/relay/channels/${channel.channel}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), type: deviceType,
          on_alert: onAlert, alert_schedule: parseRelaySchedule(schedule),
          auto_off_seconds: parseInt(autoOff) || 15,
          cooldown_seconds: parseInt(cooldown) || 30,
          fixed_schedule: fixedEnabled ? { start: fixedStart, end: fixedEnd } : null,
          ...(deviceType === 'light'
            ? { alert_mode: alertMode, blink_interval_ms: parseInt(blinkMs) || 500 } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed'); return; }
      setSaved(true);
      setTimeout(() => onSaved(data), 1000);
    } catch {
      setError('Cannot reach server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="adm-overlay" onClick={onClose}>
      <div className="adm-modal" onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Edit Channel {channel.channel}</h3>
          <button className="adm-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="adm-modal-form" onSubmit={handleSubmit}>
          {channel.port && (
            <div className="adm-board-tag">
              🔌 {channel.port.split('/').pop()} · CH {channel.board_channel ?? channel.channel}
            </div>
          )}
          {channel.backend === 'wiz' && (
            <div className="adm-board-tag"><WizInfo ch={channel} /></div>
          )}
          <div className="adm-field">
            <label>Name</label>
            <input className="adm-input" type="text" placeholder="e.g. Siren, Light, Motor"
              value={name} onChange={e => setName(e.target.value)} autoFocus />
          </div>
          <div className="adm-field">
            <label>Type</label>
            <select className="adm-input" value={deviceType} onChange={e => setDeviceType(e.target.value)}>
              <option value="onoff">On / Off</option>
              <option value="siren">Siren</option>
              <option value="light">Light</option>
              <option value="strobe">Strobe</option>
              <option value="speaker">Speaker</option>
              <option value="motor">Motor</option>
            </select>
          </div>
          <div className="adm-dev-form-row">
            <div className="adm-field" style={{ flex: 1 }}>
              <label>Auto-off (s)</label>
              <input className="adm-input" type="number" min="1"
                value={autoOff} onChange={e => setAutoOff(e.target.value)} />
            </div>
            <div className="adm-field" style={{ flex: 1 }}>
              <label>Cooldown (s)</label>
              <input className="adm-input" type="number" min="0"
                value={cooldown} onChange={e => setCooldown(e.target.value)} />
            </div>
          </div>
          <label className="adm-checkbox-row">
            <input type="checkbox" checked={onAlert} onChange={e => setOnAlert(e.target.checked)} />
            <span>Fire on alert</span>
          </label>
          {onAlert && (
            <div className="adm-field">
              <label>Alert Schedule</label>
              <input className="adm-input" type="text" placeholder="always | HH:MM-HH:MM"
                value={schedule} onChange={e => setSchedule(e.target.value)} />
            </div>
          )}
          {onAlert && deviceType === 'light' && !wizPlug && (
            <LightAlertModeFields mode={alertMode} setMode={setAlertMode}
              blinkMs={blinkMs} setBlinkMs={setBlinkMs} />
          )}
          <div className="adm-field">
            <label>Fixed Schedule</label>
            <div className="adm-sched-wrap">
              <select className="adm-sched-mode" value={fixedEnabled ? 'on' : 'off'}
                onChange={e => setFixedEnabled(e.target.value === 'on')}>
                <option value="off">Off</option>
                <option value="on">Scheduled</option>
              </select>
              {fixedEnabled && (
                <div className="adm-sched-times">
                  <input type="time" className="adm-sched-time" value={fixedStart}
                    onChange={e => setFixedStart(e.target.value)} />
                  <span className="adm-sched-sep">→</span>
                  <input type="time" className="adm-sched-time" value={fixedEnd}
                    onChange={e => setFixedEnd(e.target.value)} />
                </div>
              )}
            </div>
          </div>
          {error && <div className="adm-msg error">{error}</div>}
          {saved && <div className="adm-msg success">✓ Saved</div>}
          <div className="adm-modal-actions">
            <button type="button" className="adm-btn secondary" onClick={onClose} disabled={loading || saved}>Cancel</button>
            <button type="submit" className="adm-btn primary" disabled={loading || saved}>
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DevicesSection() {
  const [status,    setStatus]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [scanning,  setScanning]  = useState({ relay: false, audio: false });
  const [testing,   setTesting]   = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [showAddCh, setShowAddCh] = useState(false);
  const [editingCh, setEditingCh] = useState(null); // { channel, name, type }
  const [talkKey,   setTalkKey]   = useState(null); // `${card}_${device}` currently recording
  const [talkErr,   setTalkErr]   = useState('');
  const talkRecRef  = useRef(null);
  const volTimerRef = useRef({});  // per-card debounce timers for the volume slider
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    try {
      const res  = await apiFetch('/ui/devices/status');
      const data = await res.json();
      setStatus(data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scanRelay = async () => {
    setScanning(s => ({ ...s, relay: true }));
    try { await apiFetch('/ui/devices/relay/scan', { method: 'POST' }); await load(); }
    catch {} finally { setScanning(s => ({ ...s, relay: false })); }
  };

  const scanAudio = async () => {
    setScanning(s => ({ ...s, audio: true }));
    try { await apiFetch('/ui/devices/audio/scan', { method: 'POST' }); await load(); }
    catch {} finally { setScanning(s => ({ ...s, audio: false })); }
  };

  const testAudio = async (card, device) => {
    const key = `${card}_${device}`;
    setTesting(key);
    try { await apiFetch(`/ui/devices/audio/test/${card}/${device}`, { method: 'POST' }); }
    catch {} finally { setTimeout(() => setTesting(t => t === key ? null : t), 3000); }
  };

  const removeAudio = async (card, device) => {
    try {
      const res = await apiFetch(`/ui/devices/audio/${card}/${device}`, { method: 'DELETE' });
      // Never wipe the list on a failed/old-backend response — reload the truth.
      if (!res.ok) { await load(); return; }
      const data = await res.json();
      if (Array.isArray(data?.devices)) {
        setStatus(s => ({ ...s, audio_devices: data.devices }));
      } else {
        await load();
      }
    } catch { await load(); }
  };

  // Volume is per ALSA card — reflect the change on every device sharing that card.
  // Update the slider immediately, but debounce the PUT (which shells out to amixer)
  // so dragging doesn't fire a request per step.
  const setAudioVolume = (card, volume) => {
    setStatus(s => ({
      ...s,
      audio_devices: (s.audio_devices || []).map(d =>
        d.card === card ? { ...d, volume } : d),
    }));
    clearTimeout(volTimerRef.current[card]);
    volTimerRef.current[card] = setTimeout(() => {
      apiFetch(`/ui/devices/audio/${card}/volume`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ volume }),
      }).catch(() => {});
    }, 250);
  };

  // Push-to-talk to one speaker: tap to record, tap again to stop & send.
  // Records via the Flutter WebView native recorder when present, else getUserMedia.
  // The chosen card/device is sent so the backend plays on exactly that speaker.
  const talkToDevice = async (card, device) => {
    const key = `${card}_${device}`;
    setTalkErr('');

    if (talkKey === key) {           // already recording this device → stop
      if (talkRecRef.current) talkRecRef.current.stop();
      else if (window.FlutterAudio) window.FlutterAudio.postMessage('stop');
      return;
    }
    if (talkKey) return;             // another device is mid-recording

    // Flutter WebView native path — target passed via window.__speakTarget
    if (window.FlutterAudio) {
      window.__speakTarget = { card, device };
      window.__flutterAudioDone = (st) => {
        setTalkKey(null);
        window.__speakTarget = null;
        window.__flutterAudioDone = null;
        if (st === 'permission_error') setTalkErr('Mic access denied — allow it in app permissions.');
        else if (st === 'record_error' || st === 'error') setTalkErr('Recording failed. Please try again.');
      };
      window.FlutterAudio.postMessage('start');
      setTalkKey(key);
      return;
    }

    // Browser path
    if (!navigator.mediaDevices?.getUserMedia) {
      setTalkErr('Mic not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setTalkKey(null);
        talkRecRef.current = null;
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size === 0) { setTalkErr('No audio recorded.'); return; }
        const fd = new FormData();
        fd.append('audio', blob, 'voice.webm');
        fd.append('card', String(card));
        fd.append('device', String(device));
        try { await apiFetch('/controls/speak/audio', { method: 'POST', body: fd }); }
        catch { setTalkErr('Failed to send audio.'); }
      };
      talkRecRef.current = mr;
      setTalkKey(key);
      mr.start();
    } catch {
      const isHttp = window.location.protocol === 'http:' && window.location.hostname !== 'localhost';
      setTalkErr(isHttp ? 'Mic requires HTTPS.' : 'Mic access denied.');
    }
  };

  const updateRelayConfig = async patch => {
    setSavingCfg(true);
    try {
      const res  = await apiFetch('/ui/devices/relay/config', {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(patch),
      });
      const data = await res.json();
      setStatus(s => ({ ...s, relay_global: data }));
    } catch {} finally { setSavingCfg(false); }
  };

  const removeChannel = async channel => {
    try {
      const res  = await apiFetch(`/ui/devices/relay/channels/${channel}`, { method: 'DELETE' });
      const data = await res.json();
      setStatus(s => ({ ...s, relay_channels: data.channels }));
    } catch {}
  };

  const patchChannel = async (channel, patch) => {
    try {
      await apiFetch(`/ui/devices/relay/channels/${channel}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(patch),
      });
      await load();
    } catch {}
  };


  if (loading) return (
    <div className="adm-state" style={{ minHeight: 80 }}>
      <div className="adm-spinner" />
    </div>
  );

  const relayGlobal  = status?.relay_global  ?? {};
  const channels     = status?.relay_channels ?? [];
  const audioDevices = status?.audio_devices  ?? [];
  const relayConn    = status?.relay_connected ?? false;
  const boards       = status?.relay_ports    ?? [];
  const multiBoard   = boards.length > 1;

  // Group channels under their relay board (device). Legacy channels with no `port`
  // and legacy backends (no relay_ports) collapse to a single flat group so nothing
  // breaks — the board sub-headers only appear once 2+ boards are detected.
  const firstPort = boards[0]?.port || null;
  // WiZ Wi-Fi devices have no board/port — list them in their own group.
  const wizSt         = status?.wiz_status ?? {};
  const wizChannels   = channels.filter(ch => ch.backend === 'wiz');
  const boardChannels = channels.filter(ch => ch.backend !== 'wiz');
  let channelGroups;
  if (multiBoard) {
    const known = new Set(boards.map(b => b.port));
    channelGroups = boards.map(b => ({
      key: b.port, label: b.label, connected: b.connected,
      channels: boardChannels.filter(ch => (ch.port || firstPort) === b.port),
    }));
    const orphans = boardChannels.filter(ch => ch.port && !known.has(ch.port));
    if (orphans.length) channelGroups.push({ key: '__orphan', label: 'Unassigned', connected: false, channels: orphans });
  } else {
    channelGroups = [{ key: '__all', label: boards[0]?.label, connected: boards[0]?.connected, channels: boardChannels }];
  }
  if (wizChannels.length) channelGroups.push({ key: '__wiz', label: 'Wi-Fi (WiZ)', wiz: true, channels: wizChannels });
  channelGroups = channelGroups.filter(g => g.channels.length);

  return (
    <div className="adm-dev-section">

      <div className="adm-health-header" style={{ marginBottom: 16 }}>
        <h2 className="adm-section-title">Speakers / Lights / Devices</h2>
        <button className="adm-refresh-btn" onClick={load}>↻ Refresh</button>
      </div>

      {/* ── Relay Boards (Devices) ── */}
      <div className="adm-dev-group">
        <div className="adm-dev-group-hdr">
          <span className="adm-dev-group-label">Relay Boards</span>
          <button className="adm-btn secondary" onClick={scanRelay} disabled={scanning.relay}>
            {scanning.relay ? 'Scanning…' : 'Detect Relay Boards'}
          </button>
        </div>
        {boards.length === 0 ? (
          <p className="adm-dev-empty">
            {relayConn
              ? 'Relay board connected.'
              : 'No relay board detected — click "Detect Relay Boards" to scan for connected devices.'}
          </p>
        ) : (
          <div className="adm-board-list">
            {boards.map(b => (
              <div key={b.port} className="adm-board-chip">
                <span className={`adm-board-dot ${b.connected ? 'on' : 'off'}`} />
                <span className="adm-board-chip-label">{b.label}</span>
                <span className="adm-board-chip-state">{b.connected ? 'Connected' : 'Offline'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Alert Config ── */}
      <div className="adm-dev-group">
        <div className="adm-dev-group-hdr">
          <span className="adm-dev-group-label">Alert Config</span>
        </div>
        <div className="adm-dev-config-row">
          <label className="adm-checkbox-row">
            <input type="checkbox"
              checked={relayGlobal.enabled ?? true}
              disabled={savingCfg}
              onChange={e => updateRelayConfig({ enabled: e.target.checked })} />
            <span>Alerts enabled</span>
          </label>
          <div className="adm-field" style={{ flex: 1, minWidth: 200 }}>
            <label>Schedule</label>
            {/* The window is stored as {start,end}; "always" is the only string the
                engine understands. This field used to show the literal word "always"
                whenever it held an object and write that back on blur — so merely
                tabbing through it widened a customer's siren to 24/7, silently.
                The placeholder also promised HH:MM-HH:MM, which _in_schedule cannot
                parse: a string range hits its except and fails OPEN to always. It is
                rendered and parsed properly now, so the promise holds. */}
            <input className="adm-input" type="text"
              placeholder="always | HH:MM-HH:MM"
              defaultValue={fmtRelaySchedule(relayGlobal.alert_schedule)}
              onBlur={e => updateRelayConfig({ alert_schedule: parseRelaySchedule(e.target.value) })} />
          </div>
        </div>
      </div>

      {/* ── Relay Channels ── */}
      <div className="adm-dev-group">
        <div className="adm-dev-group-hdr">
          <span className="adm-dev-group-label">Relay Channels</span>
          <button className="adm-btn primary" style={{ fontSize: 12, padding: '5px 14px' }}
            onClick={() => setShowAddCh(true)}>
            + Add Device
          </button>
        </div>
        {channels.length === 0 ? (
          <p className="adm-dev-empty">No relay channels configured.</p>
        ) : channelGroups.map(g => (
          <div key={g.key} className="adm-board-group">
            {(multiBoard || g.wiz) && (
              <div className="adm-board-subhdr">
                {!g.wiz && <span className={`adm-board-dot ${g.connected ? 'on' : 'off'}`} />}
                <span className="adm-board-subhdr-name">{g.wiz ? '📶' : '🔌'} {g.label || 'Board'}</span>
                <span className="adm-board-subhdr-count">{g.channels.length} {g.wiz ? 'device' : 'ch'}</span>
              </div>
            )}
            {isMobile ? (
            <div className="adm-card-list">
              {g.channels.map(ch => {
                const sched    = ch.alert_schedule;
              const isTimed  = sched && typeof sched === 'object';
              const fs       = ch.fixed_schedule;
              const hasFixed = fs && typeof fs === 'object';
              return (
                <div key={ch.channel} className="adm-user-card">
                  <div className="adm-user-card-top">
                    <div>
                      <div className="adm-user-card-name">{ch.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {ch.backend === 'wiz' ? <WizInfo ch={ch} st={wizSt[ch.channel]} /> : `CH ${ch.board_channel ?? ch.channel}`}
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                      <input type="checkbox" checked={ch.on_alert ?? false}
                        onChange={e => patchChannel(ch.channel, { on_alert: e.target.checked })} />
                      On Alert
                    </label>
                  </div>
                  <div className="adm-ch-card-body">
                    <div className="adm-ch-row">
                      <span className="adm-ch-label">Alert Schedule</span>
                      {(ch.on_alert ?? false) ? (
                        <div className="adm-sched-wrap">
                          <select className="adm-sched-mode" value={isTimed ? 'timed' : 'always'}
                            onChange={e => patchChannel(ch.channel, { alert_schedule: e.target.value === 'always' ? 'always' : { start: '22:00', end: '06:00' } })}>
                            <option value="always">Always</option>
                            <option value="timed">Timed</option>
                          </select>
                          {isTimed && (
                            <div className="adm-sched-times">
                              <input type="time" className="adm-sched-time" value={sched.start || '22:00'}
                                onChange={e => patchChannel(ch.channel, { alert_schedule: { ...sched, start: e.target.value } })} />
                              <span className="adm-sched-sep">→</span>
                              <input type="time" className="adm-sched-time" value={sched.end || '06:00'}
                                onChange={e => patchChannel(ch.channel, { alert_schedule: { ...sched, end: e.target.value } })} />
                            </div>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                    </div>
                    <div className="adm-ch-row">
                      <span className="adm-ch-label">Fixed Schedule</span>
                      <div className="adm-sched-wrap">
                        <select className="adm-sched-mode" value={hasFixed ? 'on' : 'off'}
                          onChange={e => patchChannel(ch.channel, { fixed_schedule: e.target.value === 'on' ? { start: '07:00', end: '19:00' } : null })}>
                          <option value="off">Off</option>
                          <option value="on">Scheduled</option>
                        </select>
                        {hasFixed && (
                          <div className="adm-sched-times">
                            <input type="time" className="adm-sched-time" value={fs.start || '07:00'}
                              onChange={e => patchChannel(ch.channel, { fixed_schedule: { ...fs, start: e.target.value } })} />
                            <span className="adm-sched-sep">→</span>
                            <input type="time" className="adm-sched-time" value={fs.end || '19:00'}
                              onChange={e => patchChannel(ch.channel, { fixed_schedule: { ...fs, end: e.target.value } })} />
                          </div>
                        )}
                      </div>
                    </div>
                    {ch.auto_off_seconds && (
                      <div className="adm-ch-row">
                        <span className="adm-ch-label">Auto-off</span>
                        <span style={{ fontSize: 13 }}>{ch.auto_off_seconds}s</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button className="adm-btn secondary" style={{ fontSize: 12, padding: '4px 12px' }}
                      onClick={() => setEditingCh(ch)}>
                      Edit
                    </button>
                    <button className="adm-del-btn" onClick={() => removeChannel(ch.channel)}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="adm-section">
            <div className="adm-table-wrap"><table className="adm-table">
              <thead>
                <tr>
                  <th>Ch</th><th>Name</th>
                  <th className="adm-th-center">On Alert</th>
                  <th>Alert Schedule</th>
                  <th>Fixed Schedule</th>
                  <th>Auto-off</th><th></th>
                </tr>
              </thead>
              <tbody>
                {g.channels.map(ch => {
                  const sched = ch.alert_schedule;
                  const isTimed = sched && typeof sched === 'object';
                  const fs = ch.fixed_schedule;
                  const hasFixed = fs && typeof fs === 'object';
                  return (
                  <>
                  <tr key={ch.channel}>
                    <td className="adm-cell-id">{ch.backend === 'wiz' ? '📶' : (ch.board_channel ?? ch.channel)}</td>
                    <td className="adm-cell-name">
                      {ch.name}
                      {ch.backend === 'wiz' && <div><WizInfo ch={ch} st={wizSt[ch.channel]} /></div>}
                    </td>
                    <td className="adm-cell-center">
                      <input type="checkbox" checked={ch.on_alert ?? false}
                        onChange={e => patchChannel(ch.channel, { on_alert: e.target.checked })} />
                    </td>
                    <td className="adm-cell-alert-sched">
                      {(ch.on_alert ?? false) && (
                        <div className="adm-sched-wrap">
                          <select className="adm-sched-mode"
                            value={isTimed ? 'timed' : 'always'}
                            onChange={e => patchChannel(ch.channel, {
                              alert_schedule: e.target.value === 'always'
                                ? 'always'
                                : { start: '22:00', end: '06:00' }
                            })}>
                            <option value="always">Always</option>
                            <option value="timed">Timed</option>
                          </select>
                          {isTimed && (
                            <div className="adm-sched-times">
                              <input type="time" className="adm-sched-time"
                                value={sched.start || '22:00'}
                                onChange={e => patchChannel(ch.channel, { alert_schedule: { ...sched, start: e.target.value } })} />
                              <span className="adm-sched-sep">→</span>
                              <input type="time" className="adm-sched-time"
                                value={sched.end || '06:00'}
                                onChange={e => patchChannel(ch.channel, { alert_schedule: { ...sched, end: e.target.value } })} />
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="adm-cell-sched">
                      <div className="adm-sched-wrap">
                        <select className="adm-sched-mode"
                          value={hasFixed ? 'on' : 'off'}
                          onChange={e => patchChannel(ch.channel, {
                            fixed_schedule: e.target.value === 'on'
                              ? { start: '07:00', end: '19:00' }
                              : null
                          })}>
                          <option value="off">Off</option>
                          <option value="on">Scheduled</option>
                        </select>
                        {hasFixed && (
                          <div className="adm-sched-times">
                            <input type="time" className="adm-sched-time"
                              value={fs.start || '07:00'}
                              onChange={e => patchChannel(ch.channel, { fixed_schedule: { ...fs, start: e.target.value } })} />
                            <span className="adm-sched-sep">→</span>
                            <input type="time" className="adm-sched-time"
                              value={fs.end || '19:00'}
                              onChange={e => patchChannel(ch.channel, { fixed_schedule: { ...fs, end: e.target.value } })} />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="adm-cell-dt">{ch.auto_off_seconds}s</td>
                    <td className="adm-cell-action" style={{ whiteSpace: 'nowrap' }}>
                      <button className="adm-row-action-btn" title="Edit" onClick={() => setEditingCh(ch)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="adm-row-action-btn danger" title="Remove" onClick={() => removeChannel(ch.channel)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      </button>
                    </td>
                  </tr>
                  </>
                  );
                })}
              </tbody>
            </table></div>
          </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Audio Devices ── */}
      <div className="adm-dev-group">
        <div className="adm-dev-group-hdr">
          <span className="adm-dev-group-label">Audio Devices</span>
          <button className="adm-btn secondary" onClick={scanAudio} disabled={scanning.audio}>
            {scanning.audio ? 'Scanning…' : 'Scan Audio Devices'}
          </button>
        </div>
        {talkKey && (
          <p className="adm-dev-empty" style={{ color: 'var(--accent, #2a7)' }}>
            🎙 Recording… tap Stop to send to the speaker.
          </p>
        )}
        {talkErr && <p className="adm-dev-empty" style={{ color: 'var(--danger, #c33)' }}>{talkErr}</p>}
        {audioDevices.length === 0 ? (
          <p className="adm-dev-empty">No audio devices found — click "Scan Audio Devices" to detect.</p>
        ) : isMobile ? (
          <div className="adm-card-list">
            {audioDevices.map(d => {
              const key = `${d.card}_${d.device}`;
              return (
                <div key={key} className="adm-user-card">
                  <div className="adm-user-card-top">
                    <span className="adm-user-card-name">{d.name}</span>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button className={`adm-edit-btn${talkKey === key ? ' active' : ''}`}
                        disabled={talkKey !== null && talkKey !== key}
                        onClick={() => talkToDevice(d.card, d.device)}>
                        {talkKey === key ? '⏹ Stop' : '🎙 Talk'}
                      </button>
                      <button className="adm-edit-btn" disabled={testing === key}
                        onClick={() => testAudio(d.card, d.device)}>
                        {testing === key ? 'Playing…' : 'Test'}
                      </button>
                      <button className="adm-del-btn" onClick={() => removeAudio(d.card, d.device)}>Remove</button>
                    </div>
                  </div>
                  <div className="adm-user-card-meta">
                    {d.type && <span>{d.type}</span>}
                    {d.backend && <span>{d.backend}</span>}
                  </div>
                  {typeof d.volume === 'number' && (
                    <div className="adm-ch-row" style={{ marginTop: 8 }}>
                      <span className="adm-ch-label">🔊 Volume</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                        <input type="range" min="0" max="100" step="5" value={d.volume}
                          style={{ flex: 1 }}
                          onChange={e => setAudioVolume(d.card, Number(e.target.value))} />
                        <span style={{ fontSize: 12, minWidth: 34, textAlign: 'right' }}>{d.volume}%</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="adm-section">
            <div className="adm-table-wrap"><table className="adm-table">
              <thead>
                <tr><th>Name</th><th>Type</th><th>Backend</th><th>Volume</th><th></th></tr>
              </thead>
              <tbody>
                {audioDevices.map(d => {
                  const key = `${d.card}_${d.device}`;
                  return (
                    <tr key={key}>
                      <td className="adm-cell-name">{d.name}</td>
                      <td className="adm-cell-dt">{d.type}</td>
                      <td className="adm-cell-dt">{d.backend}</td>
                      <td className="adm-cell-dt">
                        {typeof d.volume === 'number' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 150 }}>
                            <input type="range" min="0" max="100" step="5" value={d.volume}
                              style={{ flex: 1 }}
                              onChange={e => setAudioVolume(d.card, Number(e.target.value))} />
                            <span style={{ minWidth: 34, textAlign: 'right' }}>{d.volume}%</span>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="adm-cell-action" style={{ whiteSpace: 'nowrap' }}>
                        <button className={`adm-edit-btn${talkKey === key ? ' active' : ''}`}
                          disabled={talkKey !== null && talkKey !== key}
                          title="Push to talk — click to record, click again to send"
                          onClick={() => talkToDevice(d.card, d.device)}>
                          {talkKey === key ? '⏹ Stop' : '🎙 Talk'}
                        </button>
                        <button className="adm-edit-btn"
                          disabled={testing === key}
                          onClick={() => testAudio(d.card, d.device)}>
                          {testing === key ? 'Playing…' : 'Test'}
                        </button>
                        <button className="adm-row-action-btn danger" title="Remove"
                          onClick={() => removeAudio(d.card, d.device)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        )}
      </div>

      {showAddCh && (
        <AddChannelModal
          onClose={() => setShowAddCh(false)}
          onAdded={data => { setStatus(s => ({ ...s, relay_channels: data.channels })); setShowAddCh(false); }}
          existingChannels={channels}
          ports={boards}
        />
      )}
      {editingCh && (
        <EditChannelModal
          channel={editingCh}
          onClose={() => setEditingCh(null)}
          onSaved={data => { setStatus(s => ({ ...s, relay_channels: data.channels })); setEditingCh(null); }}
        />
      )}
    </div>
  );
}

function UsersTab() {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [showAdd,    setShowAdd]    = useState(false);
  const [editUser,   setEditUser]   = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const isMobile = useIsMobile();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch('/admin/users');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load');
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await apiFetch(`/admin/users/${confirmDel.id}`, { method: 'DELETE' });
      setConfirmDel(null);
      load();
    } catch {}
    finally { setDeleting(false); }
  };

  const visible = users.filter(u => u.status !== 'deleted');

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="adm-btn primary" onClick={() => setShowAdd(true)}>+ Add User</button>
      </div>

      {loading && (
        <div className="adm-state">
          <div className="adm-spinner" />
          Loading users…
        </div>
      )}
      {!loading && error && (
        <div className="adm-state adm-error-state">
          <div>⚠️ {error}</div>
          <button className="adm-btn secondary" onClick={load}>Retry</button>
        </div>
      )}
      {!loading && !error && visible.length === 0 && (
        <div className="adm-state">
          <div className="adm-empty-icon">👥</div>
          <div>No users configured.</div>
          <button className="adm-btn primary" onClick={() => setShowAdd(true)}>Add User</button>
        </div>
      )}
      {!loading && !error && visible.length > 0 && (
        <div className="adm-section">
          {isMobile ? (
            <div className="adm-card-list">
              {visible.map(u => (
                <div key={u.id} className={`adm-user-card${u.status === 'orphan' ? ' orphan' : ''}`}>
                  <div className="adm-user-card-top">
                    <span className="adm-user-card-name">
                      {u.name || <span className="adm-empty-val">{u.status === 'orphan' ? 'No details' : '—'}</span>}
                    </span>
                    <div className="adm-user-card-badges">
                      <span className={`adm-status ${u.status}`}>
                        {u.status === 'orphan' ? 'Default' : 'Active'}
                      </span>
                      {u.isAdmin && <span className="adm-badge-admin">Admin</span>}
                    </div>
                  </div>
                  <div className="adm-user-card-bottom">
                    <div className="adm-user-card-meta">
                      <span>ID: {u.id}</span>
                      {u.createdOn && <span>{fmtDT(u.createdOn)}</span>}
                    </div>
                    <div className="adm-user-card-actions">
                      <button className="adm-edit-btn" onClick={() => setEditUser(u)}>Edit</button>
                      {visible.length > 1 && (
                        <button className="adm-del-btn" onClick={() => setConfirmDel(u)}>Delete</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="adm-table-wrap"><table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th-id">User ID</th><th>Name</th><th>Added On</th>
                  <th>Status</th><th className="adm-th-center">Admin</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(u => (
                  <tr key={u.id} className={u.status === 'orphan' ? 'adm-row-orphan' : ''}>
                    <td className="adm-cell-id">{u.id}</td>
                    <td className="adm-cell-name">
                      {u.name || <span className="adm-empty-val">{u.status === 'orphan' ? 'No details' : '—'}</span>}
                    </td>
                    <td className="adm-cell-dt">{fmtDT(u.createdOn) || <span className="adm-empty-val">—</span>}</td>
                    <td>
                      <span className={`adm-status ${u.status}`}>
                        {u.status === 'orphan' ? 'Default' : 'Active'}
                      </span>
                    </td>
                    <td className="adm-cell-center">
                      <span className={`adm-check-indicator ${u.isAdmin ? 'on' : 'off'}`}>
                        {u.isAdmin ? '✓' : ''}
                      </span>
                    </td>
                    <td className="adm-cell-action">
                      <button className="adm-edit-btn" onClick={() => setEditUser(u)}>Edit</button>
                      {visible.length > 1 && (
                        <button className="adm-del-btn" onClick={() => setConfirmDel(u)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}

      {showAdd   && <AddUserModal  onClose={() => setShowAdd(false)}  onAdded={load} />}
      {editUser  && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={load} />}
      {confirmDel && (
        <ConfirmModal
          user={confirmDel}
          onCancel={() => setConfirmDel(null)}
          onConfirm={handleDelete}
          loading={deleting}
        />
      )}
    </>
  );
}

const STEP_ORDER_DIRECT = ['source', 'discover', 'creds', 'cam_type', 'probe', 'name', 'confirm'];
const STEP_ORDER_NVR    = ['source', 'nvr_ip',   'creds', 'cam_type', 'probe', 'name', 'confirm'];
const STEP_ORDER_RTSP   = ['source', 'manual', 'probe_url', 'name', 'confirm'];
const STEP_LABELS       = ['Source', 'Camera',   'Creds', 'Brand',    'Probe', 'Name', 'Save'];
const STEP_LABELS_NVR   = ['Source', 'NVR',      'Creds', 'Brand',    'Probe', 'Name', 'Save'];
const STEP_LABELS_RTSP  = ['Source', 'URL',       'Test',             'Name', 'Save'];

// Exported for Guided Setup's "Add Camera" (a camera the scan didn't find).
export function AddCameraWizard({ onDone, doneLabel = 'Back to List', recordingOff = false }) {
  const [camTypes,    setCamTypes]    = useState({});
  const [step,        setStep]        = useState('source');
  const [source,      setSource]      = useState(null);
  const [ip,          setIp]          = useState('');
  const [mac,         setMac]         = useState('');
  const [nvrCh,       setNvrCh]       = useState('1');
  const [camType,     setCamType]     = useState('');
  const [camTypeErr,  setCamTypeErr]  = useState(false);
  const [user,        setUser]        = useState('');
  const [pass_,       setPass_]       = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [manualRtsp,    setManualRtsp]    = useState('');
  const [manualRtspSub, setManualRtspSub] = useState('');  // optional sub-stream URL
  const [rtspTransport, setRtspTransport] = useState('tcp');  // "tcp" (default) | "udp" — some cameras only support one
  const [noAuth,        setNoAuth]        = useState(false);
  const [camName,     setCamName]     = useState('');
  const [zone,        setZone]        = useState('');
  const [recStream,   setRecStream]   = useState('sub');
  const [liveStream,  setLiveStream]  = useState('');      // '' = same as recording (default, no extra connection)
  const [recAudio,    setRecAudio]    = useState(false);
  const [addEnabled,  setAddEnabled]  = useState(true);   // false = save config, don't connect yet
  const [saving,      setSaving]      = useState(false);
  const [saveErr,     setSaveErr]     = useState(null);
  const [saveOk,      setSaveOk]      = useState(false);
  const [newIdx,      setNewIdx]      = useState(null);    // index of the camera just added

  const [scanning,    setScanning]    = useState(false);
  const [scanJobId,   setScanJobId]   = useState(null);
  const [scanStatus,  setScanStatus]  = useState(null);
  const [discovered,  setDiscovered]  = useState([]);
  const [scanLogs,    setScanLogs]    = useState([]);
  const [subnetPick,  setSubnetPick]  = useState(false);   // "Scan other subnets" toggle
  const [subnetOpts,  setSubnetOpts]  = useState([]);      // discovered subnets
  const [selSubnets,  setSelSubnets]  = useState([]);      // subnets chosen to scan
  const [customRange, setCustomRange] = useState('');      // extra routed range to sweep
  const [scanUpstream, setScanUpstream] = useState([]);    // subnets behind a 2nd router (add by IP)
  const [scanFilter,  setScanFilter]  = useState('');      // results search text
  const [scanFilterMode, setScanFilterMode] = useState('all'); // all | new | configured

  const [probeJobId,  setProbeJobId]  = useState(null);
  const [probeLogs,   setProbeLogs]   = useState([]);
  const [probeLogCt,  setProbeLogCt]  = useState(0);
  const [probeStatus, setProbeStatus] = useState(null);
  const [existingIps, setExistingIps] = useState([]);
  const logRef = useCallback(el => { if (el) el.scrollTop = el.scrollHeight; }, []);

  useEffect(() => {
    apiFetch('/ui/cameras/types').then(r => r.json()).then(d =>
      setCamTypes({ auto: { label: 'Auto (try all)', default_user: '', default_pass: '' }, ...d })
    ).catch(() => {});
    apiFetch('/health').then(r => r.json()).then(d =>
      setExistingIps((d.cameras || []).map(c => c.ip).filter(Boolean))
    ).catch(() => {});
  }, []);

  useEffect(() => {
    const t = camTypes[camType];
    if (t && camType && !user && !pass_) {
      setUser(t.default_user || '');
      setPass_(t.default_pass || '');
    }
    if (camType) setCamTypeErr(false);
  }, [camType, camTypes]); // intentionally excludes user/pass_ to avoid loop

  useEffect(() => {
    if (!scanJobId || scanStatus === 'done' || scanStatus === 'error') return;
    const id = setInterval(async () => {
      try {
        const res  = await apiFetch(`/ui/cameras/network-scan/${scanJobId}`);
        const data = await res.json();
        setDiscovered(data.cameras || []);
        setScanLogs(data.logs || []);
        setScanUpstream(data.diag?.upstream || []);
        setScanStatus(data.status);
        if (data.status !== 'running') setScanning(false);
      } catch { setScanning(false); setScanStatus('error'); }
    }, 1500);
    return () => clearInterval(id);
  }, [scanJobId, scanStatus]);

  useEffect(() => {
    if (!probeJobId || probeStatus === 'done' || probeStatus === 'failed' || probeStatus === 'error' || probeStatus === 'cancelled') return;
    const id = setInterval(async () => {
      try {
        const res  = await apiFetch(`/ui/cameras/probe/${probeJobId}?after=${probeLogCt}`);
        const data = await res.json();
        if (data.logs?.length) { setProbeLogs(p => [...p, ...data.logs]); setProbeLogCt(data.total); }
        setProbeStatus(data.status);
        if (data.result) {
          setProbeResult(data.result);
          const derived = deriveSubUrl(data.result.rtsp_url);
          if (derived) setManualRtspSub(prev => prev || derived);
        }
        if (data.status !== 'running') clearInterval(id);
      } catch {}
    }, 800);
    return () => clearInterval(id);
  }, [probeJobId, probeStatus, probeLogCt]);

  const toggleSubnetPick = async () => {
    const next = !subnetPick;
    setSubnetPick(next);
    if (next && subnetOpts.length === 0) {
      try {
        const res  = await apiFetch('/ui/cameras/subnets');
        const data = await res.json();
        const list = data.subnets || [];
        setSubnetOpts(list);
        setSelSubnets(list);          // default: all selected
      } catch {}
    }
  };

  const startScan = async () => {
    setScanning(true); setScanStatus('running'); setDiscovered([]); setScanJobId(null); setScanLogs([]);
    // Custom field is a SINGLE IP only (one safe connection) — strip any CIDR the
    // user might paste so it can never turn into a whole-subnet sweep.
    const ipOnly = customRange.trim().split('/')[0].trim();
    const validIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(ipOnly);
    const subs = subnetPick
      ? [...selSubnets, ...(validIp ? [ipOnly] : [])]
      : [];
    const body = subs.length ? { subnets: subs } : {};
    try {
      const res  = await apiFetch('/ui/cameras/network-scan', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      setScanJobId(data.job_id);
    } catch { setScanning(false); setScanStatus('error'); }
  };

  // Native app path: the WebView can't do UDP multicast, so ask the Flutter host
  // to run ONVIF discovery on the phone's Wi-Fi and hand back the camera list.
  const findViaApp = () => {
    if (!window.FlutterDiscovery) return;
    setScanning(true); setScanStatus('running'); setDiscovered([]); setScanUpstream([]);
    setScanLogs(['📱 Discovering cameras on this phone’s network…']);
    window.__onCamerasDiscovered = (json) => {
      let list = [];
      try { list = JSON.parse(json); } catch { /* leave empty */ }
      const cams = list.map(c => ({
        ip: c.ip, port: 554, mac: '', vendor: '', via: 'onvif',
        rtsp: c.rtsp || '',
        already_added: existingIps.includes(c.ip),
        name: '',
      }));
      setDiscovered(cams);
      setScanLogs(l => [...l, `Found ${cams.length} camera(s).`]);
      setScanStatus('done'); setScanning(false);
    };
    // camera creds (if the operator already typed them) let the app resolve RTSP URLs
    window.FlutterDiscovery.postMessage(JSON.stringify({ user: user || null, pass: pass_ || '' }));
  };

  const startProbe = async () => {
    if (!camType) { setCamTypeErr(true); return; }
    setCamTypeErr(false);
    setProbeLogs([]); setProbeLogCt(0); setProbeStatus('running');
    setProbeResult(null); setProbeJobId(null);
    setStep('probe');
    try {
      const res  = await apiFetch('/ui/cameras/probe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: ip.trim(), cam_type: camType, user: user || null, password: pass_ || null }),
      });
      const data = await res.json();
      setProbeJobId(data.job_id);
    } catch {
      setProbeLogs(['💥 Failed to reach server']);
      setProbeStatus('error');
    }
  };

  const save = async () => {
    setSaving(true); setSaveErr(null);
    const s       = v => (typeof v === 'string' && v.trim()) ? v.trim() : null;
    const rtspUrl = s(probeResult?.rtsp_url) || s(manualRtsp);

    if (source === 'rtsp' && !rtspUrl) {
      setSaveErr('Main stream URL is required'); setSaving(false); return;
    }

    const payload = {
      name: camName.trim(),
      ...(s(ip)       && { ip:           s(ip) }),
      ...(s(camType)  && { type:         s(camType) }),
      ...(rtspUrl     && { rtsp_url:     rtspUrl }),
      ...(s(probeResult?.user)     || s(user)  ? { user:     s(probeResult?.user)     || s(user)  } : {}),
      ...(s(probeResult?.password) || s(pass_) ? { password: s(probeResult?.password) || s(pass_) } : {}),
      ...(s(zone)     && { zone:         s(zone) }),
      ...(source === 'nvr' ? { channel: parseInt(nvrCh) || 1 } : {}),
      ...(s(manualRtspSub) ? { rtsp_url_sub: s(manualRtspSub) }
        : s(probeResult?.rtsp_url_sub) ? { rtsp_url_sub: s(probeResult.rtsp_url_sub) } : {}),
      ...(source !== 'nvr' && rtspUrl
        ? { url_source: s(probeResult?.rtsp_url) ? urlSourceFromProbe(probeResult.method) : 'manual' } : {}),
      ...(rtspTransport === 'udp' ? { rtsp_transport: 'udp' } : {}),
      ...(s(mac)           ? { mac:          s(mac) }           : {}),
      ...(recStream === 'main' ? { record_stream: 'main' } : {}),
      ...(liveStream ? { live_stream: liveStream } : {}),
      ...(recAudio ? { record_audio: true } : {}),
      ...(addEnabled === false ? { enabled: false } : {}),
      ...(recordingOff ? { nvr: false } : {}),   // Guided Setup: recording starts off
    };
    try {
      const res = await apiFetch('/ui/cameras', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        setSaveErr(
          Array.isArray(d) ? d.map(e => {
            const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : '';
            return field ? `${field}: ${e.msg}` : (e.msg || String(e));
          }).join('; ')
          : typeof d === 'string' ? d
          : 'Failed to save'
        );
        return;
      }
      setNewIdx(Number.isInteger(data.index) ? data.index : null);
      setSaveOk(true);
    } catch { setSaveErr('Cannot reach server'); }
    finally { setSaving(false); }
  };

  const reset = () => {
    setStep('source'); setSource(null); setIp(''); setMac(''); setNvrCh('1'); setCamType(''); setCamTypeErr(false);
    setUser(''); setPass_(''); setShowPass(false); setNoAuth(false); setProbeResult(null); setManualRtsp(''); setManualRtspSub(''); setRtspTransport('tcp');
    setCamName(''); setZone(''); setRecStream('sub'); setLiveStream(''); setRecAudio(false); setAddEnabled(true); setSaving(false); setSaveErr(null); setSaveOk(false); setNewIdx(null);
    setScanning(false); setScanJobId(null); setScanStatus(null); setDiscovered([]);
    setScanLogs([]); setSubnetPick(false); setSubnetOpts([]); setSelSubnets([]);
    setCustomRange(''); setScanUpstream([]); setScanFilter(''); setScanFilterMode('all');
    setProbeJobId(null); setProbeLogs([]); setProbeLogCt(0); setProbeStatus(null);
  };

  const startUrlProbe = async () => {
    setProbeLogs([]); setProbeLogCt(0); setProbeStatus('running');
    setProbeResult(null); setProbeJobId(null);
    setStep('probe_url');
    try {
      const res  = await apiFetch('/ui/cameras/probe-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rtsp_url: manualRtsp.trim(), transport: rtspTransport }),
      });
      const data = await res.json();
      setProbeJobId(data.job_id);
    } catch {
      setProbeLogs(['💥 Failed to reach server']);
      setProbeStatus('error');
    }
  };

  const order      = source === 'nvr' ? STEP_ORDER_NVR : source === 'rtsp' ? STEP_ORDER_RTSP : STEP_ORDER_DIRECT;
  const labels     = source === 'nvr' ? STEP_LABELS_NVR : source === 'rtsp' ? STEP_LABELS_RTSP : STEP_LABELS;
  const effectStep = (step === 'manual'    && source !== 'rtsp') ? 'probe'
                   : (step === 'probe_url' && source !== 'rtsp') ? 'probe'
                   : step;
  const stepIdx    = order.indexOf(effectStep);

  const logLine = (l, i) => (
    <div key={i} className={`probe-log-line${
      l.startsWith('  ✅') || l.startsWith('✅') ? ' success' :
      l.startsWith('  ✗')  || l.startsWith('❌') ? ' fail'    :
      l.startsWith('💥')   ? ' error' : ''}`}>{l}</div>
  );

  if (saveOk) return (
    <div className="wiz-wrap">
      <div className="wiz-success">
        <div className="wiz-success-icon">✓</div>
        <div className="wiz-success-title">Camera Added</div>
        <div className="wiz-success-name">{camName}</div>
        {/* Ask right here: from Settings → Cameras nothing else prompts for it, and a
            camera left on its own heavy streams slows the box and fills the disk. */}
        {newIdx !== null && (
          <div className="wiz-success-stream"><CameraStreamSettings idx={newIdx} /></div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="adm-btn secondary" onClick={reset}>Add Another</button>
          {onDone && <button className="adm-btn primary" onClick={onDone}>{doneLabel}</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="wiz-wrap">

      {/* ── Step bar ── */}
      {step !== 'source' && (
        <div className="wiz-stepbar">
          {labels.flatMap((lbl, i) => {
            const cls = i === stepIdx ? ' active' : i < stepIdx ? ' done' : '';
            const items = [];
            if (i > 0) items.push(
              <div key={`ln${i}`} className={`wiz-connector${i <= stepIdx ? ' done' : ''}`} />
            );
            items.push(
              <div key={lbl} className={`wiz-step${cls}`}>
                <div className="wiz-step-dot">{i < stepIdx ? '✓' : i + 1}</div>
                <div className="wiz-step-label">{lbl}</div>
              </div>
            );
            return items;
          })}
        </div>
      )}

      {/* ── Step 1: Source ── */}
      {step === 'source' && (
        <div className="wiz-source-grid">
          <div className="wiz-source-hdr">
            <h2 className="wiz-title">Add Camera</h2>
            <p className="wiz-subtitle">How is this camera connected to the network?</p>
          </div>
          <div className="wiz-source-cards">
            <button className="wiz-src-card" onClick={() => { setSource('direct'); setStep('discover'); }}>
              <div className="wiz-src-icon">📷</div>
              <div className="wiz-src-title">Direct IP Camera</div>
              <div className="wiz-src-desc">Camera is connected directly via Ethernet or Wi-Fi — you need its IP address</div>
              <div className="wiz-src-arrow">→</div>
            </button>
            <button className="wiz-src-card" onClick={() => { setSource('nvr'); setStep('nvr_ip'); }}>
              <div className="wiz-src-icon">🖥</div>
              <div className="wiz-src-title">Via NVR / DVR</div>
              <div className="wiz-src-desc">Camera feeds into a Network Video Recorder — enter the NVR IP and channel number</div>
              <div className="wiz-src-arrow">→</div>
            </button>
            <button className="wiz-src-card" onClick={() => { setSource('rtsp'); setStep('manual'); }}>
              <div className="wiz-src-icon">🔗</div>
              <div className="wiz-src-title">RTSP URL</div>
              <div className="wiz-src-desc">You already have the full RTSP stream URL — paste it directly, no probing needed</div>
              <div className="wiz-src-arrow">→</div>
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2a: Discover (Direct IP) ── */}
      {step === 'discover' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn" onClick={() => setStep('source')}>← Back</button>
            <div className="wiz-card-title">Find Camera</div>
          </div>

          <div className="wiz-scan-section">
            <button className="adm-btn primary wiz-scan-btn" onClick={startScan}
              disabled={scanning || (subnetPick && selSubnets.length === 0 && !customRange.trim())}>
              {scanning ? <><span className="wiz-scan-dot" />Scanning…</> : '⊕ Scan Network'}
            </button>

            {typeof window !== 'undefined' && window.FlutterDiscovery && (
              <button className="adm-btn secondary wiz-scan-btn" onClick={findViaApp}
                disabled={scanning} style={{ marginTop: 8 }}>
                📱 Find cameras on this phone's network
              </button>
            )}

            <label className="wiz-subnet-toggle" style={{ display: 'flex', alignItems: 'center',
              gap: 8, margin: '10px 0 0', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={subnetPick} onChange={toggleSubnetPick} disabled={scanning} />
              Scan other subnets
            </label>

            {subnetPick && (
              <div className="wiz-subnet-list" style={{ margin: '8px 0 0', padding: '8px 10px',
                border: '1px solid var(--adm-border,#333)', borderRadius: 6 }}>
                {subnetOpts.length === 0
                  ? <div className="wiz-scan-hint">Detecting subnets…</div>
                  : <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                        opacity: .8, marginBottom: 6, cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={selSubnets.length === subnetOpts.length}
                          ref={el => { if (el) el.indeterminate =
                            selSubnets.length > 0 && selSubnets.length < subnetOpts.length; }}
                          onChange={e => setSelSubnets(e.target.checked ? [...subnetOpts] : [])}
                          disabled={scanning} />
                        Select all
                      </label>
                      {subnetOpts.map(sn => (
                        <label key={sn} style={{ display: 'flex', alignItems: 'center', gap: 8,
                          fontSize: 13, padding: '2px 0', cursor: 'pointer' }}>
                          <input type="checkbox" checked={selSubnets.includes(sn)}
                            onChange={e => setSelSubnets(prev => e.target.checked
                              ? [...prev, sn] : prev.filter(x => x !== sn))}
                            disabled={scanning} />
                          {sn}
                        </label>
                      ))}
                    </>}
                <div style={{ marginTop: 8, borderTop: '1px solid var(--adm-border,#333)', paddingTop: 8 }}>
                  <label style={{ fontSize: 12, opacity: .8, display: 'block', marginBottom: 4 }}>
                    Check a specific camera IP
                    <span style={{ opacity: .6 }}> — a camera on another subnet (behind a second router)</span>
                  </label>
                  <input className="adm-input" type="text"
                    placeholder="192.168.1.145"
                    value={customRange} onChange={e => setCustomRange(e.target.value)}
                    disabled={scanning}
                    style={{ fontFamily: 'monospace', fontSize: 13, width: '100%' }} />
                  <div style={{ fontSize: 11, opacity: .55, marginTop: 4 }}>
                    Checks just this one IP (a single connection — safe across routers).
                    Get the IP from the ONVIF tool or the mobile app.
                  </div>
                </div>
              </div>
            )}

            {scanning && (
              <div className="wiz-scan-progress">
                <div className="probe-progress" style={{ margin: '12px 0 0' }}>
                  <div className="probe-progress-bar" />
                </div>
                <div className="wiz-scan-hint">Pinging local network, checking port 554…</div>
                {scanLogs.length > 0 && (
                  <div className="wiz-scan-log" ref={logRef} style={{ maxHeight: 120,
                    overflowY: 'auto', margin: '8px 0 0', fontFamily: 'monospace',
                    fontSize: 11, lineHeight: 1.5, opacity: .85 }}>
                    {scanLogs.slice(-40).map((l, i) => <div key={i}>{l}</div>)}
                  </div>
                )}
              </div>
            )}

            {discovered.length > 0 && (() => {
              const q = scanFilter.trim().toLowerCase();
              const rows = discovered
                .filter(c => scanFilterMode === 'all' ? true
                           : scanFilterMode === 'new' ? !c.already_added : c.already_added)
                .filter(c => !q || [c.ip, c.mac, c.vendor, c.name]
                  .some(v => (v || '').toLowerCase().includes(q)))
                .slice()
                .sort((a, b) => (a.already_added ? 1 : 0) - (b.already_added ? 1 : 0)); // new first
              const newCams   = discovered.filter(c => !c.already_added);
              const addedCams = discovered.filter(c => c.already_added);
              const showFilter = discovered.length > 8;
              return (
              <div className="wiz-discovered">
                <div className="wiz-discovered-label">
                  {newCams.length && addedCams.length
                    ? `${newCams.length} new · ${addedCams.length} already configured`
                    : addedCams.length && !newCams.length
                      ? `${addedCams.length} configured camera${addedCams.length !== 1 ? 's' : ''} on network`
                      : `${newCams.length} camera${newCams.length !== 1 ? 's' : ''} found`}
                </div>

                {showFilter && (
                  <div className="wiz-cam-filter" style={{ display: 'flex', gap: 8, flexWrap: 'wrap',
                    alignItems: 'center', margin: '4px 0 8px' }}>
                    <input className="adm-input" type="text" placeholder="Filter by IP, MAC, vendor…"
                      value={scanFilter} onChange={e => setScanFilter(e.target.value)}
                      style={{ flex: '1 1 180px', fontSize: 13, padding: '5px 10px' }} />
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[['all', `All (${discovered.length})`], ['new', `New (${newCams.length})`],
                        ['configured', `Configured (${addedCams.length})`]].map(([mode, lbl]) => (
                        <button key={mode} onClick={() => setScanFilterMode(mode)}
                          className={`adm-btn ${scanFilterMode === mode ? 'primary' : 'secondary'}`}
                          style={{ padding: '5px 10px', fontSize: 12 }}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="wiz-cam-table-wrap" style={{ overflowX: 'auto' }}>
                  <table className="wiz-cam-table" style={{ width: '100%', borderCollapse: 'collapse',
                    fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11,
                        textTransform: 'uppercase', letterSpacing: .3 }}>
                        <th style={{ padding: '6px 10px 6px 0' }}>IP</th>
                        <th className="wiz-col-sec" style={{ padding: '6px 10px' }}>Vendor</th>
                        <th className="wiz-col-sec" style={{ padding: '6px 10px' }}>MAC</th>
                        <th className="wiz-col-sec" style={{ padding: '6px 10px' }}>Port</th>
                        <th className="wiz-col-sec" style={{ padding: '6px 10px' }}>Found via</th>
                        <th style={{ padding: '6px 0 6px 10px', textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(cam => (
                        <tr key={cam.ip} style={{ borderTop: '1px solid var(--adm-border,#2a2a2a)',
                          opacity: cam.already_added ? .6 : 1 }}>
                          <td style={{ padding: '8px 10px 8px 0', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {cam.ip}
                            {cam.name && <div style={{ fontSize: 11, opacity: .7 }}>{cam.name}</div>}
                            {/* mobile-only: fold vendor/MAC/port under the IP (columns hidden ≤640px) */}
                            <div className="wiz-ip-meta">
                              {[cam.vendor, cam.mac, cam.port && `:${cam.port}`].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </td>
                          <td className="wiz-col-sec" style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{cam.vendor || '—'}</td>
                          <td className="wiz-col-sec" style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11,
                            opacity: .8, whiteSpace: 'nowrap' }}>{cam.mac || '—'}</td>
                          <td className="wiz-col-sec" style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{cam.port}</td>
                          <td className="wiz-col-sec" style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            <span className={`cam-status-badge ${cam.via === 'onvif' ? 'online' : 'connecting'}`}>
                              {cam.via === 'onvif' ? `ONVIF${cam.onvif_port ? ` :${cam.onvif_port}` : ''}` : 'Port scan'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 0 8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {cam.already_added
                              ? <span className="cam-status-badge disabled">Configured</span>
                              : <button className="adm-btn primary" style={{ padding: '4px 12px', fontSize: 12 }}
                                  onClick={() => {
                                    setIp(cam.ip); setMac(cam.mac || '');
                                    if (cam.rtsp) { setManualRtsp(cam.rtsp); setStep('name'); }
                                    else setStep('creds');
                                  }}>
                                  Select →
                                </button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length === 0 && (
                  <div className="wiz-scan-empty" style={{ padding: '10px 0' }}>
                    No cameras match this filter.
                  </div>
                )}
              </div>
              );
            })()}

            {scanUpstream.length > 0 && (
              <div className="wiz-upstream-hint" style={{ margin: '10px 0 0', padding: '8px 10px',
                border: '1px solid var(--adm-border,#333)', borderRadius: 6, fontSize: 12,
                background: 'rgba(245,158,11,.08)' }}>
                📡 Detected network(s) behind another router:{' '}
                <strong style={{ fontFamily: 'monospace' }}>{scanUpstream.join(', ')}</strong>.
                <div style={{ opacity: .75, marginTop: 3 }}>
                  Cameras there can't be auto-listed (they're behind a second router's NAT).
                  Add one by entering its exact IP{scanUpstream[0] ? ` (e.g. ${scanUpstream[0].replace(/\.0\/24$/, '.145')})` : ''} in
                  the manual field below, or the custom-range box.
                </div>
              </div>
            )}

            {scanStatus === 'done' && discovered.length === 0 && (
              <div className="wiz-scan-empty">No cameras found — enter IP manually below</div>
            )}
          </div>

          <div className="wiz-divider"><span>or enter IP manually</span></div>

          <div className="wiz-ip-row">
            <input className="adm-input probe-ip-input" type="text"
              placeholder="192.168.1.64" value={ip}
              onChange={e => setIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && ip.trim() && setStep('creds')} />
            <button className="probe-paste-btn" onClick={async () => {
              try { const t = await navigator.clipboard.readText(); setIp(t.trim()); } catch {}
            }}>Paste</button>
            <button className="adm-btn primary" disabled={!ip.trim()}
              onClick={() => setStep('creds')}>Next →</button>
          </div>
          {ip.trim() && existingIps.includes(ip.trim()) && (
            <div className="wiz-dup-warn">
              ⚠ A camera at <strong>{ip.trim()}</strong> is already configured —
              adding another may exceed the camera's connection limit.
            </div>
          )}
        </div>
      )}

      {/* ── Step 2b: NVR Setup ── */}
      {step === 'nvr_ip' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn" onClick={() => setStep('source')}>← Back</button>
            <div className="wiz-card-title">NVR / DVR Setup</div>
          </div>
          <div className="wiz-field-group">
            <div className="adm-field">
              <label>NVR IP Address</label>
              <div className="wiz-ip-row">
                <input className="adm-input probe-ip-input" type="text"
                  placeholder="192.168.1.100" value={ip}
                  onChange={e => setIp(e.target.value)} />
                <button className="probe-paste-btn" onClick={async () => {
                  try { const t = await navigator.clipboard.readText(); setIp(t.trim()); } catch {}
                }}>Paste</button>
              </div>
            </div>
            <div className="adm-field" style={{ maxWidth: 140 }}>
              <label>Channel Number</label>
              <input className="adm-input" type="number" min="1" max="64"
                value={nvrCh} onChange={e => setNvrCh(e.target.value)} />
            </div>
          </div>
          <button className="adm-btn primary" disabled={!ip.trim()}
            onClick={() => setStep('creds')} style={{ marginTop: 20 }}>
            Next →
          </button>
        </div>
      )}

      {/* ── Step 3: Credentials ── */}
      {step === 'creds' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn"
              onClick={() => setStep(source === 'nvr' ? 'nvr_ip' : 'discover')}>← Back</button>
            <div className="wiz-card-title">Login Credentials</div>
          </div>

          <div className="wiz-ip-display" style={{ marginBottom: existingIps.includes(ip.trim()) ? 8 : 18 }}>
            <span className="wiz-ip-label">IP</span>
            <span className="wiz-ip-val">{ip}</span>
            {source === 'nvr' && (
              <><span className="wiz-ip-label" style={{ marginLeft: 14 }}>Ch</span>
              <span className="wiz-ip-val">{nvrCh}</span></>
            )}
          </div>
          {existingIps.includes(ip.trim()) && (
            <div className="wiz-dup-warn" style={{ marginBottom: 14 }}>
              ⚠ A camera at <strong>{ip}</strong> is already configured —
              adding another may exceed the camera's connection limit.
            </div>
          )}

          <label className="adm-checkbox-row" style={{ marginBottom: 14 }}>
            <input type="checkbox" checked={noAuth}
              onChange={e => { setNoAuth(e.target.checked); if (e.target.checked) { setUser(''); setPass_(''); } }} />
            <span>No credentials needed (camera has no login)</span>
          </label>

          {!noAuth && (
            <div className="probe-creds-row">
              <div className="adm-field" style={{ flex: 1 }}>
                <label>Username</label>
                <input className="adm-input" type="text" placeholder="admin"
                  value={user} onChange={e => setUser(e.target.value)} autoFocus />
              </div>
              <div className="adm-field" style={{ flex: 1 }}>
                <label>Password</label>
                <div className="probe-pass-wrap">
                  <input className="adm-input" type={showPass ? 'text' : 'password'}
                    placeholder="••••••" value={pass_}
                    onChange={e => setPass_(e.target.value)} />
                  <button className="probe-eye-btn" onClick={() => setShowPass(s => !s)}>
                    {showPass ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="wiz-actions">
            <button className="adm-btn primary" onClick={() => setStep('cam_type')}>
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Camera Brand ── */}
      {step === 'cam_type' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn" onClick={() => setStep('creds')}>← Back</button>
            <div className="wiz-card-title">Camera Brand</div>
          </div>

          <div className="wiz-ip-display" style={{ marginBottom: 18 }}>
            <span className="wiz-ip-label">IP</span>
            <span className="wiz-ip-val">{ip}</span>
            {source === 'nvr' && (
              <><span className="wiz-ip-label" style={{ marginLeft: 14 }}>Ch</span>
              <span className="wiz-ip-val">{nvrCh}</span></>
            )}
            {(user || pass_) && (
              <><span className="wiz-ip-label" style={{ marginLeft: 14 }}>User</span>
              <span className="wiz-ip-val">{user || '—'}</span></>
            )}
          </div>

          <div className="adm-field">
            <label>Camera Brand <span style={{ color: 'var(--danger, #e53)', fontWeight: 400, fontSize: 12 }}>*</span></label>
            <div className="probe-chips" style={{ marginTop: 8 }}>
              {Object.keys(camTypes).filter(k => k !== 'auto').map(k => (
                <button key={k}
                  className={`probe-chip${camType === k ? ' active' : ''}`}
                  onClick={() => setCamType(k)}>
                  {camTypes[k]?.label || k}
                </button>
              ))}
            </div>
            {camTypeErr && (
              <div style={{ color: 'var(--danger, #e53)', fontSize: 12, marginTop: 6 }}>
                Please select a camera brand before probing.
              </div>
            )}
          </div>

          <div className="wiz-brand-creds">
            <div className="wiz-brand-creds-label">
              Credentials to use
              {camType !== 'auto' && camTypes[camType] && (
                <button className="wiz-use-defaults-btn"
                  onClick={() => { setNoAuth(false); setUser(camTypes[camType].default_user || ''); setPass_(camTypes[camType].default_pass || ''); }}>
                  Reset to {camTypes[camType].label} defaults
                </button>
              )}
            </div>
            <label className="adm-checkbox-row" style={{ margin: '8px 0' }}>
              <input type="checkbox" checked={noAuth}
                onChange={e => { setNoAuth(e.target.checked); if (e.target.checked) { setUser(''); setPass_(''); } }} />
              <span>No credentials needed</span>
            </label>
            {!noAuth && (
              <div className="probe-creds-row" style={{ marginTop: 4 }}>
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>Username</label>
                  <input className="adm-input" type="text" placeholder="admin"
                    value={user} onChange={e => setUser(e.target.value)} />
                </div>
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>Password</label>
                  <div className="probe-pass-wrap">
                    <input className="adm-input" type={showPass ? 'text' : 'password'}
                      placeholder="••••••" value={pass_}
                      onChange={e => setPass_(e.target.value)} />
                    <button className="probe-eye-btn" onClick={() => setShowPass(s => !s)}>
                      {showPass ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="wiz-actions">
            <button className="adm-btn primary" disabled={!ip.trim()} onClick={startProbe}>
              Start Probe →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Live Probe ── */}
      {step === 'probe' && (
        <div className="wiz-card wiz-probe-card">
          <div className="probe-terminal-header" style={{ padding: '14px 20px 10px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button className="wiz-back-btn"
                onClick={() => { setProbeStatus(null); setProbeResult(null); setProbeLogs([]); setStep('cam_type'); }}>
                ← Back
              </button>
              <div>
                <div className="wiz-card-title">Probing {ip}</div>
                {source === 'nvr' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Channel {nvrCh}</div>
                )}
              </div>
            </div>
            <span className={`probe-status-badge ${probeStatus}`}>
              {probeStatus === 'running' ? '● Probing'
               : probeStatus === 'done'   ? '✓ Found'
               : probeStatus === 'failed' ? '✗ Not found'
               : probeStatus === 'error'  ? '✗ Error' : '…'}
            </span>
          </div>

          {probeStatus === 'running' && (
            <>
              <div className="probe-progress" style={{ margin: '0 20px 6px' }}>
                <div className="probe-progress-bar" />
              </div>
              <div style={{ padding: '0 20px 10px', textAlign: 'right' }}>
                <button className="adm-btn secondary" style={{ fontSize: 12, padding: '4px 16px' }}
                  onClick={() => { setProbeStatus('cancelled'); setStep('manual'); }}>
                  ✕ Cancel & edit URL
                </button>
              </div>
            </>
          )}

          <div className="probe-terminal" ref={logRef} style={{ maxHeight: 260 }}>
            {probeLogs.map(logLine)}
            {probeStatus === 'running' && <div className="probe-log-line probe-cursor">▌</div>}
          </div>

          {probeResult && probeStatus === 'done' && (
            <div className="wiz-probe-found">
              <div className="wiz-probe-found-label">Stream found</div>

              {/* Main stream — with test button */}
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                <div className="probe-result-url" style={{ flex:1, margin:0 }}>{probeResult.rtsp_url}</div>
                <RtspTestBtn url={probeResult.rtsp_url} />
              </div>

              <div className="probe-result-details" style={{ margin: '2px 0 4px' }}>
                {(() => {
                  let host = '';
                  try { const u = new URL(probeResult.rtsp_url); host = u.hostname + (u.port ? `:${u.port}` : ''); } catch {}
                  const m = probeResult.method || '';
                  const methodLbl = m === 'ONVIF' ? 'ONVIF'
                    : m.startsWith('template:')
                      ? `${camTypes[m.slice(9)]?.label || m.slice(9)} template`
                      : m;
                  const rows = [
                    host                && ['Address',  host],
                    probeResult.user    && ['Username', probeResult.user],
                    probeResult.password && ['Password', probeResult.password],
                    methodLbl           && ['Method',   methodLbl],
                  ].filter(Boolean);
                  return rows.map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: 8, padding: '2px 0', fontSize: 13 }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 78 }}>{k}</span>
                      <span style={{ fontFamily: k === 'Address' ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{v}</span>
                    </div>
                  ));
                })()}
              </div>

              {/* Sub stream */}
              <div className="adm-field" style={{ marginTop: 12 }}>
                <label style={{ fontSize: 12 }}>
                  Sub Stream URL <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(for NVR recording)</span>
                </label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input className="adm-input" type="text"
                    placeholder={deriveSubUrl(probeResult.rtsp_url)
                      ? 'auto-derived — edit if wrong'
                      : 'enter sub-stream URL manually (e.g. ch01sub.264)'}
                    value={manualRtspSub}
                    onChange={e => setManualRtspSub(e.target.value)}
                    style={{ fontFamily:'monospace', fontSize:12, flex:1 }} />
                  <RtspTestBtn url={manualRtspSub} />
                </div>
                {!manualRtspSub && !deriveSubUrl(probeResult.rtsp_url) && (
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                    Could not auto-derive sub URL from <code>{probeResult.rtsp_url}</code> — enter it manually or skip for now
                  </div>
                )}
              </div>

              <div className="wiz-actions">
                <button className="adm-btn secondary" onClick={() => setStep('cam_type')}>
                  ← Try different brand
                </button>
                <button className="adm-btn primary" onClick={() => setStep('name')}>
                  Name this camera →
                </button>
              </div>
            </div>
          )}

          {(probeStatus === 'failed' || probeStatus === 'error') && !probeResult && (() => {
            const hasTimeout = probeLogs.some(l => l.includes('timeout'));
            const hasAuth    = probeLogs.some(l => l.toLowerCase().includes('auth') || l.includes('401') || l.includes('403'));
            return (
              <div className="wiz-probe-fail">
                {hasTimeout && !hasAuth ? (
                  <div className="wiz-probe-fail-msg">
                    <strong>Connection timed out.</strong> Camera may be at its simultaneous
                    stream limit, or the IP is unreachable. If another stream from this camera
                    is already running, use the URL below directly.
                  </div>
                ) : hasAuth ? (
                  <div className="wiz-probe-fail-msg">
                    <strong>Authentication failed.</strong> Go back and check your username and password.
                  </div>
                ) : (
                  <div className="wiz-probe-fail-msg">Could not auto-detect the stream.</div>
                )}
                <div className="wiz-actions">
                  <button className="adm-btn secondary" onClick={() => setStep('cam_type')}>
                    ← Change credentials
                  </button>
                  <button className="adm-btn primary" onClick={() => {
                    if (!manualRtsp) {
                      const line = [...probeLogs].reverse().find(l => l.includes('→ rtsp://'));
                      if (line) { const m = line.match(/rtsp:\/\/\S+/); if (m) setManualRtsp(m[0]); }
                    }
                    setStep('manual');
                  }}>
                    Enter URL manually →
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Step 3b: Probe URL (RTSP source) ── */}
      {step === 'probe_url' && (
        <div className="wiz-card wiz-probe-card">
          <div className="probe-terminal-header" style={{ padding: '14px 20px 10px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button className="wiz-back-btn"
                onClick={() => { setProbeStatus(null); setProbeResult(null); setProbeLogs([]); setStep('manual'); }}>
                ← Back
              </button>
              <div className="wiz-card-title">Testing URL</div>
            </div>
            <span className={`probe-status-badge ${probeStatus}`}>
              {probeStatus === 'running' ? '● Testing'
               : probeStatus === 'done'  ? '✓ Reachable'
               : probeStatus === 'failed' || probeStatus === 'error' ? '✗ Failed' : '…'}
            </span>
          </div>

          {probeStatus === 'running' && (
            <>
              <div className="probe-progress" style={{ margin: '0 20px 6px' }}>
                <div className="probe-progress-bar" />
              </div>
              <div style={{ padding: '0 20px 10px', textAlign: 'right' }}>
                <button className="adm-btn secondary" style={{ fontSize: 12, padding: '4px 16px' }}
                  onClick={() => { setProbeStatus('cancelled'); setStep('manual'); }}>
                  ✕ Cancel & edit URL
                </button>
              </div>
            </>
          )}

          <div className="probe-terminal" ref={logRef} style={{ maxHeight: 200 }}>
            {probeLogs.map(logLine)}
            {probeStatus === 'running' && <div className="probe-log-line probe-cursor">▌</div>}
          </div>

          {probeResult && probeStatus === 'done' && (
            <div className="wiz-probe-found">
              <div className="wiz-probe-found-label">Stream reachable</div>
              <div className="probe-result-url">{probeResult.rtsp_url}</div>
              <div className="wiz-actions">
                <button className="adm-btn primary" onClick={() => setStep('name')}>
                  Name this camera →
                </button>
              </div>
            </div>
          )}

          {(probeStatus === 'failed' || probeStatus === 'error') && !probeResult && (
            <div className="wiz-probe-fail">
              <div className="wiz-probe-fail-msg">Could not connect to this URL. Check the address and credentials.</div>
              <div className="wiz-actions">
                <button className="adm-btn secondary" onClick={() => setStep('manual')}>
                  ← Edit URL
                </button>
                <button className="adm-btn primary" onClick={() => setStep('name')}>
                  Save anyway →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 4b: Manual RTSP ── */}
      {step === 'manual' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn" onClick={() => setStep(source === 'rtsp' ? 'source' : 'probe')}>← Back</button>
            <div className="wiz-card-title">Enter Stream URL</div>
          </div>
          {ip && (
            <div className="wiz-ip-display" style={{ marginBottom: 16 }}>
              <span className="wiz-ip-label">IP</span>
              <span className="wiz-ip-val">{ip}</span>
            </div>
          )}
          <div className="adm-field">
            <label>Main Stream URL</label>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input className="adm-input" type="text"
                placeholder="rtsp://admin:password@192.168.1.64:554/stream1"
                value={manualRtsp} onChange={e => setManualRtsp(e.target.value)}
                style={{ fontFamily:'monospace', fontSize:12, flex:1 }} />
              <RtspTestBtn url={manualRtsp} transport={rtspTransport} />
            </div>
          </div>
          <div className="adm-field">
            <label>Sub Stream URL <span style={{ color:'var(--text-muted)', fontWeight:400 }}>(optional — used for NVR recording)</span></label>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input className="adm-input" type="text"
                placeholder="rtsp://admin:password@192.168.1.64:554/stream2"
                value={manualRtspSub} onChange={e => setManualRtspSub(e.target.value)}
                style={{ fontFamily:'monospace', fontSize:12, flex:1 }} />
              <RtspTestBtn url={manualRtspSub} transport={rtspTransport} />
            </div>
          </div>
          <div className="adm-field">
            <label>RTSP Transport</label>
            <select className="adm-input" value={rtspTransport} onChange={e => setRtspTransport(e.target.value)}>
              <option value="tcp">TCP (default — most cameras)</option>
              <option value="udp">UDP (try this if TCP fails with "Nonmatching transport")</option>
            </select>
          </div>
          <button className="adm-btn primary" disabled={!manualRtsp.trim()}
            onClick={startUrlProbe} style={{ marginTop: 16 }}>
            Test & Continue →
          </button>
        </div>
      )}

      {/* ── Step 5: Name + Zone ── */}
      {step === 'name' && (() => {
        const streamUrl = probeResult?.rtsp_url || manualRtsp;
        const shortUrl  = streamUrl.length > 44 ? streamUrl.slice(0, 44) + '…' : streamUrl;
        return (
          <div className="wiz-card">
            <div className="wiz-card-hdr">
              <button className="wiz-back-btn"
                onClick={() => setStep(source === 'rtsp' ? 'probe_url' : probeResult ? 'probe' : 'manual')}>← Back</button>
              <div className="wiz-card-title">Name this Camera</div>
            </div>
            <div className="wiz-ip-display" style={{ marginBottom: 18, flexWrap: 'wrap', gap: 6 }}>
              {ip && <><span className="wiz-ip-label">IP</span><span className="wiz-ip-val">{ip}</span></>}
              <span className="wiz-ip-label" style={{ marginLeft: ip ? 10 : 0 }}>Stream</span>
              <span className="wiz-ip-val" style={{ fontSize: 11 }}>{shortUrl}</span>
            </div>
            <div className="adm-field">
              <label>Camera Name</label>
              <input className="adm-input" type="text"
                placeholder="e.g. Front Door, Parking Lot"
                value={camName} onChange={e => setCamName(e.target.value)} autoFocus />
            </div>
            <div className="adm-field" style={{ marginTop: 14 }}>
              <label>Zone (optional)</label>
              <input className="adm-input" type="text"
                placeholder="e.g. entrance, office"
                value={zone} onChange={e => setZone(e.target.value)} />
            </div>
            {recOn() && (
            <div className="adm-field" style={{ marginTop: 14 }}>
              <label>Recording Quality</label>
              <select className="adm-input" value={recStream}
                onChange={e => setRecStream(e.target.value)}>
                <option value="sub">Sub-stream · SD (low disk) — default</option>
                <option value="main">Main-stream · HD (≈10× disk)</option>
              </select>
              {recStream === 'main' && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Main-stream recording uses ~10× more disk.
                </div>
              )}
            </div>
            )}
            <div className="adm-field" style={{ marginTop: 14 }}>
              <label>Live Stream Quality</label>
              <select className="adm-input" value={liveStream}
                onChange={e => setLiveStream(e.target.value)}>
                <option value="">{recOn() ? 'Same as recording — default' : 'Sub-stream · SD — default'}</option>
                <option value="sub">Sub-stream · SD</option>
                <option value="main">Main-stream · HD</option>
              </select>
              {recOn() && liveStream && liveStream !== recStream && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Different from Recording Quality — this opens a second, independent
                  connection to the camera just for live viewing. Leave it on "Same as
                  recording" unless you specifically need them to differ.
                </div>
              )}
            </div>
            {recOn() && (
            <div className="adm-field" style={{ marginTop: 14 }}>
              <label className="adm-checkbox-row">
                <input type="checkbox" checked={recAudio} onChange={e => setRecAudio(e.target.checked)} />
                <span>Record audio</span>
              </label>
              {recAudio && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Enable audio for this stream on the camera itself (Camera → Audio →
                  {recStream === 'main' ? ' Main' : ' Sub'} Stream → Enable) and check Microphone
                  Volume is not 0 — otherwise the recording is silent. Audio recording may
                  require on-site notice/consent depending on your region.
                </div>
              )}
            </div>
            )}
            <button className="adm-btn primary" disabled={!camName.trim()}
              onClick={() => setStep('confirm')} style={{ marginTop: 20 }}>
              Review & Save →
            </button>
          </div>
        );
      })()}

      {/* ── Step 6: Confirm & Save ── */}
      {step === 'confirm' && (
        <div className="wiz-card">
          <div className="wiz-card-hdr">
            <button className="wiz-back-btn" onClick={() => setStep('name')}>← Back</button>
            <div className="wiz-card-title">Review & Save</div>
          </div>

          <div className="wiz-summary">
            {[
              ['Name',    camName],
              zone ? ['Zone', zone] : null,
              ['IP',      ip],
              source === 'nvr' ? ['Channel', nvrCh] : null,
              ['Brand',   camTypes[camType]?.label || camType],
              ['Stream',  probeResult?.rtsp_url || manualRtsp],
              rtspTransport === 'udp' ? ['Transport', 'UDP'] : null,
              recOn() ? ['Recording', `${recStream === 'main' ? 'Main · HD' : 'Sub · SD'}${recAudio ? ' + audio' : ''}`] : null,
              liveStream ? ['Live view', liveStream === 'main' ? 'Main · HD' : 'Sub · SD'] : null,
              (probeResult?.user || user) ? ['Username', probeResult?.user || user] : null,
            ].filter(Boolean).map(([k, v]) => (
              <div className="wiz-summary-row" key={k}>
                <span className="wiz-summary-key">{k}</span>
                <span className={`wiz-summary-val${k === 'Stream' || k === 'IP' ? ' wiz-mono' : ''}`}>{v}</span>
              </div>
            ))}
          </div>

          <label className="wiz-add-disabled" style={{ display: 'flex', alignItems: 'flex-start',
            gap: 8, margin: '14px 0 0', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={!addEnabled}
              onChange={e => setAddEnabled(!e.target.checked)} style={{ marginTop: 2 }} />
            <span>
              Add disabled
              <span style={{ display: 'block', opacity: .65, fontSize: 12 }}>
                Save the config but don't connect or alert yet — enable it later from the camera list.
              </span>
            </span>
          </label>

          {saveErr && <div className="adm-msg error" style={{ marginTop: 14 }}>{saveErr}</div>}

          <div className="wiz-actions">
            <button className="adm-btn primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : (addEnabled ? 'Save Camera' : 'Save Disabled')}
            </button>
          </div>
        </div>
      )}

      <BuildInfoBar />
    </div>
  );
}

const CAM_TYPE_LABELS = {
  cpplus_small: 'CP Plus (Small)', cpplus: 'CP Plus (NVR)',
  dahua: 'Dahua', hikvision: 'Hikvision', endroid: 'Endroid', auto: 'Auto',
};

// ═════════════════════════════════════════════════════════════════════════════
// Ignore Zones — per-camera areas where nothing raises an alert.
// Stored in cam_config.exclusion_zones as [{polygon:[[x,y],…]}] in 0-1 frame
// coords; UnknownPersonDetector drops any detection whose centre falls inside
// one before any alert / clip / siren logic runs. Same store the industrial
// Object Intelligence page writes, so a site can be configured from either.
// ═════════════════════════════════════════════════════════════════════════════

// Normalise saved zones to a list of polygons, converting legacy rectangle
// zones ({x1,y1,x2,y2}) to a 4-corner polygon.
function normalizeExclusionZones(camCfg, key = 'exclusion_zones') {
  return (camCfg?.[key] || []).map(z => {
    if (Array.isArray(z?.polygon) && z.polygon.length >= 3) return z.polygon;
    if (z && z.x1 != null) return [[z.x1, z.y1], [z.x2, z.y1], [z.x2, z.y2], [z.x1, z.y2]];
    return null;
  }).filter(Boolean);
}

// Same rule as the box (parvekshak_api._zone_problem): ≥3 separate corners and a
// real area. A shape that fails it can never contain anyone (PRVSK-108 #14).
const MIN_ZONE_AREA = 0.0003;
const MIN_ZONE_THICKNESS = 0.005;
function zoneProblem(poly) {
  if (!Array.isArray(poly)) return null;
  const distinct = new Set(poly.map(p => `${(+p[0]).toFixed(3)},${(+p[1]).toFixed(3)}`));
  if (distinct.size < 3) return 'needs at least 3 separate corners';
  let a = 0, longest = 0;
  poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    a += p[0] * q[1] - q[0] * p[1];
    longest = Math.max(longest, Math.hypot(q[0] - p[0], q[1] - p[1]));
  });
  const area = Math.abs(a) / 2;
  if (area < MIN_ZONE_AREA || area / (longest || 1) < MIN_ZONE_THICKNESS) return 'is too thin or too small to cover anything';
  return null;
}

// kind="gate": the same editor draws a Doorbell's gate area (PRVSK-139) — the
// part of the view where a person rings the doorbell. Stored in
// cam_config.doorbell_zones; green instead of grey.
function IgnoreZonesTab({ idx, camName, zones, setZones, pts, setPts, onDirty, maximized, kind = 'ignore' }) {
  const gate = kind === 'gate';
  const zc = gate ? { stroke: '#22c55e', fill: 'rgba(34,197,94,0.25)', icon: '🔔', noun: 'gate area' }
                  : { stroke: '#94a3b8', fill: 'rgba(148,163,184,0.30)', icon: '🚫', noun: 'zone' };
  const [shapeErr, setShapeErr] = useState(null);
  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false);

  const loadFrame = useCallback(() => {
    setImgErr(false);
    apiImageUrl(`/cameras/${idx}/frame`).then(setImgUrl).catch(() => setImgErr(true));
  }, [idx]);
  useEffect(() => { loadFrame(); }, [loadFrame]);

  // redraw — saved zones in grey, the polygon being drawn in amber
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || !im.complete) return;
    const W = cv.width, H = cv.height, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const dot = (p, color) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(p[0] * W, p[1] * H, 5, 0, 7); ctx.fill(); };
    zones.forEach((poly, i) => {
      if (!poly || poly.length < 3) return;
      ctx.strokeStyle = zc.stroke; ctx.fillStyle = zc.fill; ctx.lineWidth = 2;
      ctx.beginPath(); poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      poly.forEach(p => dot(p, zc.stroke));
      ctx.fillStyle = '#e2e8f0'; ctx.font = 'bold 12px sans-serif';
      ctx.fillText(`${zc.icon} ${i + 1}`, poly[0][0] * W + 4, poly[0][1] * H - 4);
    });
    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2;
      ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H)); ctx.stroke();
      pts.forEach(p => dot(p, '#f59e0b'));
    }
  }, [imgUrl, zones, pts, zc.stroke, zc.fill, zc.icon]);

  const onCanvasClick = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const pt = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    // Functional update: quick taps land before the next render, and a `pts`
    // captured at render made each one overwrite the last (PRVSK-108 #14).
    setPts(p => [...p, pt]);
    setShapeErr(null);
  };
  const finishZone = () => {
    if (pts.length < 3) return;
    const why = zoneProblem(pts);
    if (why) { setShapeErr(`This area ${why} — add more corners, or Cancel and draw it again.`); return; }
    setZones([...zones, pts]); setPts([]); onDirty();
  };
  const removeZone = (i) => { setZones(zones.filter((_, j) => j !== i)); onDirty(); };

  return (
    <div>
      {gate ? (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Draw where someone stands at the door or gate of <strong style={{ color: 'var(--text)' }}>{camName}</strong>.
          With a gate area, the <strong>🔔 Doorbell</strong> rings only for a person whose feet are inside it — people on
          the road or elsewhere in the view don’t ring it, but still raise the camera’s normal alerts.
          No gate area = the whole view rings the doorbell, as before.
        </p>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          Draw the parts of <strong style={{ color: 'var(--text)' }}>{camName}</strong> where you do <strong>not</strong> want
          alerts — the road outside the gate, a neighbour’s balcony, a tree that sways at night.
          A person whose centre falls inside an ignore zone is dropped before any alert, clip or
          siren. The rest of the frame keeps alerting normally.
        </p>
      )}

      {/* The 640px cap is what keeps the frame readable in the normal dialog; lifting
          it when maximised is the whole point of maximising, since the accuracy of a
          hand-drawn boundary is set by how many screen pixels the frame occupies.
          Clicks normalise against getBoundingClientRect, so the stored polygon is
          identical at either size — only the precision of the placement changes. */}
      <div style={{ position: 'relative', width: '100%', maxWidth: maximized ? '100%' : 640, minHeight: 180, borderRadius: 8,
        overflow: 'hidden', border: '1px solid var(--border)', background: '#000', display: 'grid', placeItems: 'center' }}>
        {imgUrl && !imgErr ? (
          <img ref={imgRef} src={imgUrl} alt="" onError={() => setImgErr(true)}
            onLoad={() => {
              const cv = canvasRef.current, im = imgRef.current;
              if (cv && im) { cv.width = im.clientWidth; cv.height = im.clientHeight; setPts(p => [...p]); }
            }}
            style={{ width: '100%', display: 'block' }} />
        ) : (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
            {imgErr
              ? <>📷 Couldn’t load a frame from <b>{camName}</b> — the camera may be offline. Try ↻ Refresh frame.</>
              : <>📷 Loading a still frame to draw on…</>}
          </div>
        )}
        {imgUrl && !imgErr && (
          <canvas ref={canvasRef} onClick={onCanvasClick}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'crosshair' }} />
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: '1 1 180px' }}>
          Click the corners of an area, then <strong>Finish {zc.noun}</strong>. {zones.length} {zc.noun}(s) set.
        </span>
        {pts.length > 0 && (
          <button className="adm-btn primary" onClick={finishZone} disabled={pts.length < 3}>
            Finish {zc.noun} ({pts.length})
          </button>
        )}
        {pts.length > 0 && <button className="adm-btn secondary" onClick={() => { setPts([]); setShapeErr(null); }}>Cancel</button>}
        <button className="adm-btn secondary" onClick={loadFrame}>↻ Refresh frame</button>
      </div>
      {shapeErr && <div style={{ fontSize: 12, color: 'var(--danger, #ef4444)', marginTop: 6 }}>{shapeErr}</div>}

      {zones.length > 0 && (
        <div className="adm-field" style={{ marginTop: 14 }}>
          <label>{gate ? 'Gate areas' : 'Ignore zones'}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {zones.map((z, i) => (
              <span key={i} className="cam-action-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                title={zoneProblem(z) ? `This area ${zoneProblem(z)} — delete it and draw it again` : undefined}>
                {zoneProblem(z) ? '⚠️' : zc.icon} {gate ? 'Gate area' : 'Zone'} {i + 1} ({z.length} pts)
                <button onClick={() => removeZone(i)} title="Delete this zone"
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>✕</button>
              </span>
            ))}
          </div>
        </div>
      )}

      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>
        Zones are saved with <strong>Save</strong> below — the camera then reloads and applies them live.
        If you’re still drawing one when you press Save, you’ll be asked whether to keep it.
      </p>
    </div>
  );
}

// Exported for Guided Setup's "Edit camera" on each camera card.
export function EditCameraModal({ cam, idx, onClose, onSaved, globalRecOn = true, initialTab = 'basic' }) {
  // initialTab: Guided Setup's "Draw gate area" opens straight on 🔔 Gate Area.
  const [activeTab, setActiveTab] = useState(initialTab);
  // The modal opens on Basic, so an unoptimised camera would go unnoticed; the
  // stream card stays mounted (hidden) to report this and the tab shows a dot.
  const [streamNeeds, setStreamNeeds] = useState(false);
  const onStreamState = useCallback((_, st) => setStreamNeeds(!!st.needs), []);

  // Basic
  const [name,    setName]    = useState(cam.name    || '');
  const [ip,      setIp]      = useState(cam.ip      || '');
  const [mac,     setMac]     = useState(cam.mac     || '');
  const [type,    setType]    = useState(cam.type    || 'auto');
  const [user,    setUser]    = useState(cam.user    || '');
  const [pass_,   setPass_]   = useState('');
  // A camera saved without a login (no password on it) shows "Not needed" rather
  // than two empty boxes that look like something was forgotten.
  // A camera added as a full URL keeps its login inside it (rtsp://user:pass@…) with
  // the two fields blank — that one has a password too.
  const [showLogin, setShowLogin] = useState(
    !!(cam.user || cam.password || /^rtsps?:\/\/[^/@]+@/i.test(cam.rtsp_url || '')));
  const [zone,    setZone]    = useState(cam.zone    || '');
  const [enabled,    setEnabled]    = useState(cam.enabled !== false);
  const [detectMode, setDetectMode] = useState(cam.detect_mode || 'full');
  const [isWifi,     setIsWifi]     = useState(cam.wifi === true);
  const [recEnabled, setRecEnabled] = useState(cam.nvr !== false);
  const [actions,    setActions]    = useState(cam.alert_actions || {});
  const [showPass,   setShowPass]   = useState(false);
  const [rtspMain,   setRtspMain]   = useState(cam.rtsp_url     || '');
  const [rtspSub,    setRtspSub]    = useState(cam.rtsp_url_sub || deriveSubUrl(cam.rtsp_url) || '');
  const [rtspTransport, setRtspTransport] = useState(cam.rtsp_transport || 'tcp');
  const [recStream,  setRecStream]  = useState(cam.record_stream || 'sub');
  const [liveStream, setLiveStream] = useState(cam.live_stream || '');   // '' = same as recording
  const [recAudio,   setRecAudio]   = useState(!!cam.record_audio);

  // Alert Schedule
  const [schedMode,  setSchedMode]  = useState(cam.alert_time_mode || 'INHERIT');
  const [schedStart, setSchedStart] = useState(cam.alert_time?.start || '00:30');
  const [schedEnd,   setSchedEnd]   = useState(cam.alert_time?.end   || '05:00');

  // Recording Schedule — when enabled, this camera only records inside the window
  const [recSchedEnabled, setRecSchedEnabled] = useState(cam.record_schedule_enabled === true);
  const [recSchedStart,   setRecSchedStart]   = useState(cam.record_schedule?.start || '00:00');
  const [recSchedEnd,     setRecSchedEnd]     = useState(cam.record_schedule?.end   || '23:59');

  // Ignore Zones — areas of this camera's frame where nothing alerts
  // (cam_config.exclusion_zones). Held here, not in the tab, so a half-drawn
  // polygon survives switching tabs.
  const [ignZones, setIgnZones] = useState(() => normalizeExclusionZones(cam.cam_config));
  const [ignPts,   setIgnPts]   = useState([]);
  const [ignDirty, setIgnDirty] = useState(false);
  // Doorbell gate area (PRVSK-139) — same editor, cam_config.doorbell_zones.
  const [gateZones, setGateZones] = useState(() => normalizeExclusionZones(cam.cam_config, 'doorbell_zones'));
  const [gatePts,   setGatePts]   = useState([]);
  const [gateDirty, setGateDirty] = useState(false);

  // Expand the dialog to the viewport. Opens maximised on the Ignore Zones tab,
  // since that is the only tab where the dialog size changes how accurate the
  // result is; the rest are forms and read fine at the default width.
  const [maximized, setMaximized] = useState(false);

  // Detection tuning lives on the Detection Settings page (per-camera dropdown),
  // not here — Edit Camera covers identity, connection, schedule, ignore zones,
  // and actions only.

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Pull IP + MAC from the live network (fills blank recovery fields)
  const [netBusy, setNetBusy] = useState(false);
  const [netMsg,  setNetMsg]  = useState(null);   // { ok, text }

  // Stream tab: ONVIF port for date/time + overlay (stream settings: CameraStreamSettings)
  const [onvifPort, setOnvifPort] = useState(cam.onvif_port || 80);

  // Stream (ONVIF date/time + on-screen name overlay)
  const [dtInfo,  setDtInfo]  = useState(null);   // { ok, mode, tz, dst, utc, local, error }
  const [osdName, setOsdName] = useState('');
  const [osdOk,   setOsdOk]   = useState(null);   // null=unknown, true/false after read
  const [devBusy, setDevBusy] = useState(false);
  const [devMsg,  setDevMsg]  = useState(null);

  const loadDevice = async () => {
    setDevBusy(true); setDevMsg(null);
    try {
      const [dtRes, osdRes] = await Promise.all([
        apiFetch(`/cameras/${idx}/datetime`), apiFetch(`/cameras/${idx}/osd`),
      ]);
      const dt = await dtRes.json(); setDtInfo(dt);
      const osd = await osdRes.json();
      setOsdOk(!!osd.ok);
      if (osd.ok && osd.name != null) setOsdName(String(osd.name));
    } catch { setDtInfo({ ok: false, error: 'Cannot reach server' }); }
    finally { setDevBusy(false); }
  };
  const syncTime = async () => {
    setDevBusy(true); setDevMsg(null);
    try {
      const res  = await apiFetch(`/cameras/${idx}/datetime/sync`, { method: 'POST' });
      const data = await res.json();
      setDevMsg({ ok: !!data.ok, text: data.detail || (data.ok ? 'Synced' : 'Failed') });
      if (data.ok) loadDevice();
    } catch { setDevMsg({ ok: false, text: 'Cannot reach server' }); }
    finally { setDevBusy(false); }
  };
  const applyOsd = async () => {
    if (!osdName.trim()) { setDevMsg({ ok: false, text: 'Enter a name' }); return; }
    setDevBusy(true); setDevMsg(null);
    try {
      const res  = await apiFetch(`/cameras/${idx}/osd`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: osdName.trim() }),
      });
      const data = await res.json();
      setDevMsg({ ok: !!data.ok, text: data.detail || (data.ok ? 'Applied' : 'Failed') });
    } catch { setDevMsg({ ok: false, text: 'Cannot reach server' }); }
    finally { setDevBusy(false); }
  };

  const pullNet = async () => {
    setNetBusy(true); setNetMsg(null);
    try {
      const res  = await apiFetch(`/cameras/${idx}/capture-net`, { method: 'POST' });
      const data = await res.json();
      if (data.ip)  setIp(data.ip);
      if (data.mac) setMac(data.mac);
      if (data.ok) {
        const bits = Object.keys(data.updated || {});
        const via  = data.via === 'onvif' ? ' (via ONVIF)' : '';
        setNetMsg({ ok: true, text: (bits.length ? `Saved ${bits.join(' + ')} from camera` : 'Already up to date') + via });
      } else {
        setNetMsg({ ok: false, text: data.error || 'Could not reach the camera' });
      }
    } catch {
      setNetMsg({ ok: false, text: 'Cannot reach server' });
    } finally { setNetBusy(false); }
  };

  useEffect(() => {
    if (activeTab === 'stream' && dtInfo === null) loadDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Save with an area still being drawn: ask, never decide silently (PRVSK-108 #13).
  // It used to be committed automatically — from any tab, unseen — so a few
  // stray corners became an ignore area and switched alerts off there.
  const [askUnfinished, setAskUnfinished] = useState(false);
  const save = async (unfinished) => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (cam.rtsp_url && !rtspMain.trim()) { setError('Main stream URL cannot be cleared'); return; }
    if ((ignPts.length > 0 || gatePts.length > 0) && !unfinished) { setAskUnfinished(true); return; }
    setAskUnfinished(false);
    const keep = unfinished === 'keep';
    setLoading(true); setError(null);
    // "keep" applies to whichever area is being drawn — never add an empty one.
    const keepIgn = keep && ignPts.length >= 3 && !zoneProblem(ignPts);
    const effZones = keepIgn ? [...ignZones, ignPts] : ignZones;
    const zonesChanged = ignDirty || keepIgn;
    // Fold the answer into the editor too, so a failed save doesn't lose it.
    if (keepIgn) { setIgnZones(effZones); setIgnDirty(true); }
    if (unfinished) setIgnPts([]);
    const keepGate = keep && gatePts.length >= 3 && !zoneProblem(gatePts);
    const effGate = keepGate ? [...gateZones, gatePts] : gateZones;
    const gateChanged = gateDirty || keepGate;
    if (keepGate) { setGateZones(effGate); setGateDirty(true); }
    if (unfinished) setGatePts([]);
    const body = {
      name: name.trim(), ip: ip.trim(), mac: mac.trim(), type, user: user.trim(), zone: zone.trim(), enabled, detect_mode: detectMode, wifi: isWifi,
      alert_actions: actions,
      ...(rtspMain.trim() && { rtsp_url:     rtspMain.trim() }),
      ...(rtspSub.trim()  && { rtsp_url_sub: rtspSub.trim()  }),
      rtsp_transport: rtspTransport,
      record_stream: recStream,
      // '' explicitly resets to "same as recording" — the backend treats an
      // empty string (not sent-at-all, which exclude_none would just ignore)
      // as clearing any previous override.
      live_stream:   liveStream,
      record_audio:  recAudio,
      onvif_port: parseInt(onvifPort, 10) || 80,
      alert_time_mode: schedMode,
      alert_time: { start: schedStart, end: schedEnd },
      nvr: recEnabled,
      record_schedule_enabled: recSchedEnabled,
      record_schedule: { start: recSchedStart, end: recSchedEnd },
      // Only sent when touched — the backend shallow-merges cam_config, so an
      // untouched camera keeps whatever zones it already has.
      ...((zonesChanged || gateChanged) && { cam_config: {
        ...(zonesChanged && { exclusion_zones: effZones.map(p => ({ polygon: p })) }),
        ...(gateChanged && { doorbell_zones: effGate.map(p => ({ polygon: p })) }),
      } }),
    };
    if (pass_.trim()) body.password = pass_.trim();
    try {
      const res  = await apiFetch(`/cameras/${idx}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to save'); return; }
      onSaved(data.camera);
    } catch { setError('Cannot reach server'); }
    finally { setLoading(false); }
  };

  const toggleAction = k => setActions(a => ({ ...a, [k]: !a[k] }));

  const CAM_TABS = [
    { key: 'basic',      icon: '📷', label: 'Basic'      },
    { key: 'stream',     icon: '🎞️', label: 'Stream'     },
    { key: 'schedule',   icon: '📅', label: 'Schedule'   },
    { key: 'ignore',     icon: '🚫', label: 'Ignore Zones' },
    // Only for a Doorbell camera — where a person must stand to ring it (PRVSK-139).
    ...(actions.doorbell ? [{ key: 'gate', icon: '🔔', label: 'Gate Area' }] : []),
    { key: 'detectors',  icon: '🧠', label: 'Detectors'  },
  ];

  return (
    <div className={`adm-overlay${maximized ? ' adm-overlay-max' : ''}`} onClick={onClose}>
      <div className={`adm-modal adm-modal-lg${maximized ? ' adm-modal-max' : ''}`}
           onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>Edit Camera</h3>
          {/* Ignore zones are drawn by clicking points on a still frame, and at the
              default 440px the whole garden is a few hundred pixels wide — a boundary
              placed by eye there lands a couple of percent of frame height off, which
              is the difference between a zone that catches the object and one that
              misses it. Maximise gives the canvas the viewport. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button className="adm-modal-close adm-modal-max-btn"
                    title={maximized ? 'Restore' : 'Maximize'}
                    aria-label={maximized ? 'Restore dialog' : 'Maximize dialog'}
                    onClick={() => setMaximized(m => !m)}>{maximized ? '⤡' : '⤢'}</button>
            <button className="adm-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="adm-modal-body">
          <div className="adm-tab-rail">
            {CAM_TABS.map(t => (
              <button key={t.key}
                className={`adm-icon-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { setActiveTab(t.key); if (t.key === 'ignore') setMaximized(true); }}>
                <span className="adm-icon-btn-icon">{t.icon}</span>
                <span className="adm-icon-btn-label">
                  {t.label}
                  {t.key === 'stream' && streamNeeds && (
                    <span className="adm-tab-dot" title="This camera can be optimised for Parvekshak"
                          aria-label="can be optimised" />
                  )}
                </span>
              </button>
            ))}
          </div>

        <div className="adm-modal-form">
          {activeTab === 'basic' && <>
            <div className="adm-dev-form-row">
              <div className="adm-field" style={{ flex: 1 }}>
                <label>Name</label>
                <input className="adm-input" type="text" value={name}
                  onChange={e => setName(e.target.value)} autoFocus />
              </div>
              <div className="adm-field" style={{ flex: '0 0 140px' }}>
                <label>Zone</label>
                <input className="adm-input" type="text" placeholder="entrance…"
                  value={zone} onChange={e => setZone(e.target.value)} />
              </div>
            </div>
            <div className="adm-dev-form-row">
              <div className="adm-field" style={{ flex: 1 }}>
                <label>IP Address</label>
                <input className="adm-input" type="text" placeholder="192.168.1.x"
                  value={ip} onChange={e => setIp(e.target.value)} />
              </div>
              <div className="adm-field" style={{ flex: 1 }}>
                <label>MAC Address <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(recovers camera after IP change)</span></label>
                <input className="adm-input" type="text" placeholder="AA:BB:CC:DD:EE:FF"
                  style={{ fontFamily: 'monospace' }}
                  value={mac} onChange={e => setMac(e.target.value)} />
              </div>
            </div>
            <div className="adm-field" style={{ marginTop: -2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="adm-btn" onClick={pullNet} disabled={netBusy}
                  style={{ padding: '5px 12px', fontSize: 12 }}>
                  {netBusy ? 'Pulling…' : '⟳ Pull IP + MAC from camera'}
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Fills blank fields from the live network — camera must be online &amp; on this subnet
                </span>
              </div>
              {netMsg && (
                <div style={{ fontSize: 12, marginTop: 4,
                  color: netMsg.ok ? 'var(--success, #2e7d32)' : 'var(--danger, #c62828)' }}>
                  {netMsg.text}
                </div>
              )}
            </div>
            <div className="adm-field">
              <label>Brand / Type</label>
              <select className="adm-input" value={type} onChange={e => setType(e.target.value)}>
                {Object.entries(CAM_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            {showLogin ? (<>
            <div className="adm-field">
              <label>Username</label>
              <input className="adm-input" type="text" value={user}
                onChange={e => setUser(e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Password</label>
              <div className="probe-pass-wrap">
                <input className="adm-input" type={showPass ? 'text' : 'password'}
                  placeholder={cam.user || cam.password ? 'leave blank to keep current'
                    : /^rtsps?:\/\/[^/@]+@/i.test(cam.rtsp_url || '') ? 'set in the stream URL below' : ''}
                  value={pass_} onChange={e => setPass_(e.target.value)} />
                <button className="probe-eye-btn" onClick={() => setShowPass(s => !s)}>
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            </>) : (
            <div className="adm-field">
              <label>Login</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13 }}>🔓 Not needed: this camera has no password</span>
                <button type="button" className="adm-btn" onClick={() => setShowLogin(true)}>
                  Set username &amp; password
                </button>
              </div>
            </div>
            )}
            <div className="adm-field">
              <label>Main Stream URL</label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input className="adm-input" type="text" placeholder="rtsp://…"
                  value={rtspMain}
                  onChange={e => {
                    setRtspMain(e.target.value);
                    setRtspSub(prev => {
                      const derived = deriveSubUrl(rtspMain);
                      return (prev === derived || prev === '') ? deriveSubUrl(e.target.value) : prev;
                    });
                  }}
                  style={{ fontFamily:'monospace', fontSize:12, flex:1 }} />
                <RtspTestBtn url={rtspMain} transport={rtspTransport} />
              </div>
              {(() => {
                // Typing over the URL makes it the installer's own (the backend
                // records it as "manual" on save), so say that while editing.
                const src = rtspMain.trim() !== (cam.rtsp_url || '') ? 'manual' : cam.url_source;
                const lbl = urlSourceLabel(src, CAM_TYPE_LABELS);
                return lbl && (
                  <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text-muted)' }}>
                    {lbl.short.split(' ')[0]} {lbl.long}
                  </div>
                );
              })()}
            </div>
            <div className="adm-field">
              <label>Sub Stream URL <span style={{ color:'var(--text-muted)', fontWeight:400, fontSize:11 }}>NVR recording</span></label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input className="adm-input" type="text"
                  placeholder={deriveSubUrl(rtspMain) ? 'rtsp://… auto-derived from main if blank'
                    : 'No sub-stream found — live and recording use the main stream'}
                  value={rtspSub} onChange={e => setRtspSub(e.target.value)}
                  style={{ fontFamily:'monospace', fontSize:12, flex:1 }} />
                <RtspTestBtn url={rtspSub} transport={rtspTransport} />
              </div>
            </div>
            <div className="adm-field">
              <label>RTSP Transport</label>
              <select className="adm-input" value={rtspTransport} onChange={e => setRtspTransport(e.target.value)}>
                <option value="tcp">TCP (default — most cameras)</option>
                <option value="udp">UDP (try this if TCP fails with "Nonmatching transport")</option>
              </select>
            </div>
            {recOn() && (
            <div className="adm-field">
              <label>Recording Quality</label>
              <select className="adm-input" value={recStream}
                onChange={e => setRecStream(e.target.value)}>
                <option value="sub">Sub-stream · SD (low disk) — default</option>
                <option value="main">Main-stream · HD (≈10× disk)</option>
              </select>
              {recStream === 'main' && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Main-stream recording uses ~10× more disk.
                </div>
              )}
              {recStream === 'sub' && !rtspSub.trim() && !deriveSubUrl(rtspMain) && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ No sub-stream found for this camera, so recording uses the main stream
                  (more disk). Enter the Sub Stream URL on the Stream tab to fix it.
                </div>
              )}
            </div>
            )}
            <div className="adm-field">
              <label>Live Stream Quality</label>
              <select className="adm-input" value={liveStream}
                onChange={e => setLiveStream(e.target.value)}>
                <option value="">{recOn() ? 'Same as recording — default' : 'Sub-stream · SD — default'}</option>
                <option value="sub">Sub-stream · SD</option>
                <option value="main">Main-stream · HD</option>
              </select>
              {recOn() && liveStream && liveStream !== recStream && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Different from Recording Quality — this opens a second, independent
                  connection to the camera just for live viewing. Leave it on "Same as
                  recording" unless you specifically need them to differ.
                </div>
              )}
            </div>
            {recOn() && (
            <div className="adm-field">
              <label className="adm-checkbox-row">
                <input type="checkbox" checked={recAudio} onChange={e => setRecAudio(e.target.checked)} />
                <span>Record audio</span>
              </label>
              {recAudio && (
                <div style={{ fontSize: 11, color: '#e0a030', marginTop: 6, lineHeight: 1.4 }}>
                  ⚠️ Enable audio for this stream on the camera itself (Camera → Audio →
                  {recStream === 'main' ? ' Main' : ' Sub'} Stream → Enable) and check Microphone
                  Volume is not 0 — otherwise the recording is silent. Audio recording may
                  require on-site notice/consent depending on your region.
                </div>
              )}
            </div>
            )}
            <div className="adm-field">
              <label>Alert Actions</label>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
                {/* Telegram hidden from the UI (not removed): the stored value is kept as-is.
                    Restore by putting 'telegram' back at the front of this list. */}
                {[/* 'telegram', */ 'siren', 'light', 'video', 'push'].map(k => (
                  <label key={k} className="adm-checkbox-row">
                    <input type="checkbox" checked={k === 'push' ? actions[k] !== false : !!actions[k]} onChange={() => toggleAction(k)} />
                    <span style={{ textTransform: 'capitalize' }}>{k === 'push' ? 'Push' : k}</span>
                  </label>
                ))}
                {/* PRVSK-139: every person on this camera reaches the phone, with the photo,
                    at any hour — past Push off, the push window and Siren hours "Off".
                    The siren and lights keep their own schedule. */}
                <label className="adm-checkbox-row"
                       title="Every person on this camera sends a phone notification with their photo, at any hour. Siren and lights keep their schedule. With names waits to recognise known people; Instant rings in 1–2 s and opens the night confidence door at any hour.">
                  <span>🔔 Doorbell (always notify, with photo)</span>
                  <select className="adm-select" style={{ marginLeft: 8, width: 'auto' }}
                          value={actions.doorbell === 'instant' ? 'instant'
                            : (actions.doorbell === true || actions.doorbell === 'names') ? 'names' : 'off'}
                          onChange={e => setActions(a => ({ ...a, doorbell: e.target.value === 'off' ? false : e.target.value }))}>
                    <option value="off">Off</option>
                    <option value="names">On — with names</option>
                    <option value="instant">On — instant (1–2 s)</option>
                  </select>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 4 }}>
              <label className="adm-checkbox-row">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
                <span>Camera enabled</span>
              </label>
              <label className="adm-checkbox-row" title="Holds a persistent TCP connection to keep the WiFi radio awake and prevent disconnect">
                <input type="checkbox" checked={isWifi} onChange={e => setIsWifi(e.target.checked)} />
                <span>WiFi camera <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}>(keep-alive)</span></span>
              </label>
              {recOn() && (
              <label className="adm-checkbox-row"
                title={globalRecOn
                  ? 'Master switch for NVR recording on this camera — see the Schedule tab to limit it to a time window'
                  : 'Recording is disabled globally (Advanced Config > Storage) — no camera can record until it is turned back on there'}>
                <input type="checkbox" checked={globalRecOn && recEnabled} disabled={!globalRecOn}
                  onChange={e => setRecEnabled(e.target.checked)} />
                <span>Recording enabled{!globalRecOn && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (global recording is off)</span>}</span>
              </label>
              )}
            </div>
            <div className="adm-field-row" style={{ marginTop: 4 }}>
              <label className="adm-label">AI on this camera</label>
              <select className="adm-input" style={{ width: 260 }}
                value={detectMode} onChange={e => setDetectMode(e.target.value)}>
                <option value="full">Full detection + alerts</option>
                <option value="checkin">Check-ins only (patrol / attendance)</option>
                <option value="off">AI off (no detection)</option>
              </select>
            </div>
            {detectMode !== 'full' && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                {detectMode === 'checkin'
                  ? 'Check-ins only: Guard Patrol and Employee Attendance keep working on this camera, but no detection runs and no alerts are raised. Saves the AI cost; the stream is still decoded, since check-ins need the frames.'
                  : 'AI off: no detection runs on this camera at all — its stream is never opened or decoded for AI, so it costs almost no CPU, but Guard Patrol and Attendance will not work on it.' + (recOn() ? ' This no longer controls recording: use the Recording enabled toggle above (and the Schedule tab) to control whether this camera records.' : '')}
              </div>
            )}
          </>}

          {activeTab === 'stream' && <>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Stream Settings</label>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Main and sub stream resolution, frame rate and I-frame interval, set on the camera
              over ONVIF. Fewer camera frames = less decode CPU.
            </p>
          </>}
          <div hidden={activeTab !== 'stream'}>
            <CameraStreamSettings idx={idx} onState={onStreamState} />
          </div>
          {activeTab === 'stream' && <>
            <div className="adm-dev-form-row" style={{ alignItems: 'flex-end', marginTop: 12 }}>
              <div className="adm-field" style={{ flex: '0 0 120px' }}>
                <label>ONVIF Port</label>
                <input className="adm-input" type="number" value={onvifPort}
                  onChange={e => setOnvifPort(e.target.value)} />
              </div>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
              Port used for the camera date &amp; time and name overlay below. Delivered detection
              fps = camera fps ÷ Detection Frame Rate (set on the Detection Settings page).
            </p>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />

            {/* ── Date & time ── */}
            <label style={{ fontWeight: 600, fontSize: 13 }}>Camera Date &amp; Time</label>
            <div style={{ fontSize: 13, background: 'var(--surface-alt)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 8, padding: '10px 12px', margin: '8px 0' }}>
              {dtInfo && dtInfo.ok ? (<>
                <div><b>Camera local:</b> {dtInfo.local || '—'} &nbsp; <span style={{ color: 'var(--text-muted)' }}>(mode: {dtInfo.mode || '?'}{dtInfo.tz ? `, ${dtInfo.tz}` : ''})</span></div>
                <div style={{ marginTop: 4 }}><b>UTC:</b> {dtInfo.utc || '—'}</div>
              </>) : dtInfo ? (
                <span style={{ color: 'var(--danger, #f87171)' }}>Couldn’t read time: {dtInfo.error}</span>
              ) : (<span style={{ color: 'var(--text-muted)' }}>{devBusy ? 'Reading…' : '—'}</span>)}
            </div>
            <button type="button" className="adm-btn primary" onClick={syncTime} disabled={devBusy}>
              {devBusy ? 'Working…' : '🕒 Sync time from server'}
            </button>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              Sets the camera clock to this server’s current time (its time zone is kept), so footage
              timestamps match reality.
            </p>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />

            {/* ── On-screen name overlay (OSD) ── */}
            <label style={{ fontWeight: 600, fontSize: 13 }}>On-screen Name (overlay)</label>
            <div className="adm-dev-form-row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
              <div className="adm-field" style={{ flex: 1 }}>
                <input className="adm-input" type="text" value={osdName} maxLength={31}
                  onChange={e => setOsdName(e.target.value)} placeholder="e.g. Main Gate"
                  disabled={osdOk === false} />
              </div>
              <button type="button" className="adm-btn primary" onClick={applyOsd} disabled={devBusy || osdOk === false}>
                {devBusy ? 'Working…' : 'Apply name'}
              </button>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              {osdOk === false
                ? 'This camera didn’t expose an editable text overlay over ONVIF — set it in the camera’s own app instead.'
                : 'The text burned into the video (top/bottom corner). Overlay support varies by camera brand.'}
            </p>

            {devMsg && (
              <div style={{ fontSize: 12, marginTop: 8, color: devMsg.ok ? 'var(--success, #4ade80)' : 'var(--danger, #f87171)' }}>
                {devMsg.text}
              </div>
            )}
          </>}

          {activeTab === 'schedule' && <>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Alert Schedule</label>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 12px' }}>
              Override the global alert schedule for this camera.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                ['INHERIT',   '🌐 Follow Global'],
                ['ALWAYS_ON', '🟢 Always On'],
                ['OFF',       '🔴 Off'],
                ['CUSTOM',    '⏰ Custom Window'],
              ].map(([val, label]) => (
                <button key={val}
                  className={`adm-btn${schedMode === val ? ' primary' : ' secondary'}`}
                  onClick={() => setSchedMode(val)}>{label}</button>
              ))}
            </div>
            {schedMode === 'CUSTOM' && (
              <div className="adm-dev-form-row">
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>Start Time</label>
                  <input className="adm-input" type="time" value={schedStart}
                    onChange={e => setSchedStart(e.target.value)} />
                </div>
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>End Time</label>
                  <input className="adm-input" type="time" value={schedEnd}
                    onChange={e => setSchedEnd(e.target.value)} />
                </div>
              </div>
            )}

            {recOn() && (<>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '18px 0 14px' }} />

            <label style={{ fontWeight: 600, fontSize: 13 }}>Recording Schedule</label>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 12px' }}>
              {recEnabled
                ? 'By default this camera records around the clock while Recording is enabled (Basic tab). Turn this on to limit recording to a daily time window instead.'
                : 'Recording is currently disabled for this camera (Basic tab) — no schedule applies until it is turned back on.'}
            </p>
            <label className="adm-checkbox-row">
              <input type="checkbox" checked={recSchedEnabled} disabled={!recEnabled}
                onChange={e => setRecSchedEnabled(e.target.checked)} />
              <span>Only record during a scheduled window</span>
            </label>
            {recSchedEnabled && recEnabled && (
              <div className="adm-dev-form-row" style={{ marginTop: 10 }}>
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>Start Time</label>
                  <input className="adm-input" type="time" value={recSchedStart}
                    onChange={e => setRecSchedStart(e.target.value)} />
                </div>
                <div className="adm-field" style={{ flex: 1 }}>
                  <label>End Time</label>
                  <input className="adm-input" type="time" value={recSchedEnd}
                    onChange={e => setRecSchedEnd(e.target.value)} />
                </div>
              </div>
            )}
            </>)}
          </>}

          {activeTab === 'ignore' && (
            <IgnoreZonesTab
              idx={idx} camName={cam.name || `Camera ${idx}`}
              zones={ignZones} setZones={setIgnZones}
              pts={ignPts}     setPts={setIgnPts}
              onDirty={() => setIgnDirty(true)}
              maximized={maximized}
            />
          )}

          {activeTab === 'gate' && (
            <IgnoreZonesTab
              kind="gate"
              idx={idx} camName={cam.name || `Camera ${idx}`}
              zones={gateZones} setZones={setGateZones}
              pts={gatePts}     setPts={setGatePts}
              onDirty={() => setGateDirty(true)}
              maximized={maximized}
            />
          )}

          {/* Saves itself ({detectors, detector_config}) — independent of the footer
              Save, which keeps saving exactly what it saved before. */}
          {activeTab === 'detectors' && (
            <CameraDetectorsTab cam={cam} idx={idx} />
          )}

          {error && <div className="adm-msg error">{error}</div>}
        </div>{/* .adm-modal-form */}
        </div>{/* .adm-modal-body */}
        <div className="adm-modal-footer">
          <button className="adm-btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="adm-btn primary"   onClick={() => save()} disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {askUnfinished && (() => {
        const isGate = ignPts.length === 0;
        const drawing = isGate ? gatePts : ignPts;
        const why = drawing.length < 3 ? 'needs at least 3 corners' : zoneProblem(drawing);
        return (
          <div onClick={e => e.stopPropagation()}>
            <ConfirmDialog
              title={`You’re still drawing ${isGate ? 'a gate' : 'an ignore'} area (${drawing.length} point${drawing.length === 1 ? '' : 's'})`}
              message={why
                ? `It ${why}, so it can’t be saved as an area. Discard it and save the rest?`
                : isGate
                  ? 'Only a person standing inside a gate area rings the doorbell. Save this one too, or discard it?'
                  : 'Alerts are switched off inside an ignore area. Save this one too, or discard it?'}
              confirmLabel={why ? 'Discard it & save' : 'Save area'}
              confirmClass={why ? 'danger' : 'primary'}
              onConfirm={() => save(why ? 'discard' : 'keep')}
              secondaryLabel={why ? undefined : 'Discard it'}
              onSecondary={() => save('discard')}
              onCancel={() => setAskUnfinished(false)} />
          </div>
        );
      })()}
    </div>
  );
}

// Main → sub stream URL by brand convention. Kept in step with
// derive_sub_url in main.py (PRVSK-129 added Hikvision 101→102 and Endroid ch01.264).
const SUB_RULES = [
  [/subtype=0\b/i, 'subtype=1'],
  [/(\/Streaming\/Channels\/\d*?)01\b/i, (_, p) => `${p}02`],
  [/\/MAIN\b/i, '/SUB'],
  [/\b(ch\d+)\.264\b/i, '$1_sub.264'],
  [/\b(ch\d+)_0\b/i, '$1_1'],
  [/stream0\b/i, 'stream1'],
];
function deriveSubUrl(main) {
  if (!main || typeof main !== 'string') return '';
  for (const [re, rep] of SUB_RULES) {
    const s = main.replace(re, rep);
    if (s !== main) return s;
  }
  return '';
}

function RtspTestBtn({ url, transport = 'tcp' }) {
  const [state, setState] = useState(null); // null | 'testing' | {ok, message}
  const urlStr = typeof url === 'string' ? url.trim() : '';

  const test = async () => {
    if (!urlStr) return;
    setState('testing');
    try {
      const res  = await apiFetch('/ui/cameras/test-rtsp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rtsp_url: urlStr, transport }),
      });
      const data = await res.json();
      setState(data);
      setTimeout(() => setState(null), 6000);
    } catch {
      setState({ ok: false, message: 'Server error' });
      setTimeout(() => setState(null), 6000);
    }
  };

  const testing = state === 'testing';
  const result  = state && state !== 'testing' ? state : null;

  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6, flexShrink:0 }}>
      <button
        type="button"
        onClick={test}
        disabled={testing || !urlStr}
        style={{
          padding: '5px 12px', fontSize: 12, fontWeight: 600,
          border: '1px solid var(--border)', borderRadius: 6,
          background: 'var(--surface-alt)', color: 'var(--text)',
          cursor: testing || !urlStr ? 'not-allowed' : 'pointer',
          opacity: !urlStr ? 0.4 : 1, whiteSpace: 'nowrap',
        }}
      >
        {testing ? '⏳ Testing…' : 'Test'}
      </button>
      {result && (
        <span style={{ fontSize: 12, fontWeight: 600, color: result.ok ? '#22c55e' : '#ef4444' }}>
          {result.ok ? '✓' : '✗'} {result.message}
        </span>
      )}
    </span>
  );
}

function RtspUrlRow({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const display = url.length > 52 ? url.slice(0, 52) + '…' : url;
  return (
    <div onClick={copy} title={url}
      style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, cursor: 'pointer',
               fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ opacity: 0.6 }}>🔗</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
      <span style={{ fontSize: 10, color: copied ? '#22c55e' : 'var(--text-muted)', flexShrink: 0 }}>
        {copied ? 'Copied!' : 'Copy'}
      </span>
    </div>
  );
}

function CamStateBadges({ cam, globalRecOff }) {
  const camOn  = cam.nvr !== false;
  const recOn  = camOn && !globalRecOff;
  // Whenever the global NVR switch (Advanced Config > Storage) is off, no
  // camera actually records — flag that distinctly regardless of this
  // camera's own setting so it never reads as "Rec On" when it isn't.
  const recLbl = recOn ? 'Rec On' : globalRecOff ? '🌐 Rec Off' : 'Rec Off';
  const recTitle = globalRecOff
    ? `Recording is disabled globally (Advanced Config > Storage)${camOn ? ' — this camera is itself set to record' : ''}`
    : 'Recording';
  const mode  = cam.detect_mode || 'full';
  const aiCls = mode === 'off' ? 'disabled' : mode === 'checkin' ? 'connecting' : 'online';
  const aiLbl = mode === 'off' ? 'AI Off' : mode === 'checkin' ? 'AI Check-in' : 'AI On';
  const sched = cam.record_schedule_enabled && cam.record_schedule?.start && cam.record_schedule?.end
    ? `${cam.record_schedule.start}–${cam.record_schedule.end}` : null;
  return (
    <>
      <span className={`cam-status-badge ${recOn ? 'online' : 'disabled'}`} title={recTitle}>{recLbl}</span>
      <span className={`cam-status-badge ${aiCls}`} title="AI detection">{aiLbl}</span>
      {sched && <span className="cam-action-chip" title="Recording schedule">🕒 {sched}</span>}
    </>
  );
}

function CameraListSection({ onAddNew }) {
  const [cameras,      setCameras]      = useState([]);
  const [healthMap,    setHealthMap]    = useState({});
  const [globalRecOn,  setGlobalRecOn]  = useState(true);
  const [loading,      setLoading]      = useState(true);
  const [editTarget,   setEditTarget]   = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [restarting,   setRestarting]   = useState(new Set());
  const isMobile = useIsMobile();
  const [toast,        setToast]        = useState(null);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    try {
      const [camRes, hlRes] = await Promise.all([
        apiFetch('/ui/cameras'), apiFetch('/health'),
      ]);
      const camData = await camRes.json();
      const hlData  = await hlRes.json();
      setCameras(camData.cameras || []);
      const hm = {};
      (hlData.cameras || []).forEach(c => { hm[c.idx] = c; });
      setHealthMap(hm);
      setGlobalRecOn(!!hlData.nvr?.enabled);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await apiFetch(`/cameras/${confirmDel.idx}`, { method: 'DELETE' });
      setConfirmDel(null);
      load();
    } catch {}
    finally { setDeleting(false); }
  };

  const handleSaved = (updatedCam) => {
    setCameras(prev => prev.map(c => c.idx === editTarget.idx ? { ...c, ...updatedCam } : c));
    setEditTarget(null);
    showToast('Camera settings saved');
  };

  const handleMove = async (cam, direction) => {
    try {
      await apiFetch(`/ui/cameras/${cam.idx}/move?direction=${direction}`, { method: 'POST' });
      load();
    } catch { showToast('Move failed', 'err'); }
  };

  const handleRestartOne = async (cam) => {
    setRestarting(prev => new Set(prev).add(cam.idx));
    try {
      const r = await apiFetch(`/admin/cameras/${cam.idx}/restart`, { method: 'POST' });
      const d = await r.json();
      if (d.ok) showToast(`🔄 ${d.name} restarting…`);
      else showToast('Restart failed', 'err');
    } catch { showToast('Restart failed', 'err'); }
    finally {
      setTimeout(() => setRestarting(prev => { const s = new Set(prev); s.delete(cam.idx); return s; }), 2000);
    }
  };

  if (loading) return <div className="adm-state"><div className="adm-spinner" /></div>;

  return (
    <div className="cam-list-wrap">
      {toast && (
        <div className={`pk-toast ${toast.type}`} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
          {toast.msg}
        </div>
      )}
      {isMobile ? (
        <div className="cam-header-mobile">
          <div className="cam-header-mobile-top">
            <span className="adm-section-title">Cameras</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="adm-btn secondary" style={{ padding: '7px 12px' }} onClick={load} title="Refresh">↻</button>
              <RestartCamerasButton iconOnly />
            </div>
          </div>
          <button className="adm-btn primary" style={{ width: '100%' }} onClick={onAddNew}>+ Add New Camera</button>
        </div>
      ) : (
        <div className="adm-health-header" style={{ marginBottom: 16 }}>
          <h2 className="adm-section-title">Cameras</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="adm-refresh-btn" onClick={load}>↻ Refresh</button>
            <RestartCamerasButton />
            <button className="adm-btn primary" onClick={onAddNew}>+ Add New Camera</button>
          </div>
        </div>
      )}

      {cameras.length === 0 ? (
        <div className="adm-state">
          <div className="adm-empty-icon">📷</div>
          <div>No cameras configured.</div>
          <button className="adm-btn primary" onClick={onAddNew}>Add Camera</button>
        </div>
      ) : (
        <div className="cam-list">
          {cameras.map(cam => {
            const hc       = healthMap[cam.idx] || {};
            const disabled = cam.enabled === false;
            const online   = hc.connected;
            // A record-only camera has no reader by design, so "Connecting"
            // would be its permanent state. Its health is whether it is still
            // recording, which the backend reports as connected/recording_age_s.
            const recOnly  = hc.detect_mode === 'off';
            // The camera answers but refuses its saved login — say that, not "Offline".
            const authFail = !disabled && !online && hc.auth_failed;
            const statusCls = disabled ? 'disabled'
              : authFail ? 'offline'
              : recOnly ? (online ? 'online' : 'offline')
              : online ? 'online' : (hc.reader_alive === false ? 'offline' : 'connecting');
            const statusLbl = disabled ? 'Disabled'
              : authFail ? '🔒 Asks for a password'
              : recOnly ? (online ? 'Recording' : 'Not recording')
              : online ? 'Online' : (hc.reader_alive === false ? 'Offline' : 'Connecting');
            return isMobile ? (
              <div key={cam.idx} className={`adm-user-card${cam.enabled === false ? ' cam-card-disabled' : ''}`}>
                <div className="adm-user-card-top">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
                    <span className="adm-user-card-name">{cam.name}</span>
                    <div className="cam-card-meta" style={{ marginTop: 0 }}>
                      {cam.ip && <span className="cam-meta-item">📍 {cam.ip}</span>}
                      {cam.type && <span className="cam-meta-item">🎥 {CAM_TYPE_LABELS[cam.type] || cam.type}</span>}
                      {cam.zone && <span className="cam-meta-item">📌 {cam.zone}</span>}
                      {urlSourceLabel(cam.url_source, CAM_TYPE_LABELS) && (
                        <span className="cam-meta-item" title={urlSourceLabel(cam.url_source, CAM_TYPE_LABELS).long}>
                          {urlSourceLabel(cam.url_source, CAM_TYPE_LABELS).short}
                        </span>
                      )}
                    </div>
                    <div className="cam-card-actions-row" style={{ padding: 0 }}>
                      <CamStateBadges cam={cam} globalRecOff={!globalRecOn} />
                    </div>
                  </div>
                  <span className={`cam-status-badge ${statusCls}`}
                        title={authFail ? 'The camera is on the network but refuses its saved login. Edit the camera and enter its username and password.' : undefined}>{statusLbl}</span>
                </div>
                {cam.alert_actions && (Object.entries(cam.alert_actions).some(([k, v]) => k !== 'telegram' && v) || cam.alert_actions.push === false) && (
                  <div className="cam-card-actions-row" style={{ padding: '0 0 6px' }}>
                    {/* Telegram hidden from the UI: {cam.alert_actions.telegram && <span className="cam-action-chip">Telegram</span>} */}
                    {cam.alert_actions.siren    && <span className="cam-action-chip">Siren</span>}
                    {cam.alert_actions.light    && <span className="cam-action-chip">Light</span>}
                    {cam.alert_actions.video    && <span className="cam-action-chip">Video</span>}
                    {cam.alert_actions.push === false && <span className="cam-action-chip" style={{ background: 'var(--danger, #e53935)', color: '#fff' }}>No Push</span>}
                    {cam.alert_actions.doorbell && <span className="cam-action-chip">🔔 Doorbell{cam.alert_actions.doorbell === 'instant' ? ' · Instant' : ''}</span>}
                  </div>
                )}
                {cam.rtsp_url && <RtspUrlRow url={cam.rtsp_url} />}
                <div className="cam-mob-actions">
                  <div className="cam-mob-move">
                    <button className="cam-move-btn" disabled={cam.idx === 0} onClick={() => handleMove(cam, 'up')}>▲</button>
                    <button className="cam-move-btn" disabled={cam.idx === cameras.length - 1} onClick={() => handleMove(cam, 'down')}>▼</button>
                  </div>
                  <button className="adm-edit-btn cam-mob-action-btn" onClick={() => setEditTarget(cam)}>Edit</button>
                  <button className="adm-restart-btn cam-mob-action-btn" disabled={restarting.has(cam.idx)} onClick={() => handleRestartOne(cam)}>
                    {restarting.has(cam.idx) ? '…' : 'Restart'}
                  </button>
                  <button className="adm-del-btn cam-mob-action-btn" onClick={() => setConfirmDel(cam)}>Remove</button>
                </div>
              </div>
            ) : (
              <div key={cam.idx} className={`cam-card${cam.enabled === false ? ' cam-card-disabled' : ''}`}>
                <div className="cam-card-left">
                  <div className="cam-card-name">
                    {cam.name}
                    <span className={`cam-status-badge ${statusCls}`}
                        title={authFail ? 'The camera is on the network but refuses its saved login. Edit the camera and enter its username and password.' : undefined}>{statusLbl}</span>
                  </div>
                  <div className="cam-card-meta">
                    {cam.ip && <span className="cam-meta-item">📍 {cam.ip}</span>}
                    {cam.type && <span className="cam-meta-item">🎥 {CAM_TYPE_LABELS[cam.type] || cam.type}</span>}
                    {cam.zone && <span className="cam-meta-item">📌 {cam.zone}</span>}
                      {urlSourceLabel(cam.url_source, CAM_TYPE_LABELS) && (
                        <span className="cam-meta-item" title={urlSourceLabel(cam.url_source, CAM_TYPE_LABELS).long}>
                          {urlSourceLabel(cam.url_source, CAM_TYPE_LABELS).short}
                        </span>
                      )}
                    {cam.source === 'nvr' && cam.channel && <span className="cam-meta-item">Ch {cam.channel}</span>}
                  </div>
                  <div className="cam-card-actions-row" style={{ padding: 0 }}>
                    <CamStateBadges cam={cam} globalRecOff={!globalRecOn} />
                  </div>
                  {cam.rtsp_url && <RtspUrlRow url={cam.rtsp_url} />}
                  {cam.alert_actions && (Object.entries(cam.alert_actions).some(([k, v]) => k !== 'telegram' && v) || cam.alert_actions.push === false) && (
                    <div className="cam-card-actions-row">
                      {/* Telegram hidden from the UI: {cam.alert_actions.telegram && <span className="cam-action-chip">Telegram</span>} */}
                      {cam.alert_actions.siren    && <span className="cam-action-chip">Siren</span>}
                      {cam.alert_actions.light    && <span className="cam-action-chip">Light</span>}
                      {cam.alert_actions.video    && <span className="cam-action-chip">Video</span>}
                      {cam.alert_actions.push === false && <span className="cam-action-chip" style={{ background: 'var(--danger, #e53935)', color: '#fff' }}>No Push</span>}
                      {cam.alert_actions.doorbell && <span className="cam-action-chip">🔔 Doorbell{cam.alert_actions.doorbell === 'instant' ? ' · Instant' : ''}</span>}
                    </div>
                  )}
                </div>
                <div className="cam-card-right">
                  <div className="cam-move-btns">
                    <button className="cam-move-btn" title="Move up"
                      disabled={cam.idx === 0}
                      onClick={() => handleMove(cam, 'up')}>▲</button>
                    <button className="cam-move-btn" title="Move down"
                      disabled={cam.idx === cameras.length - 1}
                      onClick={() => handleMove(cam, 'down')}>▼</button>
                  </div>
                  <button className="adm-edit-btn" onClick={() => setEditTarget(cam)}>Edit</button>
                  <button className="adm-restart-btn" disabled={restarting.has(cam.idx)}
                    title="Restart camera" onClick={() => handleRestartOne(cam)}>
                    {restarting.has(cam.idx) ? 'Restarting…' : 'Restart'}
                  </button>
                  <button className="adm-del-btn"  onClick={() => setConfirmDel(cam)}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editTarget && (
        <EditCameraModal
          cam={editTarget} idx={editTarget.idx}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
          globalRecOn={globalRecOn}
        />
      )}

      {confirmDel && (
        <div className="adm-overlay" onClick={() => setConfirmDel(null)}>
          <div className="adm-modal adm-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>Remove Camera</h3>
              <button className="adm-modal-close" onClick={() => setConfirmDel(null)}>✕</button>
            </div>
            <div className="adm-confirm-body">
              <div className="adm-confirm-icon">⚠️</div>
              <p>Remove <strong>{confirmDel.name}</strong>?<br />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  The camera will stop streaming. This cannot be undone.
                </span>
              </p>
            </div>
            <div className="adm-modal-actions">
              <button className="adm-btn secondary" onClick={() => setConfirmDel(null)} disabled={deleting}>Cancel</button>
              <button className="adm-btn danger"    onClick={handleDelete}              disabled={deleting}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RestartCamerasButton({ iconOnly = false }) {
  const [restarting, setRestarting] = useState(false);
  const [msg,        setMsg]        = useState(null);
  const [confirm,    setConfirm]    = useState(false);

  const doRestart = async () => {
    setConfirm(false);
    setRestarting(true); setMsg(null);
    try {
      const r = await apiFetch('/admin/restart-cameras', { method: 'POST' });
      const d = await r.json();
      setMsg(d.ok ? { ok: true, text: `✓ Restarting ${d.restarted} camera(s)…` } : { ok: false, text: 'Failed' });
    } catch { setMsg({ ok: false, text: 'Error' }); }
    finally { setRestarting(false); setTimeout(() => setMsg(null), 4000); }
  };

  return (
    <>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button className="adm-btn secondary" style={iconOnly ? { padding: '7px 12px' } : {}}
        onClick={() => setConfirm(true)} disabled={restarting}
        title={iconOnly ? 'Restart All Cameras' : undefined}>
        {restarting ? (iconOnly ? '…' : 'Restarting…') : (iconOnly ? '🔄' : '📷 Restart All')}
      </button>
      {!iconOnly && msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
    </div>
    {confirm && (
      <ConfirmDialog
        title="Restart all cameras?"
        message="Streams will briefly disconnect."
        confirmLabel="Restart"
        confirmClass="danger"
        onConfirm={doRestart}
        onCancel={() => setConfirm(false)}
      />
    )}
    </>
  );
}

function ManageCameraTab() {
  const [view, setView] = useState('list');

  return (
    <>
      {view === 'list' && (
        <CameraListSection onAddNew={() => setView('add')} />
      )}
      {view === 'add'  && (
        <div>
          <button className="wiz-back-btn" onClick={() => setView('list')}
            style={{ marginBottom: 16, fontSize: 13, display: 'block' }}>
            ← Back to camera list
          </button>
          <AddCameraWizard onDone={() => setView('list')} />
        </div>
      )}
    </>
  );
}

function AlertScheduleTab({ mode, setMode, start, setStart, end, setEnd, loading }) {
  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  return (
    <div style={{ maxWidth: 420 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
        Controls when alerts fire across all cameras. Per-camera schedule can override this.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['ALWAYS_ON', '🟢 Always On'], ['OFF', '🔴 Off'], ['TIME_BASED', '⏰ Time Window']].map(([val, label]) => (
          <button key={val} className={`adm-btn${mode === val ? ' primary' : ' secondary'}`}
            onClick={() => setMode(val)}>{label}</button>
        ))}
      </div>
      {mode === 'TIME_BASED' && (
        <div className="adm-dev-form-row" style={{ marginBottom: 16 }}>
          <div className="adm-field" style={{ flex: 1 }}>
            <label>Start Time</label>
            <input className="adm-input" type="time" value={start} onChange={e => setStart(e.target.value)} />
          </div>
          <div className="adm-field" style={{ flex: 1 }}>
            <label>End Time</label>
            <input className="adm-input" type="time" value={end} onChange={e => setEnd(e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}

function AdvancedConfigTab({ clipGate, setClipGate, clipThreshold, setClipThreshold, debugMode, setDebugMode, loading }) {
  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  return (
    <div style={{ maxWidth: 420 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Global settings that apply to all cameras (per-camera overrides take priority).
      </p>
      <div className="adm-field">
        <label className="adm-checkbox-row">
          <input type="checkbox" checked={clipGate} onChange={e => setClipGate(e.target.checked)} />
          <span>CLIP Gate <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(AI human verification)</span></span>
        </label>
      </div>
      <div className="adm-field">
        <label>CLIP Threshold</label>
        <input className="adm-input" type="number" step="0.005" min="-0.1" max="0.1"
          value={clipThreshold} disabled={!clipGate}
          onChange={e => setClipThreshold(e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Lower = more sensitive · default: 0.0</div>
      </div>
      <div className="adm-field">
        <label className="adm-checkbox-row">
          <input type="checkbox" checked={debugMode} onChange={e => setDebugMode(e.target.checked)} />
          <span>Debug Mode <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>(verbose logging)</span></span>
        </label>
      </div>
    </div>
  );
}

function GlobalSettingsSection() {
  const [sub, setSub] = useState('schedule');

  const [schedMode,    setSchedMode]    = useState('ALWAYS_ON');
  const [schedStart,   setSchedStart]   = useState('00:30');
  const [schedEnd,     setSchedEnd]     = useState('05:00');
  const [schedLoading, setSchedLoading] = useState(true);
  const [schedSaving,  setSchedSaving]  = useState(false);
  const [schedMsg,     setSchedMsg]     = useState(null);

  const [clipGate,      setClipGate]      = useState(true);
  const [clipThreshold, setClipThreshold] = useState(0.0);
  const [debugMode,     setDebugMode]     = useState(false);
  const [advLoading,    setAdvLoading]    = useState(true);
  const [advSaving,     setAdvSaving]     = useState(false);
  const [advMsg,        setAdvMsg]        = useState(null);

  const [camOffEnabled, setCamOffEnabled] = useState(true);
  const [camOffMinutes, setCamOffMinutes] = useState(5);
  const [camOffLoading, setCamOffLoading] = useState(true);
  const [camOffSaving,  setCamOffSaving]  = useState(false);
  const [camOffMsg,     setCamOffMsg]     = useState(null);

  useEffect(() => {
    apiFetch('/settings/alert-schedule').then(r => r.json()).then(d => {
      setSchedMode(d.mode || 'ALWAYS_ON');
      setSchedStart(d.start || '00:30');
      setSchedEnd(d.end || '05:00');
      setSchedLoading(false);
    }).catch(() => setSchedLoading(false));
    apiFetch('/settings/advanced-config').then(r => r.json()).then(d => {
      setClipGate(d.clip_gate ?? true);
      setClipThreshold(d.clip_threshold ?? 0.0);
      setDebugMode(d.debug_mode ?? false);
      setAdvLoading(false);
    }).catch(() => setAdvLoading(false));
    apiFetch('/settings/camera-offline-alert').then(r => r.json()).then(d => {
      setCamOffEnabled(d.enabled ?? true);
      setCamOffMinutes(d.minutes ?? 5);
      setCamOffLoading(false);
    }).catch(() => setCamOffLoading(false));
  }, []);

  const saveSchedule = async () => {
    setSchedSaving(true); setSchedMsg(null);
    try {
      const r = await apiFetch('/settings/alert-schedule', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: schedMode, start: schedStart, end: schedEnd }),
      });
      if (r.ok) setSchedMsg({ ok: true, text: '✓ Saved' });
      else setSchedMsg({ ok: false, text: 'Failed' });
    } catch { setSchedMsg({ ok: false, text: 'Error' }); }
    finally { setSchedSaving(false); setTimeout(() => setSchedMsg(null), 2500); }
  };

  const saveAdvanced = async () => {
    setAdvSaving(true); setAdvMsg(null);
    try {
      const r = await apiFetch('/settings/advanced-config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clip_gate: clipGate, clip_threshold: parseFloat(clipThreshold), debug_mode: debugMode }),
      });
      if (r.ok) setAdvMsg({ ok: true, text: '✓ Saved' });
      else setAdvMsg({ ok: false, text: 'Failed' });
    } catch { setAdvMsg({ ok: false, text: 'Error' }); }
    finally { setAdvSaving(false); setTimeout(() => setAdvMsg(null), 2500); }
  };

  const saveCamOffline = async () => {
    setCamOffSaving(true); setCamOffMsg(null);
    try {
      const r = await apiFetch('/settings/camera-offline-alert', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: camOffEnabled, minutes: parseInt(camOffMinutes, 10) || 5 }),
      });
      if (r.ok) setCamOffMsg({ ok: true, text: '✓ Saved' });
      else setCamOffMsg({ ok: false, text: 'Failed' });
    } catch { setCamOffMsg({ ok: false, text: 'Error' }); }
    finally { setCamOffSaving(false); setTimeout(() => setCamOffMsg(null), 2500); }
  };

  const subTabStyle = t => ({
    padding: '5px 12px', fontSize: 12, cursor: 'pointer', border: 'none',
    background: sub === t ? 'var(--primary)' : 'var(--surface-alt)',
    color: sub === t ? '#fff' : 'var(--text-muted)',
    borderRadius: 6, fontWeight: sub === t ? 600 : 400,
  });

  const activeMsg    = sub === 'schedule' ? schedMsg    : sub === 'advanced' ? advMsg    : camOffMsg;
  const activeSaving = sub === 'schedule' ? schedSaving : sub === 'advanced' ? advSaving : camOffSaving;
  const activeSave   = sub === 'schedule' ? saveSchedule : sub === 'advanced' ? saveAdvanced : saveCamOffline;

  return (
    <div className="adm-section" style={{ marginBottom: 16 }}>
      <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Alert Schedule & Detection</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 24px 0' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={subTabStyle('schedule')} onClick={() => setSub('schedule')}>📅 Alert Schedule</button>
          <button style={subTabStyle('advanced')} onClick={() => setSub('advanced')}>⚙️ Advanced Config</button>
          <button style={subTabStyle('camoffline')} onClick={() => setSub('camoffline')}>📴 Camera Offline Alert</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {activeMsg && <span style={{ fontSize: 13, color: activeMsg.ok ? '#22c55e' : '#ef4444' }}>{activeMsg.text}</span>}
          <button className="adm-btn primary" onClick={activeSave} disabled={activeSaving}>{activeSaving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      <div className="adm-section-body">
        {sub === 'schedule' && <AlertScheduleTab
          mode={schedMode} setMode={setSchedMode}
          start={schedStart} setStart={setSchedStart}
          end={schedEnd} setEnd={setSchedEnd}
          loading={schedLoading} />}
        {sub === 'advanced' && <AdvancedConfigTab
          clipGate={clipGate} setClipGate={setClipGate}
          clipThreshold={clipThreshold} setClipThreshold={setClipThreshold}
          debugMode={debugMode} setDebugMode={setDebugMode}
          loading={advLoading} />}
        {sub === 'camoffline' && <CameraOfflineAlertTab
          enabled={camOffEnabled} setEnabled={setCamOffEnabled}
          minutes={camOffMinutes} setMinutes={setCamOffMinutes}
          loading={camOffLoading} />}
      </div>
    </div>
  );
}

function CameraOfflineAlertTab({ enabled, setEnabled, minutes, setMinutes, loading }) {
  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  return (
    <div style={{ maxWidth: 420 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        Raise a Critical alert in the Ops Console when a camera stops sending
        video for this long. One threshold for every camera, wired or WiFi.
      </p>
      <div className="adm-field">
        <label className="adm-checkbox-row">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
          <span>Alert on camera offline</span>
        </label>
      </div>
      <div className="adm-field">
        <label>Offline for (minutes)</label>
        <input className="adm-input" type="number" min="1" step="1"
          value={minutes} disabled={!enabled}
          onChange={e => setMinutes(e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Default: 5</div>
      </div>
    </div>
  );
}

const INTERVAL_OPTIONS = [
  { label: '10 seconds', value: 10 },
  { label: '15 seconds', value: 15 },
  { label: '30 seconds',        value: 30 },
  { label: '1 minute (default)', value: 60 },
  { label: '2 minutes',  value: 120 },
  { label: '5 minutes',  value: 300 },
];

const IDLE_RELOAD_OPTIONS = [
  { label: '1 minute',            value: 60  },
  { label: '2 minutes',           value: 120 },
  { label: '5 minutes (default)', value: 300 },
  { label: '10 minutes',          value: 600 },
  { label: 'Never',               value: 0   },
];

function ServerControlsSection() {
  const [reloading, setReloading] = useState(false);
  const [reloadMsg, setReloadMsg] = useState(null);

  const reloadConfig = async () => {
    setReloading(true); setReloadMsg(null);
    try {
      const r = await apiFetch('/admin/reload-config', { method: 'POST' });
      const d = await r.json();
      setReloadMsg(d.ok ? { ok: true, text: '✓ Config reloaded' } : { ok: false, text: d.detail || 'Failed' });
    } catch { setReloadMsg({ ok: false, text: 'Error' }); }
    finally { setReloading(false); setTimeout(() => setReloadMsg(null), 3000); }
  };

  return (
    <div className="adm-section" style={{ marginBottom: 16 }}>
      <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Server Controls</div>
      <div className="adm-section-body">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="adm-btn" onClick={reloadConfig} disabled={reloading}>
            {reloading ? 'Reloading…' : '🔄 Reload Config'}
          </button>
          {reloadMsg && <span style={{ fontSize: 13, color: reloadMsg.ok ? '#22c55e' : '#ef4444' }}>{reloadMsg.text}</span>}
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Picks up manual config.json changes without restarting the server.
        </p>
      </div>
    </div>
  );
}

function DeviceScheduleSection() {
  const [globalCfg,   setGlobalCfg]   = useState(null);
  const [channels,    setChannels]    = useState([]);
  const [gStart,      setGStart]      = useState('00:30');
  const [gEnd,        setGEnd]        = useState('05:00');
  const [globalSaved, setGlobalSaved] = useState(false);
  const [globalErr,   setGlobalErr]   = useState(false);
  const [chEdits,     setChEdits]     = useState({});   // { channel: { start, end } }
  const [chSaved,     setChSaved]     = useState({});   // { channel: true }

  useEffect(() => {
    apiFetch('/ui/devices/status')
      .then(r => r.json())
      .then(d => {
        const g = d.relay_global || {};
        setGlobalCfg(g);
        const sched = g.alert_schedule;
        if (sched && typeof sched === 'object') {
          setGStart(sched.start || '00:30');
          setGEnd(sched.end   || '05:00');
        }
        setChannels(d.relay_channels || []);
      })
      .catch(() => {});
  }, []);

  const globalMode = !globalCfg ? 'always'
    : globalCfg.enabled === false ? 'off'
    : (globalCfg.alert_schedule && typeof globalCfg.alert_schedule === 'object' ? 'window' : 'always');

  const saveGlobal = async (patch) => {
    try {
      const res  = await apiFetch('/ui/devices/relay/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      setGlobalCfg(data);
      setGlobalSaved(true);
      setTimeout(() => setGlobalSaved(false), 2000);
    } catch {
      setGlobalErr(true);
      setTimeout(() => setGlobalErr(false), 2000);
    }
  };

  const patchChannel = async (ch, patch) => {
    try {
      const res  = await apiFetch(`/ui/devices/relay/channels/${ch}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      setChannels(data.channels || []);
      setChEdits(e => { const n = { ...e }; delete n[ch]; return n; });
      setChSaved(s => ({ ...s, [ch]: true }));
      setTimeout(() => setChSaved(s => { const n = { ...s }; delete n[ch]; return n; }), 2000);
    } catch {}
  };

  const initEdit = (ch) => {
    const sched = ch.alert_schedule;
    const fs    = ch.fixed_schedule;
    return {
      alertMode:  !sched || sched === 'always' ? 'always' : sched === 'follow_global' ? 'follow_global' : 'window',
      alertStart: typeof sched === 'object' ? sched.start : '00:30',
      alertEnd:   typeof sched === 'object' ? sched.end   : '05:00',
      fixedMode:  fs ? 'on' : 'off',
      fixedStart: fs?.start ?? '07:00',
      fixedEnd:   fs?.end   ?? '19:00',
    };
  };

  const getEdit = (ch) => chEdits[ch.channel] ?? initEdit(ch);

  const saveCard = (ch) => {
    const e = getEdit(ch);
    const patch = {};
    if      (e.alertMode === 'follow_global') patch.alert_schedule = 'follow_global';
    else if (e.alertMode === 'always')        patch.alert_schedule = 'always';
    else                                       patch.alert_schedule = { start: e.alertStart, end: e.alertEnd };
    patch.fixed_schedule = e.fixedMode === 'on' ? { start: e.fixedStart, end: e.fixedEnd } : null;
    patchChannel(ch.channel, patch);
  };

  const updateEdit = (ch, fields) =>
    setChEdits(e => ({ ...e, [ch.channel]: { ...getEdit(ch), ...fields } }));

  const chMode = (ch) => {
    const sched = ch.alert_schedule;
    if (!sched || sched === 'always') return 'always';
    if (sched === 'follow_global')    return 'follow_global';
    return 'window';
  };

  if (!globalCfg) return null;

  return (
    <div className="adm-section" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <div className="adm-section-title" style={{ margin: 0 }}>Device Schedule</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {globalSaved && <span style={{ color: 'var(--success, #22c55e)', fontSize: 13 }}>✓ Saved</span>}
          {globalErr   && <span style={{ color: 'var(--danger, #ef4444)',  fontSize: 13 }}>Save failed</span>}
          <button className="adm-btn primary" onClick={() => {
            if (globalMode === 'off')     saveGlobal({ enabled: false });
            else if (globalMode === 'always') saveGlobal({ enabled: true, alert_schedule: 'always' });
            else saveGlobal({ enabled: true, alert_schedule: { start: gStart, end: gEnd } });
          }}>Save</button>
        </div>
      </div>
      <div className="adm-section-body">

        {/* ── Global ── */}
        <label className="adm-label" style={{ marginBottom: 8, display: 'block' }}>Global Device Schedule</label>
        <div className="adm-push-seg">
          {[['always', '🟢 Always On'], ['off', '🔴 Off'], ['window', '⏰ Time Window']].map(([val, label]) => (
            <button key={val}
              className={`adm-push-seg-btn${globalMode === val ? ' active' : ''}`}
              onClick={() => {
                if (val === 'off')    setGlobalCfg(g => ({ ...g, enabled: false }));
                else if (val === 'always') setGlobalCfg(g => ({ ...g, enabled: true, alert_schedule: 'always' }));
                else setGlobalCfg(g => ({ ...g, enabled: true, alert_schedule: { start: gStart, end: gEnd } }));
              }}>
              {label}
            </button>
          ))}
        </div>
        {globalMode === 'window' && (
          <>
            <div className="adm-field-row" style={{ marginTop: 12 }}>
              <label className="adm-label">Active from</label>
              <input type="time" className="adm-input" style={{ width: 130 }}
                value={gStart} onChange={e => setGStart(e.target.value)} />
            </div>
            <div className="adm-field-row">
              <label className="adm-label">Active until</label>
              <input type="time" className="adm-input" style={{ width: 130 }}
                value={gEnd} onChange={e => setGEnd(e.target.value)} />
            </div>
          </>
        )}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          {globalMode === 'off'    ? 'All relay devices disabled. Motor & strobe still follow their own schedule.'
          : globalMode === 'always' ? 'Relay devices fire on any alert, any time.'
          : `Relay devices only fire between ${gStart} – ${gEnd}.`}
        </p>

      </div>

      {/* ── Per-device — full width ── */}
      {channels.filter(ch => ch.on_alert).length > 0 && (() => {
          const renderCard = (ch) => {
            const e       = getEdit(ch);
            const isIndep = ['motor','strobe'].includes(ch.type);
            return (
              <div key={ch.channel} className="adm-dev-sched-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="adm-dev-sched-name">{ch.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {chSaved[ch.channel] && <span style={{ fontSize: 12, color: 'var(--success, #22c55e)' }}>✓ Saved</span>}
                    <button className="adm-btn primary" onClick={() => saveCard(ch)}>Save</button>
                  </div>
                </div>
                <div className="adm-dev-sched-body">

                  {/* Alert Schedule */}
                  <div>
                    <span className="adm-dev-sched-label" style={{ display: 'block', marginBottom: 8 }}>Alert Schedule</span>
                    <div className="adm-push-seg" style={{ margin: 0, width: '100%' }}>
                      {[['follow_global', '🌐 Follow Global'], ['always', '🟢 Always On'], ['window', '⏰ Time Window']]
                        .filter(([val]) => val !== 'follow_global' || !isIndep)
                        .map(([val, label]) => (
                          <button key={val}
                            className={`adm-push-seg-btn${e.alertMode === val ? ' active' : ''}`}
                            style={{ flex: 1 }}
                            onClick={() => updateEdit(ch, { alertMode: val })}>
                            {label}
                          </button>
                        ))}
                    </div>
                    {e.alertMode === 'window' && (
                      <div className="adm-dev-sched-times" style={{ marginTop: 10 }}>
                        <input type="time" className="adm-input"
                          value={e.alertStart}
                          onChange={ev => updateEdit(ch, { alertStart: ev.target.value })} />
                        <span className="adm-dev-sched-arrow">→</span>
                        <input type="time" className="adm-input"
                          value={e.alertEnd}
                          onChange={ev => updateEdit(ch, { alertEnd: ev.target.value })} />
                      </div>
                    )}
                  </div>

                  {/* Fixed Schedule */}
                  <div>
                    <span className="adm-dev-sched-label" style={{ display: 'block', marginBottom: 8 }}>Fixed Schedule</span>
                    <div className="adm-push-seg" style={{ margin: 0, width: '100%' }}>
                      {[['off', 'Off'], ['on', '⏱ Scheduled']].map(([val, label]) => (
                        <button key={val}
                          className={`adm-push-seg-btn${e.fixedMode === val ? ' active' : ''}`}
                          style={{ flex: 1 }}
                          onClick={() => updateEdit(ch, { fixedMode: val })}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {e.fixedMode === 'on' && (
                      <div className="adm-dev-sched-times" style={{ marginTop: 10 }}>
                        <input type="time" className="adm-input"
                          value={e.fixedStart}
                          onChange={ev => updateEdit(ch, { fixedStart: ev.target.value })} />
                        <span className="adm-dev-sched-arrow">→</span>
                        <input type="time" className="adm-input"
                          value={e.fixedEnd}
                          onChange={ev => updateEdit(ch, { fixedEnd: ev.target.value })} />
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          };

          const regularChs = channels.filter(ch => ch.on_alert && !['motor','strobe'].includes(ch.type));
          const indepChs   = channels.filter(ch => ch.on_alert &&  ['motor','strobe'].includes(ch.type));
          return (
            <div style={{ padding: '0 24px 24px' }}>
              {regularChs.length > 0 && <>
                <label className="adm-label" style={{ marginBottom: 12, display: 'block' }}>Per-Device Schedule</label>
                <div className="adm-dev-sched-grid">
                  {regularChs.map(renderCard)}
                </div>
              </>}
              {indepChs.length > 0 && <>
                <label className="adm-label" style={{ marginTop: 20, marginBottom: 4, display: 'block' }}>⚙️ Motor & Strobe Schedule</label>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>These devices are independent of the global schedule.</p>
                <div className="adm-dev-sched-grid">
                  {indepChs.map(renderCard)}
                </div>
              </>}
            </div>
          );
        })()}

    </div>
  );
}

function PushScheduleSection() {
  const [mode,    setMode]    = useState('scheduled');
  const [start,   setStart]   = useState('00:30');
  const [end,     setEnd]     = useState('05:00');
  const [saved,   setSaved]   = useState(false);
  const [err,     setErr]     = useState(false);
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    apiFetch('/settings/push-schedule')
      .then(r => r.json())
      .then(d => {
        setMode(d.mode ?? 'scheduled');
        setStart(d.start ?? '00:30');
        setEnd(d.end ?? '05:00');
      })
      .catch(() => {});
    apiFetch('/ui/cameras')
      .then(r => r.json())
      .then(d => setCameras(d.cameras || []))
      .catch(() => {});
  }, []);

  const toggleCamPush = async (cam) => {
    const current = cam.alert_actions?.push !== false;
    const updated = { ...cam, alert_actions: { ...(cam.alert_actions || {}), push: !current } };
    setCameras(cs => cs.map(c => c.idx === cam.idx ? updated : c));
    try {
      await apiFetch(`/cameras/${cam.idx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alert_actions: updated.alert_actions }),
      });
    } catch {
      setCameras(cs => cs.map(c => c.idx === cam.idx ? cam : c));
    }
  };

  const save = async () => {
    try {
      await apiFetch('/settings/push-schedule', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, start, end }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setErr(true);
      setTimeout(() => setErr(false), 2000);
    }
  };

  const modeDesc = {
    off:       'Push notifications are off. SOS alerts still come through.',
    '24x7':    'Push alerts sent 24/7 — no time restriction.',
    scheduled: `Push alerts sent only ${start} – ${end} (device time). SOS always comes through.`,
  };

  return (
    <div className="adm-section" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <div className="adm-section-title" style={{ margin: 0 }}>Push Notifications</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && <span style={{ color: 'var(--success, #22c55e)', fontSize: 13 }}>✓ Saved</span>}
          {err   && <span style={{ color: 'var(--danger, #ef4444)',  fontSize: 13 }}>Save failed</span>}
          <button className="adm-btn primary" onClick={save}>Save</button>
        </div>
      </div>
      <div className="adm-section-body">
        <label className="adm-label" style={{ marginBottom: 8, display: 'block' }}>Push Schedule</label>
        <div className="adm-push-seg">
          {[['off', 'Off'], ['scheduled', 'Scheduled'], ['24x7', '24×7']].map(([val, label]) => (
            <button key={val}
              className={`adm-push-seg-btn${mode === val ? ' active' : ''}`}
              onClick={() => setMode(val)}>
              {label}
            </button>
          ))}
        </div>
        {mode === 'scheduled' && (
          <>
            <div className="adm-field-row" style={{ marginTop: 12 }}>
              <label className="adm-label">Active from</label>
              <input type="time" className="adm-input" style={{ width: 130 }}
                value={start} onChange={e => setStart(e.target.value)} />
            </div>
            <div className="adm-field-row">
              <label className="adm-label">Active until</label>
              <input type="time" className="adm-input" style={{ width: 130 }}
                value={end} onChange={e => setEnd(e.target.value)} />
            </div>
          </>
        )}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{modeDesc[mode]}</p>
        {cameras.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <label className="adm-label" style={{ marginBottom: 8, display: 'block' }}>Per-Camera Push</label>
            {cameras.map(cam => {
              const enabled = cam.alert_actions?.push !== false;
              return (
                <div key={cam.idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 14 }}>{cam.name}</span>
                  <button
                    onClick={() => toggleCamPush(cam)}
                    className={`adm-btn${enabled ? ' primary' : ''}`}
                    style={{ padding: '4px 16px', fontSize: 12, minWidth: 52 }}>
                    {enabled ? 'On' : 'Off'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const CAP_OPTIONS = [
  { value: 100,  label: '100 alerts'  },
  { value: 250,  label: '250 alerts'  },
  { value: 500,  label: '500 alerts'  },
  { value: 1000, label: '1000 alerts' },
];

const fmtBytes = (n) => {
  if (!n) return '0 MB';
  const mb = n / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

// On-device offline alert cache — "Sync with phone". Lets recent alerts be
// reviewed (Local Mode, or "View Saved Alerts" on the login page) even when the
// server is unreachable. No PIN — encrypted under a device key, see
// offlineAlertStore.js.
function OnDeviceAlertsSection() {
  const supported = offline.isOfflineCacheSupported();
  const [enabled,  setEnabled]  = useState(false);
  const [cap,      setCap]      = useState(500);
  const [inited,   setInited]   = useState(false);
  const [stats,    setStats]    = useState({ count: 0, bytes: 0, lastSyncedAt: 0 });
  const [err,      setErr]      = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [msg,      setMsg]      = useState('');

  const refresh = useCallback(async () => {
    const cfg = getOfflineSyncConfig();
    setEnabled(cfg.enabled);
    setCap(cfg.cap);
    setInited(await offline.isCacheInitialized());
    setStats(await offline.stats());
  }, []);

  useEffect(() => { if (supported) refresh(); }, [supported, refresh]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  // Turn the master toggle on/off.
  const onToggle = async (next) => {
    setErr('');
    if (next) {
      try {
        await offline.init();
        if (inited) await offline.setCap(cap);
        setOfflineSyncConfig({ enabled: true, cap });
        flash('On-device sync enabled');
        await refresh();
      } catch (e) {
        setErr(e.message || 'Something went wrong');
      }
    } else {
      // Disabling clears the cache — the user expects alerts to stop living here.
      setConfirmClear(true);
    }
  };

  const doClear = async () => {
    await offline.clearAll();
    setOfflineSyncConfig({ enabled: false });
    setConfirmClear(false);
    await refresh();
    flash('Cached alerts cleared');
  };

  const onCapChange = async (v) => {
    setCap(v);
    setOfflineSyncConfig({ cap: v });
    if (inited) { await offline.setCap(v); await refresh(); }
  };

  return (
    <div className="adm-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <div className="adm-section-title" style={{ margin: 0 }}>On-device alerts (Sync with phone)</div>
        {msg && <span style={{ color: 'var(--success, #22c55e)', fontSize: 13 }}>✓ {msg}</span>}
      </div>
      <div className="adm-section-body">
        {!supported ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Saving alerts to this device needs a secure (https) connection, so it isn’t
            available here. Open the app over https to keep alerts offline.
          </p>
        ) : (
          <>
            <div className="adm-field-row">
              <label className="adm-label">Keep recent alerts on this device</label>
              <label className="adm-toggle">
                <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} />
                <span className="adm-toggle-track" />
              </label>
            </div>
            {err && <div style={{ color: 'var(--danger, #ef4444)', fontSize: 13, marginBottom: 8 }}>{err}</div>}

            <div className="adm-field-row" style={{ opacity: enabled ? 1 : 0.4, pointerEvents: enabled ? 'auto' : 'none' }}>
              <label className="adm-label">Keep newest</label>
              <select className="adm-input" style={{ width: 180 }}
                value={cap} onChange={e => onCapChange(Number(e.target.value))}>
                {CAP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {enabled && inited && (
              <>
                <div className="adm-field-row">
                  <label className="adm-label">Saved alerts</label>
                  <button className="adm-btn danger" onClick={() => setConfirmClear(true)}>Clear cached alerts</button>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                  New alerts you view are being saved to this device.
                  {' '}Using {fmtBytes(stats.bytes)} · {stats.count} alert{stats.count !== 1 ? 's' : ''}
                  {stats.lastSyncedAt ? ` · last saved ${fmtISO(new Date(stats.lastSyncedAt).toISOString())}` : ''}.
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Saved alerts open from Local Mode even when the server is unreachable — anyone who can open
                  this app on this device can see them.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {confirmClear && (
        <ConfirmDialog
          title="Clear cached alerts?"
          message="This turns off on-device sync and permanently deletes the alerts saved on this device. Alerts on the server are not affected."
          confirmLabel="Clear"
          onConfirm={doClear}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </div>
  );
}

function SettingsTab() {
  const [enabled,    setEnabled]    = useState(DEFAULTS.enabled);
  const [interval,   setInterval]   = useState(DEFAULTS.intervalSecs);
  const [idleReload, setIdleReload] = useState(DEFAULTS.idleReloadSecs);
  const [saved,      setSaved]      = useState(false);
  const [saveErr,    setSaveErr]    = useState(false);

  useEffect(() => {
    syncAlertPollFromServer(apiFetch).then(() => {
      apiFetch('/settings/alert-poll').then(r => r.json()).then(d => {
        setEnabled(d.enabled);
        setInterval(d.intervalSecs);
        setIdleReload(d.idleReloadSecs);
      }).catch(() => {});
    });
  }, []);

  const save = async () => {
    try {
      await saveAlertPollToServer(apiFetch, { enabled, intervalSecs: interval, idleReloadSecs: idleReload });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveErr(true);
      setTimeout(() => setSaveErr(false), 2000);
    }
  };

  const dim = { opacity: enabled ? 1 : 0.4, pointerEvents: enabled ? 'auto' : 'none' };

  return (
    <>
    <ServerControlsSection />
    <PushScheduleSection />
    <DeviceScheduleSection />
    <GlobalSettingsSection />
    <div className="adm-section" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 0' }}>
        <div className="adm-section-title" style={{ margin: 0 }}>Alert Polling</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved    && <span style={{ color: 'var(--success, #22c55e)', fontSize: 13 }}>✓ Saved</span>}
          {saveErr  && <span style={{ color: 'var(--danger, #ef4444)',  fontSize: 13 }}>Save failed</span>}
          <button className="adm-btn primary" onClick={save}>Save</button>
        </div>
      </div>
      <div className="adm-section-body">
        <div className="adm-field-row">
          <label className="adm-label">Auto-refresh alerts</label>
          <label className="adm-toggle">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <span className="adm-toggle-track" />
          </label>
        </div>
        <div className="adm-field-row" style={dim}>
          <label className="adm-label">Poll interval</label>
          <select className="adm-input" style={{ width: 180 }}
            value={interval} onChange={e => setInterval(Number(e.target.value))}>
            {INTERVAL_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="adm-field-row" style={dim}>
          <label className="adm-label">Auto-reload when idle</label>
          <select className="adm-input" style={{ width: 180 }}
            value={idleReload} onChange={e => setIdleReload(Number(e.target.value))}>
            {IDLE_RELOAD_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          When new alerts arrive the list shows a banner. If you have been scrolling,
          the list auto-reloads only after the idle timeout. "Never" means you must tap the banner.
          Changes apply on next page load.
        </p>
      </div>
    </div>
    <OnDeviceAlertsSection />
    </>
  );
}

function MissedSnapshot({ entryId, onOpen, style, className }) {
  const [src,    setSrc]    = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    setSrc(null);
    setHidden(false);
    apiImageUrl(`/admin/missed-alerts/${entryId}/snapshot`).then(setSrc);
  }, [entryId]);
  // The strip renders under the Full View image and returns null when the record
  // has none, so older entries look exactly as they did.
  const strip = (
    <EvidenceStrip
      countPath={`/admin/missed-alerts/${entryId}/frames`}
      framePath={i => `/admin/missed-alerts/${entryId}/frame/${i}`}
      onOpen={onOpen}
      label="Around the block"
    />
  );
  if (!src || hidden) return (
    <div className={className || ''} style={{ ...style }}>
      <div style={{ background: 'var(--surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80 }}>📷</div>
      {strip}
    </div>
  );
  return (
    <div className={className || ''} style={style}>
      <img
        src={src}
        alt="snapshot"
        style={{ width: '100%', cursor: 'zoom-in' }}
        onClick={() => onOpen(src)}
        onError={() => setHidden(true)}
      />
      {strip}
    </div>
  );
}

// Kept (not removed) while Telegram is hidden from the UI — see the commented
// 'CC Alerts' tab entry. Restore that entry to bring it back.
// eslint-disable-next-line no-unused-vars
function CcAlertsTab() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [toggling,  setToggling]  = useState(false);
  const [grpId,     setGrpId]     = useState('');
  const [grpTitle,  setGrpTitle]  = useState('');
  const [viewerId,  setViewerId]  = useState('');
  const [addErr,    setAddErr]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/admin/cc-alerts');
      const d = await r.json();
      setData({ enabled: d.enabled ?? false, groups: d.groups ?? [], viewers: d.viewers ?? [] });
    } catch { setData(null); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setToggling(true);
    const r = await apiFetch('/admin/cc-alerts/toggle', { method: 'POST' });
    const d = await r.json();
    setData(prev => ({ ...prev, enabled: d.enabled }));
    setToggling(false);
  };

  const removeGroup = async idx => {
    await apiFetch(`/admin/cc-alerts/groups/${idx}`, { method: 'DELETE' });
    setData(prev => ({ ...prev, groups: prev.groups.filter((_, i) => i !== idx) }));
  };

  const removeViewer = async uid => {
    await apiFetch(`/admin/cc-alerts/viewers/${uid}`, { method: 'DELETE' });
    setData(prev => ({ ...prev, viewers: prev.viewers.filter(v => v !== uid) }));
  };

  const addGroup = async () => {
    setAddErr('');
    const id = parseInt(grpId);
    if (!id) { setAddErr('Valid Chat ID required (negative for groups, e.g. -1001234567890)'); return; }
    const res = await apiFetch('/admin/cc-alerts/groups', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat_id: id, title: grpTitle.trim() || String(id) }),
    });
    if (!res.ok) { const d = await res.json(); setAddErr(d.detail || 'Failed'); return; }
    setData(prev => ({ ...prev, groups: [...(prev.groups||[]), { chat_id: id, title: grpTitle.trim() || String(id) }] }));
    setGrpId(''); setGrpTitle('');
  };

  const addViewer = async () => {
    setAddErr('');
    const id = parseInt(viewerId);
    if (!id) { setAddErr('Valid Telegram User ID required'); return; }
    const res = await apiFetch('/admin/cc-alerts/viewers', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: id }),
    });
    if (!res.ok) { const d = await res.json(); setAddErr(d.detail || 'Failed'); return; }
    setData(prev => ({ ...prev, viewers: [...(prev.viewers||[]), id] }));
    setViewerId('');
  };

  if (loading) return <div className="adm-section" style={{ padding: 20, color: 'var(--text-muted)' }}>Loading…</div>;
  if (!data)   return <div className="adm-section" style={{ padding: 20, color: 'var(--text-muted)' }}>Failed to load CC Alerts config.</div>;

  return (
    <div className="adm-section" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>📋 CC Alerts</h3>
        <button className={`adm-btn${data.enabled ? '' : ' secondary'}`} onClick={toggle} disabled={toggling}>
          {toggling ? 'Saving…' : data.enabled ? '🔔 Alerts ON — Disable' : '🔕 Alerts OFF — Enable'}
        </button>
        <button className="adm-btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
        Linked groups and contacts receive forwarded alerts (read-only). Panic always notifies regardless of toggle.<br/>
        <strong>To add groups or contacts:</strong> use the Telegram bot → CC Alerts menu.
      </p>

      {addErr && <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{addErr}</p>}

      <div style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>CC Groups ({(data.groups||[]).length})</div>
        {(data.groups||[]).map((g, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, flex: 1 }}>{g.title || `Group ${g.chat_id}`}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{g.chat_id}</span>
            <button className="adm-btn danger" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => removeGroup(i)}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <input className="adm-input" placeholder="Chat ID (e.g. -1001234567890)" value={grpId}
            onChange={e => setGrpId(e.target.value)} style={{ flex: 2, minWidth: 200, fontSize: 13 }} />
          <input className="adm-input" placeholder="Name / Title" value={grpTitle}
            onChange={e => setGrpTitle(e.target.value)} style={{ flex: 2, minWidth: 140, fontSize: 13 }} />
          <button className="adm-btn primary" onClick={addGroup} disabled={!grpId}>+ Add Group</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Telegram group Chat ID (negative number). Find it by adding @userinfobot to the group.
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Individual Contacts ({(data.viewers||[]).length})</div>
        {(data.viewers||[]).map(uid => (
          <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, flex: 1, fontFamily: 'monospace' }}>{uid}</span>
            <button className="adm-btn danger" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => removeViewer(uid)}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input className="adm-input" placeholder="Telegram User ID (e.g. 7965790454)" value={viewerId}
            onChange={e => setViewerId(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
          <button className="adm-btn primary" onClick={addViewer} disabled={!viewerId}>+ Add Contact</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Find user ID by messaging @userinfobot on Telegram.
        </div>
      </div>
    </div>
  );
}

const MISSED_PAGE_SIZE = 20;

const EV_SECTIONS = [
  { label: 'Rule', fields: [
    ['ev_rule_trigger',      'Triggered by'],
    ['ev_rule_block_reason', 'Blocked by'],
  ]},
  { label: 'Detection', fields: [
    ['ev_yolo_conf_best', 'YOLO conf best'],
    ['ev_yolo_conf_last', 'YOLO conf last'],
  ]},
  { label: 'Face', fields: [
    ['ev_face_large',     'Face ≥40px'],
    ['ev_face_small',     'Face ≥10px'],
    ['ev_face_w',         'Face w'],
    ['ev_face_h',         'Face h'],
    ['ev_face_det_score', 'Det score'],
    ['ev_face_quality',   'Quality'],
    ['ev_face_frames',    'Frames'],
  ]},
  { label: 'Skeleton', fields: [
    ['ev_skeleton',             'Skeleton'],
    ['ev_skeleton_frame_count', 'Skel frames'],
    ['ev_pose_frame_count',     'Pose frames'],
    ['ev_kp_conf_count',        'KP count'],
    ['ev_kp_conf_mean',         'KP mean'],
    ['ev_kp_shoulder_conf',     'Shoulder'],
    ['ev_kp_elbow_conf',        'Elbow'],
    ['ev_kp_hip_conf',          'Hip'],
    ['ev_kp_knee_conf',         'Knee'],
    ['ev_kp_ankle_conf',        'Ankle'],
    ['ev_segment_score',        'Segment'],
    ['ev_pose_confirmed_track', 'RTMPose ran'],
  ]},
  { label: 'CLIP', fields: [
    ['ev_clip_human',       'Human'],
    ['ev_clip_score',       'Score'],
    ['ev_clip_source',      'Source'],
  ]},
  { label: 'Clothing', fields: [
    ['ev_clothing', 'Clothing'],
  ]},
  { label: 'Motion', fields: [
    ['ev_track_path_px',  'Track path (px)'],
    ['ev_path_px',        'Path (px)'],
    ['ev_net_px',         'Net (px)'],
    ['ev_net_ratio',      'Net ratio'],
    ['ev_fast_track',     'Fast-track'],
  ]},
];

function fmtEvVal(v) {
  if (v === true)  return <span style={{ color: '#22c55e' }}>✅</span>;
  if (v === false) return <span style={{ color: '#ef4444' }}>❌</span>;
  if (v === null || v === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (typeof v === 'number') return <span>{Number.isInteger(v) ? v : v.toFixed(3)}</span>;
  return <span>{String(v)}</span>;
}

function EvTiles({ ev }) {
  if (!ev) return null;
  const knownKeys = new Set(EV_SECTIONS.flatMap(s => s.fields.map(f => f[0])));
  const extraKeys = Object.keys(ev).filter(k => !knownKeys.has(k));
  const tileStyle = { background: 'var(--surface-alt, #1a1a2e)', borderRadius: 6, padding: '8px 10px' };
  return (
    <>
      {EV_SECTIONS.map(sec => {
        const rows = sec.fields.filter(([k]) => k in ev);
        if (!rows.length) return null;
        return (
          <div key={sec.label} style={tileStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              {sec.label}
            </div>
            {rows.map(([k, label]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                {fmtEvVal(ev[k])}
              </div>
            ))}
          </div>
        );
      })}
      {extraKeys.length > 0 && (
        <div style={tileStyle}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Other</div>
          {extraKeys.map(k => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
              <span style={{ color: 'var(--text-muted)' }}>{k}</span>
              {fmtEvVal(ev[k])}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function downloadMissedEntry(entryId, camName, blockPath, ts, verdict, ev) {
  const data = { entry_id: entryId, cam_name: camName, block_path: blockPath, ts, verdict, ev };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `missed_${entryId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function MobilePickerField({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value) || options[0];
  return (
    <>
      <button className="mob-field" onClick={() => setOpen(true)}>
        <span className="mob-field-text">
          <span className="mob-field-label">{label}</span>
          <span className="mob-field-value">{selected?.label}</span>
        </span>
        <span className="mob-field-chevron">⌄</span>
      </button>
      {open && (
        <div className="dash-sheet-overlay" style={{ zIndex: 9600 }} onClick={() => setOpen(false)}>
          <div className="dash-sheet" onClick={e => e.stopPropagation()}>
            <div className="dash-sheet-handle" />
            <h3>{label}</h3>
            <div className="mob-option-list">
              {options.map(o => (
                <button key={String(o.value)} className={`mob-option-row${o.value === value ? ' active' : ''}`}
                  onClick={() => { onChange(o.value); setOpen(false); }}>
                  <span>{o.label}</span>
                  {o.value === value && <span className="mob-option-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function MissedAlertsTab() {
  const [entries,     setEntries]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [clearing,    setClearing]    = useState(false);
  const [visible,     setVisible]     = useState(MISSED_PAGE_SIZE);
  const [saving,      setSaving]      = useState(null);
  const [saved,       setSaved]       = useState(null);
  const [lightbox,    setLightbox]    = useState(null);
  const [evModal,     setEvModal]     = useState(null); // { entry_id, cam_name, ts, ev }
  const [confirmClear, setConfirmClear] = useState(false);
  const [filterCam,     setFilterCam]     = useState('all');
  const [filterVerdict, setFilterVerdict] = useState('all');
  const [filterBlock,   setFilterBlock]   = useState('all');
  const _localDT = (d) => {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const _todayStart = () => { const d = new Date(); d.setHours(0,0,0,0);   return _localDT(d); };
  const _todayEnd   = () => { const d = new Date(); d.setHours(23,59,0,0); return _localDT(d); };
  const [filterFrom,    setFilterFrom]    = useState(_todayStart);
  const [filterTo,      setFilterTo]      = useState(_todayEnd);
  const [filterEntryId, setFilterEntryId] = useState('');
  const [showFilters,   setShowFilters]   = useState(false);
  const [labelJob,      setLabelJob]      = useState(null);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/admin/missed-alerts?n=500');
      const d = await r.json();
      setEntries(d.entries || []);
      setVisible(MISSED_PAGE_SIZE);
    } catch { setEntries([]); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Ask for a suggestion on every record currently filtered to. Runs on the box in
  // the background — each record is a call to an external service, so a few hundred
  // is minutes — and the list is reloaded at the end so the suggestions appear.
  //
  // It only ever answers "is a person visible in this picture". It never sets a
  // verdict and never preselects a button: the reviewer still decides, they just
  // confirm rather than judge each image cold.
  // Takes the ids rather than closing over `filtered`, which is declared further
  // down — a dependency on it here is read during render, before it exists, and
  // throws a TDZ ReferenceError that no amount of JSX checking would catch.
  const startLabelJob = useCallback(async (entryIds) => {
    try {
      const res = await apiFetch('/admin/missed-alerts/label', {
        method: 'POST', json: { entry_ids: entryIds },
      });
      const d = await res.json();
      if (!d.started) {
        alert(d.reason === 'not_configured'
          ? 'Label suggestions are not set up on this site yet.'
          : d.reason === 'already_running'
            ? 'A labelling run is already going.'
            : 'Could not start labelling.');
        return;
      }
      setLabelJob(d);
      const poll = setInterval(async () => {
        try {
          const s = await (await apiFetch('/admin/missed-alerts/label')).json();
          setLabelJob(s);
          if (!s.running) { clearInterval(poll); setLabelJob(null); load(); }
        } catch { clearInterval(poll); setLabelJob(null); }
      }, 2000);
    } catch { alert('Could not start labelling.'); }
  }, [load]);

  const sentinelCb = useCallback(node => {
    if (!node) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(v => v + MISSED_PAGE_SIZE);
    }, { threshold: 0.1 });
    obs.observe(node);
  }, []);

  const clearAll = async () => {
    setClearing(true);
    await apiFetch('/admin/missed-alerts', { method: 'DELETE' });
    setClearing(false);
    setEntries([]);
    setVisible(MISSED_PAGE_SIZE);
  };

  const setVerdict = async (entryId, verdict) => {
    setSaving(entryId);
    setSaved(null);
    try {
      const res = await apiFetch(`/admin/missed-alerts/${entryId}/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict }),
      });
      if (!res.ok) throw new Error();
      setEntries(prev => prev.map(e =>
        e.entry_id === entryId ? { ...e, verdict } : e
      ));
      setSaved(entryId);
      setTimeout(() => setSaved(null), 2000);
    } catch {
      setSaved(`err_${entryId}`);
      setTimeout(() => setSaved(null), 2000);
    } finally {
      setSaving(null);
    }
  };

  const fmtTs = ts => {
    if (!ts) return '—';
    const d = new Date(ts * 1000);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const verdictBadge = v => {
    const base = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 };
    if (!v) return <span style={{ ...base, background: 'var(--surface-alt)', color: 'var(--text-muted)' }}>Unlabelled</span>;
    if (v === 'confirmed_missed') return <span style={{ ...base, background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>✗ False</span>;
    return <span style={{ ...base, background: 'rgba(34,197,94,0.12)', color: '#22c55e' }}>✓ Correct</span>;
  };

  const unlabelled = entries.filter(e => !e.verdict).length;

  const camOptions   = ['all', ...Array.from(new Set(entries.map(e => e.cam_name))).sort()];
  const blockOptions = ['all', ...Array.from(new Set(entries.map(e => e.block_path).filter(Boolean))).sort()];

  const filtered = entries.filter(e => {
    if (filterCam     !== 'all' && e.cam_name   !== filterCam)     return false;
    if (filterBlock   !== 'all' && e.block_path !== filterBlock)   return false;
    if (filterVerdict === 'unlabelled'       && e.verdict !== null)                return false;
    if (filterVerdict === 'confirmed_missed' && e.verdict !== 'confirmed_missed')  return false;
    if (filterVerdict === 'correct_block'    && e.verdict !== 'correct_block')     return false;
    if (filterFrom    && e.ts * 1000 < new Date(filterFrom).getTime())  return false;
    if (filterTo      && e.ts * 1000 > new Date(filterTo).getTime())    return false;
    if (filterEntryId && !e.entry_id.toLowerCase().includes(filterEntryId.toLowerCase())) return false;
    return true;
  });

  const visibleEntries = filtered.slice(0, visible);
  // Index into `filtered`, not `visibleEntries` — Full View walks the whole filtered
  // set so labelling never stops at the lazy-load boundary.
  const [fullViewIdx, setFullViewIdx] = useState(null);
  const hasMore        = visible < filtered.length;



  const doDownload = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `missed_alerts_${filtered.length}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  // Snapshots for the same filtered selection, zipped server-side. The JSON on its
  // own is only half a training set — the numbers describe a box nobody can see.
  // Zipping on the server rather than in the browser avoids pulling a zip library
  // in for this, and lets the cap be enforced where the files actually are.
  const IMG_EXPORT_MAX = 200;
  const [imgBusy, setImgBusy] = useState(false);
  const doDownloadImages = async () => {
    if (!filtered.length || imgBusy) return;
    if (filtered.length > IMG_EXPORT_MAX) {
      alert(`${filtered.length} records selected — images are capped at ${IMG_EXPORT_MAX} per download.\n\n`
          + `Narrow the date range, camera or verdict filter and try again.`);
      return;
    }
    setImgBusy(true);
    try {
      const r = await apiFetch('/admin/missed-alerts/export-images', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: filtered.map(e => e.entry_id) }),
      });
      if (!r.ok) {
        // The server carries the reason (cap exceeded, nothing on disk); show it
        // rather than a generic failure, since both are the user's to act on.
        let msg = `Image download failed (${r.status}).`;
        try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch { /* keep default */ }
        alert(msg);
        return;
      }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `missed_images_${filtered.length}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      alert('Image download failed — check the connection and try again.');
    } finally { setImgBusy(false); }
  };

  const isMobile = useIsMobile();
  const hasActiveFilter = filterCam !== 'all' || filterVerdict !== 'all' || filterBlock !== 'all' || filterFrom !== _todayStart() || filterTo !== _todayEnd() || filterEntryId;
  const resetFilters = () => { setFilterCam('all'); setFilterVerdict('all'); setFilterBlock('all'); setFilterFrom(_todayStart()); setFilterTo(_todayEnd()); setFilterEntryId(''); setVisible(MISSED_PAGE_SIZE); };

  return (
    <div className="adm-section">

      {/* Toolbar */}
      <div className="page-toolbar missed-filter-row">
        <button className={`tb-btn${showFilters ? ' active' : ''}`}
          onClick={() => setShowFilters(o => !o)} title="Filters">
          🔍 Filters {hasActiveFilter ? '●' : ''}
        </button>
        {hasActiveFilter && (
          <button className="tb-ghost-btn" onClick={resetFilters} style={{ color: 'var(--primary)' }}>✕ Reset</button>
        )}
        <button className="tb-btn" disabled={!filtered.length}
          onClick={() => setFullViewIdx(Math.max(0, filtered.findIndex(e => !e.verdict)))}
          title="Review these one at a time, with the rule trace">
          ⛶ Review
        </button>
        <button className="tb-btn" disabled={!filtered.length || labelJob?.running}
          onClick={() => startLabelJob(filtered.map(e => e.entry_id))}
          title="Ask for a suggestion on each of these records. It only ever says whether a person is visible — you still decide the verdict.">
          {labelJob?.running
            ? `✨ ${labelJob.done}/${labelJob.total}…`
            : `✨ Suggest labels (${filtered.length})`}
        </button>
        <span className="tb-sep" />
        <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length}{filtered.length !== entries.length ? `/${entries.length}` : ''} entries
          {unlabelled > 0 ? <span> · {unlabelled} unlabelled</span> : ''}
        </span>
        <span className="tb-sep" />
        <button className="tb-ghost-btn" onClick={load} disabled={loading}>↺ Refresh</button>
        <span className="tb-sep" />
        <button className="tb-ghost-btn" onClick={doDownload} disabled={loading || filtered.length === 0}>⬇ Download ({filtered.length})</button>
        <button className="tb-ghost-btn" onClick={doDownloadImages}
                disabled={loading || imgBusy || filtered.length === 0}
                title={filtered.length > IMG_EXPORT_MAX
                       ? `Capped at ${IMG_EXPORT_MAX} — narrow the filter`
                       : 'Download the snapshots for this selection as a zip'}>
          {imgBusy ? '⏳ Zipping…' : `⬇ Images (${filtered.length})`}
        </button>
        <span className="tb-sep" />
        <button className="tb-ghost-btn" style={{ color: '#e74c3c' }}
          onClick={() => setConfirmClear(true)} disabled={clearing || entries.length === 0}>
          🗑 {clearing ? '…' : 'Delete All'}
        </button>
      </div>

      {/* Desktop: collapsible filter row */}
      {!isMobile && showFilters && (
        <div className="page-toolbar missed-filter-row" style={{ borderTop: '1px solid var(--border)', background: 'var(--surface-alt, rgba(0,0,0,0.02))' }}>
          <input className="tb-input" placeholder="Search Entry ID…" value={filterEntryId}
            style={{ minWidth: 160 }}
            onChange={e => { setFilterEntryId(e.target.value); setVisible(MISSED_PAGE_SIZE); }} />
          <span className="tb-sep" />
          <select className="tb-input" value={filterCam} onChange={e => { setFilterCam(e.target.value); setVisible(MISSED_PAGE_SIZE); }}>
            {camOptions.map(c => <option key={c} value={c}>{c === 'all' ? 'All Cameras' : c}</option>)}
          </select>
          <select className="tb-input" value={filterVerdict} onChange={e => { setFilterVerdict(e.target.value); setVisible(MISSED_PAGE_SIZE); }}>
            <option value="all">All Verdicts</option>
            <option value="unlabelled">Unlabelled</option>
            <option value="confirmed_missed">✗ False</option>
            <option value="correct_block">✓ Correct</option>
          </select>
          <select className="tb-input" value={filterBlock} onChange={e => { setFilterBlock(e.target.value); setVisible(MISSED_PAGE_SIZE); }}>
            {blockOptions.map(b => <option key={b} value={b}>{b === 'all' ? 'All Blocks' : b}</option>)}
          </select>
          <span className="tb-sep" />
          <span className="tb-label">From</span>
          <input type="datetime-local" className="tb-input" value={filterFrom}
            onChange={e => { setFilterFrom(e.target.value); setVisible(MISSED_PAGE_SIZE); }} />
          <span className="tb-label">→</span>
          <input type="datetime-local" className="tb-input" value={filterTo}
            onChange={e => { setFilterTo(e.target.value); setVisible(MISSED_PAGE_SIZE); }} />
        </div>
      )}

      {/* Mobile: bottom sheet filters (same as AlertsPage) */}
      {isMobile && showFilters && (
        <div className="dash-sheet-overlay" onClick={() => setShowFilters(false)}>
          <div className="dash-sheet" onClick={e => e.stopPropagation()}>
            <div className="dash-sheet-handle" />
            <h3>Filters</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '14px 0 18px' }}>
              <label className="mob-date-field" style={{ width: '100%' }}>
                <span className="mob-field-label">Search</span>
                <input className="mob-date-input" placeholder="Entry ID…" value={filterEntryId}
                  onChange={e => { setFilterEntryId(e.target.value); setVisible(MISSED_PAGE_SIZE); }} />
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <label className="mob-date-field">
                  <span className="mob-field-label">From</span>
                  <input className="mob-date-input" type="date"
                    value={filterFrom.slice(0, 10)}
                    onChange={e => { const d = new Date(e.target.value); d.setHours(0,0,0,0); setFilterFrom(_localDT(d)); setVisible(MISSED_PAGE_SIZE); }} />
                </label>
                <label className="mob-date-field">
                  <span className="mob-field-label">To</span>
                  <input className="mob-date-input" type="date"
                    value={filterTo.slice(0, 10)}
                    onChange={e => { const d = new Date(e.target.value); d.setHours(23,59,0,0); setFilterTo(_localDT(d)); setVisible(MISSED_PAGE_SIZE); }} />
                </label>
              </div>
              <MobilePickerField label="Camera" value={filterCam}
                options={camOptions.map(c => ({ value: c, label: c === 'all' ? 'All Cameras' : c }))}
                onChange={v => { setFilterCam(v); setVisible(MISSED_PAGE_SIZE); }} />
              <MobilePickerField label="Verdict" value={filterVerdict}
                options={[
                  { value: 'all',             label: 'All Verdicts' },
                  { value: 'unlabelled',       label: 'Unlabelled'  },
                  { value: 'confirmed_missed', label: '✗ False'    },
                  { value: 'correct_block',    label: '✓ Correct'   },
                ]}
                onChange={v => { setFilterVerdict(v); setVisible(MISSED_PAGE_SIZE); }} />
              <MobilePickerField label="Block" value={filterBlock}
                options={blockOptions.map(b => ({ value: b, label: b === 'all' ? 'All Blocks' : b }))}
                onChange={v => { setFilterBlock(v); setVisible(MISSED_PAGE_SIZE); }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              {hasActiveFilter && (
                <button className="dash-sheet-close" style={{ flex: 1 }}
                  onClick={() => { resetFilters(); setShowFilters(false); }}>✕ Reset</button>
              )}
              <button className="dash-sheet-close"
                style={{ flex: 1, background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' }}
                onClick={() => setShowFilters(false)}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {fullViewIdx !== null && (
        <ReviewView
          entries={filtered}
          startIndex={fullViewIdx}
          fmtTs={fmtTs}
          onVerdict={setVerdict}
          onClose={() => setFullViewIdx(null)}
        />
      )}

      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {!loading && entries.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>No missed alert entries recorded yet.</p>
      )}

      <div className="pk-grid">
      {!loading && visibleEntries.map((e, idx) => (
        <div key={e.entry_id} className="alert-card">
          <div className="alert-card-num">#{idx + 1}</div>

          {/* Snapshot */}
          <MissedSnapshot
            entryId={e.entry_id}
            onOpen={setLightbox}
            style={{ width: '100%', display: 'block', objectFit: 'cover' }}
            className="alert-card-thumb"
          />

          {/* Body */}
          <div className="alert-card-body">
            <div className="alert-card-badges">
              {verdictBadge(e.verdict)}
              <span className="badge badge-cam">{e.cam_name}</span>
            </div>
            <p className="alert-card-time">{fmtTs(e.ts)}</p>
            <p style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-muted)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.block_path}</p>
          </div>

          {/* Actions */}
          <div className="alert-card-actions">
            <button
              className={`alert-action-btn${e.verdict === 'confirmed_missed' ? ' active' : ''}`}
              style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)' }}
              disabled={saving === e.entry_id}
              onClick={() => setVerdict(e.entry_id, 'confirmed_missed')}
            >✗ False</button>
            <button
              className={`alert-action-btn${e.verdict === 'correct_block' ? ' active' : ''}`}
              disabled={saving === e.entry_id}
              onClick={() => setVerdict(e.entry_id, 'correct_block')}
            >✓ Block</button>
            <button
              className="alert-action-btn download"
              title="Download JSON"
              onClick={() => downloadMissedEntry(e.entry_id, e.cam_name, e.block_path, e.ts, e.verdict, e.ev)}
            >⬇ JSON</button>
            <button
              className="alert-action-btn"
              title="View evidence detail"
              onClick={() => setEvModal({ entry_id: e.entry_id, cam_name: e.cam_name, ts: e.ts, verdict: e.verdict, ev: e.ev })}
            >📎</button>
          </div>
        </div>
      ))}
      </div>

      {!loading && hasMore && (
        <div ref={sentinelCb} style={{ textAlign: 'center', padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>
          Loading more…
        </div>
      )}

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 16, right: 20,
              background: 'none', border: 'none', color: '#fff',
              fontSize: 28, cursor: 'pointer', lineHeight: 1,
            }}
          >✕</button>
          <img
            src={lightbox}
            alt="full view"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '88vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', cursor: 'default' }}
          />
        </div>
      )}
      {evModal && (
        <div className="adm-overlay" onClick={() => setEvModal(null)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480, width: '92vw' }}>
            <div className="adm-modal-header">
              <span style={{ fontWeight: 600 }}>
                Evidence Detail — {evModal.cam_name} · {fmtTs(evModal.ts)}
              </span>
              <button className="adm-modal-close" onClick={() => setEvModal(null)}>✕</button>
            </div>
            <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)', wordBreak: 'break-all', padding: '4px 8px', background: 'var(--surface-alt)', borderRadius: 4 }}>
                {evModal.entry_id}
              </div>
              <EvTiles ev={evModal.ev} />
              {saved === evModal.entry_id && <span style={{ fontSize: 12, color: '#22c55e' }}>✓ Saved</span>}
              {saved === `err_${evModal.entry_id}` && <span style={{ fontSize: 12, color: '#ef4444' }}>✗ Failed</span>}
            </div>
          </div>
        </div>
      )}

      {confirmClear && (
        <ConfirmDialog
          title="Clear all missed alerts?"
          message="All missed alert entries will be permanently removed."
          confirmLabel="Clear All"
          confirmClass="danger"
          onConfirm={() => { setConfirmClear(false); clearAll(); }}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </div>
  );
}

function KnownSeenConfigTab() {
  const [vals,    setVals]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    apiFetch('/settings/advanced-config').then(r => r.json()).then(d => {
      setVals({
        known_face_threshold:       d.known_face_threshold       ?? 0.60,
        banned_face_threshold:      d.banned_face_threshold      ?? 0.60,
        face_match_margin:          d.face_match_margin          ?? 0.08,
        required_match_frames:      d.required_match_frames      ?? 2,
        soft_suppress_thresh_day:   d.soft_suppress_thresh_day   ?? 0.30,
        soft_suppress_thresh_night: d.soft_suppress_thresh_night ?? 0.20,
        soft_suppress_min_frames:   d.soft_suppress_min_frames   ?? 2,
        known_grace_secs:           d.known_grace_secs           ?? 30,
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setVals(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiFetch('/settings/advanced-config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          known_face_threshold:       parseFloat(vals.known_face_threshold),
          banned_face_threshold:      parseFloat(vals.banned_face_threshold),
          face_match_margin:          parseFloat(vals.face_match_margin),
          required_match_frames:      parseInt(vals.required_match_frames),
          soft_suppress_thresh_day:   parseFloat(vals.soft_suppress_thresh_day),
          soft_suppress_thresh_night: parseFloat(vals.soft_suppress_thresh_night),
          soft_suppress_min_frames:   parseInt(vals.soft_suppress_min_frames),
          known_grace_secs:           parseFloat(vals.known_grace_secs),
        }),
      });
      if (r.ok) setMsg({ ok: true, text: '✓ Saved — restart cameras to apply' });
      else setMsg({ ok: false, text: 'Failed to save' });
    } catch { setMsg({ ok: false, text: 'Error' }); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 3500); }
  };

  if (!vals) return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;

  const field = (label, key, opts = {}) => (
    <div className="adm-field">
      <label>{label}</label>
      <input className="adm-input" type="number"
        step={opts.step ?? 0.01} min={opts.min ?? 0} max={opts.max ?? 1}
        value={vals[key]} onChange={e => set(key, e.target.value)} />
      {opts.hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{opts.hint}</div>}
    </div>
  );

  const section = (title) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
      letterSpacing: '0.06em', marginTop: 20, marginBottom: 8, paddingBottom: 4,
      borderBottom: '1px solid var(--border)' }}>{title}</div>
  );

  return (
    <div style={{ maxWidth: 460 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
        Recognition thresholds applied globally across all cameras. Restart cameras after saving.
      </p>

      {section('Strong Confirm (full recognition)')}
      {field('Known face threshold', 'known_face_threshold',  { hint: 'Min score to confirm a known person — default 0.60' })}
      {field('Banned face threshold', 'banned_face_threshold', { hint: 'Min score to confirm a banned person — default 0.60' })}
      {field('Face match margin',     'face_match_margin',     { step: 0.01, max: 0.3, hint: 'Best candidate must beat second by this margin — default 0.08' })}
      {field('Required match frames', 'required_match_frames', { step: 1, min: 1, max: 10, hint: 'Frames above threshold needed to confirm — default 2' })}

      {section('Soft Suppress (leave-frame)')}
      {field('Day threshold',   'soft_suppress_thresh_day',   { hint: 'Min score to soft-suppress on daytime leave-frame — default 0.30' })}
      {field('Night threshold', 'soft_suppress_thresh_night', { hint: 'Min score to soft-suppress on night leave-frame — default 0.20' })}
      {field('Min frames',      'soft_suppress_min_frames',   { step: 1, min: 1, max: 10, hint: 'Frames above threshold needed to soft-suppress — default 2' })}

      {section('Session')}
      {field('Known grace secs', 'known_grace_secs', { step: 1, min: 0, max: 300, hint: 'Seconds to suppress unknown timer after known person confirmed on this camera — default 30' })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button className="adm-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function StorageTab() {
  const [cfg,      setCfg]     = useState(null);
  const [stats,    setStats]   = useState(null);   // /health/storage
  const [nvrStats, setNvrStats] = useState(null);  // /health .nvr
  const [saving,   setSaving]  = useState(false);
  const [msg,      setMsg]     = useState(null);

  useEffect(() => {
    apiFetch('/settings/storage').then(r => r.json()).then(setCfg).catch(() => {});
    apiFetch('/health/storage').then(r => r.json()).then(setStats).catch(() => {});
    apiFetch('/health').then(r => r.json()).then(d => setNvrStats(d.nvr || null)).catch(() => {});
  }, []);

  const field = (label, key, opts = {}) => {
    const { min, max, unit, hint } = opts;
    return (
      <div className="adm-field-row" key={key}>
        <label className="adm-label">{label}{unit ? <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>({unit})</span> : ''}</label>
        <input className="adm-input" type="number" style={{ width: 100 }}
          min={min} max={max}
          value={cfg?.[key] ?? ''}
          onChange={e => setCfg(p => ({ ...p, [key]: Number(e.target.value) }))} />
        {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{hint}</span>}
      </div>
    );
  };

  // Storage-location picker: a drive dropdown + editable path. Writes cfg[dirKey]
  // (raw config value; "" = default folder on the boot drive). Selecting a drive
  // fills a sensible subfolder on that mount; the text field accepts any custom path.
  const driveRow = (label, dirKey, effKey, subfolder, hint) => {
    const drives = cfg.drives || [];
    const cur    = cfg[dirKey] || '';
    const joinPath = (mount, sub) => {
      const win = /^[A-Za-z]:/.test(mount) || mount.includes('\\');
      const s   = win ? '\\' : '/';
      return mount.replace(/[\\/]+$/, '') + s + sub;
    };
    const opts = [{ v: '', label: '🖥️ Default — boot drive' }];
    for (const d of drives) {
      opts.push({
        v: joinPath(d.mount, subfolder),
        label: `${d.external ? '💾' : '🖥️'} ${d.mount} — ${d.free_gb ?? '?'}/${d.total_gb ?? '?'} GB free`,
      });
    }
    if (cur && !opts.some(o => o.v === cur)) opts.push({ v: cur, label: `📁 ${cur}` });
    return (
      <div className="adm-field-row" key={dirKey} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <label className="adm-label">{label}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 260 }}>
          <select className="adm-input" style={{ maxWidth: 400 }}
            value={cur}
            onChange={e => setCfg(p => ({ ...p, [dirKey]: e.target.value }))}>
            {opts.map(o => <option key={o.v || 'default'} value={o.v}>{o.label}</option>)}
          </select>
          <input className="adm-input" type="text" style={{ maxWidth: 400 }}
            placeholder="Default folder on boot drive"
            value={cur}
            onChange={e => setCfg(p => ({ ...p, [dirKey]: e.target.value }))} />
          {cfg[effKey] && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>In use: <b>{cfg[effKey]}</b></span>
          )}
          {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
        </div>
      </div>
    );
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const r = await apiFetch('/settings/storage', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) });
      const d = await r.json();
      setCfg(d);
      setMsg({ ok: true, text: 'Saved' });
    } catch {
      setMsg({ ok: false, text: 'Save failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 2500);
    }
  };

  if (!cfg) return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 640 }}>

      {recOn() && (<>
      {/* ── NVR Recording ── */}
      <div className="adm-section">
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>NVR Recording</div>
        <div className="adm-section-body">
          <div className="adm-field-row">
            <label className="adm-label">Enabled</label>
            <label className="adm-toggle">
              <input type="checkbox" checked={!!cfg.nvr_enabled}
                onChange={e => setCfg(p => ({ ...p, nvr_enabled: e.target.checked }))} />
              <span className="adm-toggle-track" />
            </label>
          </div>
          {driveRow('Storage drive', 'nvr_dir', 'nvr_dir_effective', 'nvr_recordings',
            'Pick the HDD to keep 24×7 footage off the boot SSD. Recorders restart on the new drive automatically.')}
          {field('Retention',      'nvr_retention_days',   { min: 1, max: 365, unit: 'days',    hint: 'How long to keep NVR recordings' })}
          {field('Segment length', 'nvr_segment_minutes',  { min: 1, max: 60,  unit: 'minutes', hint: 'Length of each NVR segment file' })}
          {field('Max disk use',   'nvr_max_recording_gb', { min: 1,            unit: 'GB',      hint: 'Hard cap on total NVR disk usage' })}
          {field('Clip export max','export_max_minutes',   { min: 1, max: 120, unit: 'minutes', hint: 'Max length a user can cut & download from Recordings' })}
          {nvrStats && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Used: <b>{nvrStats.recordings_gb ?? '—'} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Disk free: <b>{nvrStats.disk_free_gb ?? '—'} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Segments: <b>{nvrStats.segment_count ?? '—'}</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Disk used: <b>{nvrStats.disk_used_pct ?? '—'}%</b></span>
            </div>
          )}
        </div>
      </div>
      </>)}

      {/* ── Alert Videos ── */}
      <div className="adm-section" style={{ marginTop: 12 }}>
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Alert Videos</div>
        <div className="adm-section-body">
          {driveRow('Storage drive', 'alert_video_dir', 'alert_video_dir_effective', 'alert_videos',
            'Where alert clips are written. Changing this moves only NEW clips — existing ones stay put.')}
          {field('Retention',    'alert_video_retention_days', { min: 1, max: 365, unit: 'days', hint: 'Delete alert video clips older than this' })}
          {field('Max disk use', 'alert_video_max_disk_pct',   { min: 1, max: 95,  unit: '%',    hint: 'Auto-purge oldest clips when disk use exceeds this' })}
          {stats?.videos && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Used: <b>{stats.videos.size_gb} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Count: <b>{stats.videos.count}</b></span>
              {stats.videos.oldest && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Oldest: <b>{stats.videos.oldest}</b></span>}
            </div>
          )}
        </div>
      </div>

      {/* ── Alert Images ── */}
      <div className="adm-section" style={{ marginTop: 12 }}>
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Alert Images</div>
        <div className="adm-section-body">
          {field('Retention', 'alert_image_retention_days', { min: 1, max: 365, unit: 'days', hint: 'Delete alert snapshots older than this' })}
          {stats?.images && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Used: <b>{stats.images.size_gb} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Count: <b>{stats.images.count}</b></span>
              {stats.images.oldest && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Oldest: <b>{stats.images.oldest}</b></span>}
            </div>
          )}
        </div>
      </div>

      {/* ── Records & Detection Logs ── */}
      <div className="adm-section" style={{ marginTop: 12 }}>
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Records &amp; Detection Logs</div>
        <div className="adm-section-body">
          {field('Alert records',  'alert_record_retention_days', { min: 1, max: 365, unit: 'days', hint: 'Alert metadata log (alert_log.json)' })}
          {field('Missed alerts',  'missed_retention_days',       { min: 1, max: 365, unit: 'days', hint: 'Probable-missed entries + their images' })}
          {field('Detection logs', 'det_log_retention_days',      { min: 1, max: 365, unit: 'days', hint: 'Tier-2 detection JSONL (Detection Logs page)' })}
        </div>
      </div>

      {/* ── Disk ── */}
      <div className="adm-section" style={{ marginTop: 12 }}>
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Disk</div>
        <div className="adm-section-body">
          {field('Reserved disk floor', 'reserved_disk_gb', { min: 0, unit: 'GB', hint: 'Never let free space drop below this — triggers emergency purge' })}
          {stats?.disk && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total: <b>{stats.disk.total_gb} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Free: <b>{stats.disk.free_gb} GB</b></span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Used: <b>{stats.disk.used_pct}%</b></span>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="adm-btn primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
      </div>

    </div>
  );
}

// Mobile app logs ("Report an Error"). Lives on the App Support page (PRVSK-109);
// it used to be Admin Config's Support tab.
export function SupportTab() {
  const isMobileApp = !!window.FlutterReportError;
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  // Hidden debug panel — tap the "open this page from the mobile app" hint
  // text SECRET_TAP_COUNT times within SECRET_TAP_WINDOW_MS (from the first
  // tap of the sequence, not a per-gap reset) to reveal it. It piggybacks on
  // this existing, otherwise-inert (desktop-only) text rather than adding any
  // new visible element.
  const [debugOpen, setDebugOpen] = useState(false);
  const tapCountRef   = useRef(0);
  const firstTapAtRef = useRef(0);
  const handleSecretTap = () => {
    const now = Date.now();
    if (now - firstTapAtRef.current > SECRET_TAP_WINDOW_MS) {
      firstTapAtRef.current = now;
      tapCountRef.current = 0;
    }
    tapCountRef.current += 1;
    if (tapCountRef.current >= SECRET_TAP_COUNT) {
      tapCountRef.current = 0;
      setDebugOpen(true);
    }
  };

  const sendReport = () => {
    setSending(true);
    setMsg(null);
    window.__reportErrorDone = (status) => {
      setSending(false);
      setMsg(status === 'ok'
        ? { ok: true, text: 'Logs sent. Thank you!' }
        : { ok: false, text: 'Failed to send logs. Please try again.' });
      window.__reportErrorDone = null;
    };
    window.FlutterReportError.postMessage(JSON.stringify({ hours, notes }));
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="adm-section">
        <div className="adm-section-title" style={{ padding: '16px 24px 0', margin: 0 }}>Report an Error</div>
        <div className="adm-section-body">
          {!isMobileApp ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, userSelect: 'none' }}
                 onClick={handleSecretTap}>
              Open this page from the Parvekshak mobile app to send device logs for
              push notification or crash issues.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, userSelect: 'none' }}
                   onClick={handleSecretTap}>
                Sends recent device logs to help diagnose push notification or crash issues.
              </div>
              <div className="adm-field-row">
                <label className="adm-label">Log window</label>
                <input type="range" min={1} max={12} value={hours}
                  disabled={sending}
                  onChange={e => setHours(Number(e.target.value))}
                  style={{ width: 200 }} />
                <span style={{ fontSize: 13, marginLeft: 8 }}>{hours} hour{hours === 1 ? '' : 's'}</span>
              </div>
              <div className="adm-field-row" style={{ alignItems: 'flex-start' }}>
                <label className="adm-label">Notes</label>
                <textarea className="adm-input" rows={3} style={{ width: '100%', maxWidth: 400 }}
                  placeholder="What happened? (optional)"
                  disabled={sending}
                  value={notes}
                  onChange={e => setNotes(e.target.value)} />
              </div>
              <div style={{ padding: '16px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="adm-btn primary" onClick={sendReport} disabled={sending}>
                  {sending ? 'Sending…' : 'Send logs'}
                </button>
                {msg && <span style={{ fontSize: 13, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
              </div>
            </>
          )}
        </div>
      </div>
      {debugOpen && <DebugPanel onClose={() => setDebugOpen(false)} />}
    </div>
  );
}

const TABS = [
  { key: 'Users',             icon: '👥', short: 'Users'    },
  { key: 'Health',            icon: '🏥', short: 'Health'   },
  { key: 'Storage',           icon: '💾', short: 'Storage'  },
  { key: 'Manage Devices',    icon: '⚙️', short: 'Devices'  },
  { key: 'Manage Camera',     icon: '📷', short: 'Camera'   },
  { key: 'Settings',          icon: '🔧', short: 'Settings' },
  { key: 'Known Seen Config', icon: '👤', short: 'Known'    },
  // Hidden from the UI (not removed): CC Alerts is Telegram-only (user / group IDs).
  // { key: 'CC Alerts',         icon: '🔔', short: 'CC Alerts'},
  // Shown only where the box has no App Support page (older build, or
  // APP_SUPPORT_ENABLED not set) — there it lives at /support (PRVSK-109).
  { key: 'Support',           icon: '🐞', short: 'Support', oldSupport: true },
  // A real tab (PRVSK-126). It used to be a tab-styled button that navigated to the
  // separate /detection-settings page, which has no tab bar — so there was no way
  // back. That route still works on its own for old links.
  { key: 'Detection',         icon: '🔬', short: 'Detection' },
];

// Schedule-label helpers for the Debug panel's Config Overview — each takes
// whatever shape that particular settings endpoint returns and reduces it to
// a single at-a-glance { text, cls } badge (cls drives the badge color).
function globalAlertBadge(d) {
  if (!d) return { text: '—', cls: 'unknown' };
  if (d.mode === 'OFF')        return { text: '🔴 Off', cls: 'off' };
  if (d.mode === 'TIME_BASED') return { text: `⏰ ${d.start}–${d.end}`, cls: 'timed' };
  return { text: '🟢 Always On', cls: 'on' };
}

function pushBadge(d) {
  if (!d) return { text: '—', cls: 'unknown' };
  if (d.mode === 'off')       return { text: '🔴 Off', cls: 'off' };
  if (d.mode === 'scheduled') return { text: `⏰ ${d.start}–${d.end}`, cls: 'timed' };
  return { text: '🟢 24×7', cls: 'on' };
}

function camOfflineAlertBadge(d) {
  if (!d) return { text: '—', cls: 'unknown' };
  if (!d.enabled) return { text: '🔴 Disabled', cls: 'off' };
  return { text: `🟢 ${d.minutes ?? 5} min`, cls: 'on' };
}

function onDeviceCacheBadge(d) {
  if (!d) return { text: '—', cls: 'unknown' };
  if (!d.enabled) return { text: '🔴 Disabled', cls: 'off' };
  return { text: `🟢 On (newest ${d.cap})`, cls: 'on' };
}

// "Always" is skipped only when a fixed schedule is also shown for this
// device — that badge already tells the on/off story, so a second "Always"
// next to it is redundant. Without a fixed schedule, "Always" is the only
// thing describing this device's state, so it still needs to show.
function deviceAlertBadge(ch, hasFixed) {
  if (!(ch.on_alert ?? false)) return { text: '🔴 Alert: Off', cls: 'off' };
  const sched = ch.alert_schedule;
  if (sched && typeof sched === 'object') return { text: `⏰ Alert: ${sched.start}–${sched.end}`, cls: 'timed' };
  if (hasFixed) return null;
  return { text: '🟢 Alert: Always', cls: 'on' };
}

function deviceFixedBadge(ch) {
  const fs = ch.fixed_schedule;
  if (!(fs && typeof fs === 'object')) return null;
  return { text: `🔌 Fixed: ${fs.start}–${fs.end}`, cls: 'timed' };
}

// Lights only: what the light does on an alert/panic. A WiZ plug always holds
// ON (its mechanical relay can't blink), whatever alert_mode says.
function deviceLightModeBadge(ch) {
  if (ch.type !== 'light' || !(ch.on_alert ?? false)) return null;
  const wizPlug = ch.backend === 'wiz' && ch.wiz_kind === 'plug';
  if (ch.alert_mode === 'blink' && !wizPlug) {
    return { text: `✨ Blink · ${ch.blink_interval_ms ?? 500} ms`, cls: 'timed' };
  }
  return { text: '💡 Stay ON', cls: 'unknown' };
}

// WiZ Wi-Fi device as ONE badge — IP + live state, coloured by reachability
// (from /ui/devices/status → wiz_status, absent until the first health check).
// One badge, not two, so the row's cluster still fits beside the device name.
function wizDeviceBadge(ch, st) {
  if (ch.backend !== 'wiz') return null;
  const kind  = ch.wiz_kind === 'bulb' ? 'bulb' : 'plug';
  const ip    = ch.wiz_ip || 'IP unknown';
  const title = `WiZ ${kind} · MAC ${ch.wiz_mac || '?'} · ${ip}`;
  if (!st)           return { text: `📶 ${ip}`, cls: 'unknown', title: `${title} · not checked yet` };
  if (!st.reachable) return { text: '📶 Not reachable', cls: 'off', title };
  if (st.locked)     return { text: '🔒 Locked', cls: 'off',
                              title: `${title} · WiZ app → Security → Allow all controls` };
  return { text: `📶 ${ip}${st.on != null ? ` · ${st.on ? 'ON' : 'OFF'}` : ''}`, cls: 'on', title };
}

// "AI on this camera" (Edit Camera → detect_mode). Always shown: an AI-off
// camera records but raises no alerts, which is exactly what a debug view
// needs to make obvious.
function cameraAiBadge(cam) {
  const mode = cam.detect_mode || 'full';
  if (mode === 'off')     return { text: '⚫ AI: Off', cls: 'off' };
  if (mode === 'checkin') return { text: '🚶 AI: Check-ins only', cls: 'timed' };
  return { text: '🤖 AI: Full', cls: 'on' };
}

function cameraRecBadge(cam) {
  if (cam.nvr === false) return { text: '🔴 Rec: Off', cls: 'off' };
  if (cam.record_schedule_enabled && cam.record_schedule?.start && cam.record_schedule?.end) {
    return { text: `⏰ Rec: ${cam.record_schedule.start}–${cam.record_schedule.end}`, cls: 'timed' };
  }
  return { text: '🟢 Rec: Always', cls: 'on' };
}

// Only returns a badge when the camera actually OVERRIDES the global alert
// schedule — INHERIT (Follow Global) already reads off the Global Alert
// Schedule row above, so a second identical badge here would be noise.
function cameraAlertOverrideBadge(cam) {
  const mode = cam.alert_time_mode || 'INHERIT';
  if (mode === 'ALWAYS_ON') return { text: '🟢 Alert: Always', cls: 'on' };
  if (mode === 'OFF')       return { text: '🔴 Alert: Off', cls: 'off' };
  if (mode === 'CUSTOM' && cam.alert_time?.start && cam.alert_time?.end) {
    return { text: `⏰ Alert: ${cam.alert_time.start}–${cam.alert_time.end}`, cls: 'timed' };
  }
  return null;
}

// Per-camera push toggle (Dashboard > Notifications sheet) — independent of
// the global push schedule above, defaults to on.
function cameraPushBadge(cam) {
  const on = cam.alert_actions?.push !== false;
  return on ? { text: '🔔 Push: On', cls: 'on' } : { text: '🔕 Push: Off', cls: 'off' };
}

// Doorbell (PRVSK-139) — Off shows nothing; otherwise the mode, and whether a gate
// area limits where the doorbell rings (no area = the whole view rings it).
function cameraDoorbellBadge(cam) {
  const mode = cam.alert_actions?.doorbell;
  if (!mode) return null;
  const label = mode === 'instant' ? 'Instant' : 'With names';
  const areas = (cam.cam_config?.doorbell_zones || [])
    .filter(z => (Array.isArray(z?.polygon) && z.polygon.length >= 3) || z?.x1 != null).length;
  return { text: `🔔 Doorbell: ${label} · ${areas ? 'gate area' : 'whole view'}`, cls: 'on' };
}

function CfgOvBadge({ badge, title }) {
  if (!badge) return null;
  return <span className={`cfg-ov-badge cfg-ov-${badge.cls}`} title={title}>{badge.text}</span>;
}

// Read-only, at-a-glance rollup of every schedule/on-off setting that's
// otherwise scattered across Devices, Camera and Settings tabs — built for
// installers/support to check "what's actually configured" in one place
// without hunting through each page.
function ConfigOverview() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  // Camera whose ONVIF stream settings are open. Mounted only on click, so the
  // popup itself never logs in to the cameras.
  const [streamCam, setStreamCam] = useState(null);

  useEffect(() => {
    (async () => {
      const [g, p, ps, dv, cm, co, ip] = await Promise.allSettled([
        apiFetch('/settings/alert-schedule').then(r => r.json()),
        apiFetch('/settings/push-schedule').then(r => r.json()),
        apiFetch('/controls/devices/pause-status').then(r => r.json()),
        apiFetch('/ui/devices/status').then(r => r.json()),
        apiFetch('/ui/cameras').then(r => r.json()),
        apiFetch('/settings/camera-offline-alert').then(r => r.json()),
        apiFetch('/admin/debug/server-ip').then(r => r.json()),
      ]);
      setData({
        global:     g.status  === 'fulfilled' ? g.value : null,
        push:       p.status  === 'fulfilled' ? p.value : null,
        pause:      ps.status === 'fulfilled' ? ps.value : null,
        devices:    dv.status === 'fulfilled' ? (dv.value.relay_channels || []) : [],
        wizStatus:  dv.status === 'fulfilled' ? (dv.value.wiz_status || {})     : {},
        cameras:    cm.status === 'fulfilled' ? (cm.value.cameras || [])        : [],
        camOffline: co.status === 'fulfilled' ? co.value : null,
        serverIp:   ip.status === 'fulfilled' ? (ip.value?.ip || null) : null,
        // Per-device, not server-side — read straight from this browser's
        // localStorage rather than an endpoint.
        onDevice:   getOfflineSyncConfig(),
      });
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="adm-state" style={{ minHeight: 60 }}><div className="adm-spinner" /></div>;
  if (!data)   return <div className="adm-msg error">Failed to load config overview</div>;

  return (
    <div className="cfg-ov">
      <div className="cfg-ov-row">
        <span className="cfg-ov-label">🖥 Server IP</span>
        <CfgOvBadge badge={{ text: data.serverIp || '—', cls: 'unknown' }} title="This box's LAN IP" />
      </div>
      <div className="cfg-ov-row">
        <span className="cfg-ov-label">🌐 Global Alert Schedule</span>
        <CfgOvBadge badge={globalAlertBadge(data.global)} />
      </div>
      <div className="cfg-ov-row">
        <span className="cfg-ov-label">🔔 Push Notification Schedule</span>
        <CfgOvBadge badge={pushBadge(data.push)} />
      </div>
      <div className="cfg-ov-row">
        <span className="cfg-ov-label">📴 Camera Offline Alert</span>
        <CfgOvBadge badge={camOfflineAlertBadge(data.camOffline)} />
      </div>
      <div className="cfg-ov-row">
        <span className="cfg-ov-label">📱 On-Device Alert Cache (this browser)</span>
        <CfgOvBadge badge={onDeviceCacheBadge(data.onDevice)} />
      </div>
      {data.pause?.paused && (
        <div className="cfg-ov-row">
          <span className="cfg-ov-label">⏸ Devices Paused</span>
          <CfgOvBadge badge={{ text: `${Math.ceil((data.pause.remaining_secs || 0) / 60)}m left`, cls: 'off' }} />
        </div>
      )}

      <div className="cfg-ov-section-title">🔌 Devices ({data.devices.length})</div>
      {data.devices.length === 0 && <div className="cfg-ov-empty">No devices configured</div>}
      {data.devices.map(ch => {
        const fixed = deviceFixedBadge(ch);
        const wiz   = wizDeviceBadge(ch, data.wizStatus[ch.channel]);
        return (
        <div key={ch.channel} className="cfg-ov-row">
          <span className="cfg-ov-label">{ch.name || `CH ${ch.board_channel ?? ch.channel}`}</span>
          <span className="cfg-ov-badges">
            <CfgOvBadge badge={wiz} title={wiz?.title} />
            <CfgOvBadge badge={deviceAlertBadge(ch, !!fixed)} title="Alert schedule" />
            <CfgOvBadge badge={deviceLightModeBadge(ch)} title="On alert / panic" />
            <CfgOvBadge badge={fixed} title="Fixed on/off schedule" />
          </span>
        </div>
        );
      })}

      <div className="cfg-ov-section-title">📷 Cameras ({data.cameras.length})</div>
      {data.cameras.length === 0 && <div className="cfg-ov-empty">No cameras configured</div>}
      {data.cameras.map(cam => {
        const aa = cam.alert_actions || {};
        const open = streamCam === cam.idx;
        return (
        <Fragment key={cam.idx ?? cam.name}>
        <div className="cfg-ov-row">
          <span className="cfg-ov-label">{cam.name}</span>
          <span className="cfg-ov-badges">
            <CfgOvBadge badge={cameraAiBadge(cam)} title="AI on this camera" />
            {recOn() && <CfgOvBadge badge={cameraRecBadge(cam)} title="Recording schedule" />}
            <CfgOvBadge badge={cameraAlertOverrideBadge(cam)} title="Alert schedule override" />
            <CfgOvBadge badge={cameraPushBadge(cam)} title="Per-camera push notifications" />
            <CfgOvBadge badge={cameraDoorbellBadge(cam)} title="Doorbell mode, and where it rings (gate area or the whole view)" />
            {aa.siren    && <span className="cam-action-chip">Siren</span>}
            {aa.light    && <span className="cam-action-chip">Light</span>}
            {aa.video    && <span className="cam-action-chip">Video</span>}
            {/* Edit Camera → Record audio */}
            {cam.record_audio && <span className="cam-action-chip" title="Recording includes audio">🎙 Audio</span>}
            {cam.idx != null && (
              <button type="button" className={`cfg-ov-icon${open ? ' active' : ''}`}
                      title="Stream settings (ONVIF)" aria-label={`Stream settings for ${cam.name}`}
                      aria-expanded={open}
                      onClick={() => setStreamCam(open ? null : cam.idx)}>🎞</button>
            )}
          </span>
        </div>
        {open && <div className="cfg-ov-stream"><CameraStreamSettings idx={cam.idx} readOnly /></div>}
        </Fragment>
        );
      })}
    </div>
  );
}

// Hidden debug panel — opened by tapping the version label 10 times (see
// SECRET_TAP_TARGET below), for installer/support use only. Not linked from
// anywhere else on purpose.
function DebugPanel({ onClose }) {
  const [maximized,   setMaximized]   = useState(false);
  const [refreshKey,  setRefreshKey]  = useState(0);

  return (
    <div className={`adm-overlay${maximized ? ' adm-overlay-max' : ''}`} onClick={onClose}>
      <div className={`adm-modal adm-modal-lg${maximized ? ' adm-modal-max' : ''}`}
           onClick={e => e.stopPropagation()}>
        <div className="adm-modal-header">
          <h3>🐞 Debug</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button className="adm-modal-close"
                    title="Reload config overview"
                    aria-label="Reload config overview"
                    onClick={() => setRefreshKey(k => k + 1)}>🔄</button>
            <button className="adm-modal-close adm-modal-max-btn"
                    title={maximized ? 'Restore' : 'Maximize'}
                    aria-label={maximized ? 'Restore dialog' : 'Maximize dialog'}
                    onClick={() => setMaximized(m => !m)}>{maximized ? '⤡' : '⤢'}</button>
            <button className="adm-modal-close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={{ padding: '4px 20px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div className="cfg-ov-section-title" style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>📋 Config Overview</div>
          <ConfigOverview key={refreshKey} />
        </div>
      </div>
    </div>
  );
}

// How many taps must land within the window (measured from the first tap of
// the sequence) to count as the unlock gesture — mirrors the Android "tap
// build number" pattern.
const SECRET_TAP_COUNT = 5;
const SECRET_TAP_WINDOW_MS = 4000;

export default function AdminConfigPage() {
  const [tab,    setTab]    = useState('Users');
  const [tabKey, setTabKey] = useState(0);
  const isMobile = useIsMobile();
  const { appSupport } = useEntitlements();
  const tabs = TABS.filter(t => !(t.oldSupport && appSupport));

  const switchTab = (key) => { setTab(key); setTabKey(k => k + 1); };

  return (
    <div className="adm-page">

      <div className="page-toolbar">
        {isMobile ? (
          <div className="adm-icon-grid">
            {tabs.map(t => (
              <button key={t.key}
                className={`adm-icon-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => switchTab(t.key)}>
                <span className="adm-icon-btn-icon">{t.icon}</span>
                <span className="adm-icon-btn-label">{t.short}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="adm-tabs">
            {tabs.map(t => (
              <button key={t.key}
                className={`adm-tab${tab === t.key ? ' active' : ''}`}
                onClick={() => switchTab(t.key)}>
                {t.short}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="adm-content">
        {tab === 'Users'             && <UsersTab             key={tabKey} />}
        {tab === 'Health'            && <SystemHealthSection  key={tabKey} />}
        {tab === 'Storage'           && <StorageTab           key={tabKey} />}
        {tab === 'Manage Devices'    && <DevicesSection       key={tabKey} />}
        {tab === 'Manage Camera'     && <ManageCameraTab      key={tabKey} />}
        {tab === 'Settings'          && <SettingsTab          key={tabKey} />}
        {tab === 'Known Seen Config' && <KnownSeenConfigTab key={tabKey} />}
        {/* Telegram hidden from the UI: {tab === 'CC Alerts'         && <CcAlertsTab        key={tabKey} />} */}
        {tab === 'Support' && !appSupport && <SupportTab key={tabKey} />}
        {tab === 'Detection'         && <DetectionSettingsPage key={tabKey} embedded />}
      </div>

    </div>
  );
}

